<template>
  <div id="app" :class="[{ collapsed: collapsed, login: admin, fe: !admin, 'widgets-expanded': widgetsExpanded || lockedImagePanelActive }]">
    <template>
      <div :class="[{ stickyHeader: admin || currentUser }]">
        <!-- MOBILE: always-visible collapsed cart bar, pinned at the TOP of the
             sticky header (above the logo header + nav) per user request. Lives
             INSIDE the fixed sticky header so it stays on screen while
             scrolling; applyStickyClasses() measures the header (incl. this bar)
             to set body padding-top so content clears it. Tapping toggles the
             inline cart (in the body, below). Counts come from counts-change. -->
        <!-- Only show the cart bar when the cart actually HAS items (orders/quotes > 0) — no empty "Your Cart" banner.
             The CartWidget below stays mounted regardless (it feeds these counts), so the bar reappears when a part is added. -->
        <button v-if="isMobile && showPortalWidgets && !isCartRoute && (mobileCartCounts.orders || mobileCartCounts.quotes)" type="button"
                class="jcs-mobile-cart-bar" @click="mobileCartOpen = !mobileCartOpen">
          <span class="jcs-mcb-left">
            <!-- Inline SVG cart (not the 🛒 emoji): emoji render in their own
                 colors and ignore `color`, so they can't be made white/bold.
                 Thick white strokes give the bold white look. -->
            <svg class="jcs-mcb-cart-icon" viewBox="0 0 24 24" fill="none"
                 stroke="#fff" stroke-width="2.5" stroke-linecap="round"
                 stroke-linejoin="round" aria-hidden="true">
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              <circle cx="9" cy="21" r="1.7" fill="#fff" stroke="none" />
              <circle cx="20" cy="21" r="1.7" fill="#fff" stroke="none" />
            </svg>
            Your Cart
            <span v-if="mobileCartCounts.orders" class="jcs-mcb-badge jcs-mcb-orders"
                  :title="mobileCartCounts.orders + ' open order(s)'">{{ mobileCartCounts.orders }}</span>
            <span v-if="mobileCartCounts.quotes" class="jcs-mcb-badge jcs-mcb-quotes"
                  :title="mobileCartCounts.quotes + ' open quote(s)'">{{ mobileCartCounts.quotes }}</span>
          </span>
          <span class="jcs-mobile-cart-chev">{{ mobileCartOpen ? 'Hide ▾' : 'View ▸' }}</span>
        </button>
        <Head></Head>
        <MainNav></MainNav>
      </div>
    </template>

    <template v-if="admin">
      <link
        rel="stylesheet"
        href="https://use.fontawesome.com/releases/v5.2.0/css/all.css"
        integrity="sha384-hWVjflwFxL6sNzntih27bfxkr27PmbbK/iSvJ+a4+0owXq79v+lsFkW54bOGbiDQ"
        crossorigin="anonymous"
      />
      <TopMenu></TopMenu>
      <sidebar-menu
        :menu="menu"
        :collapsed="collapsed"
        :theme="selectedTheme"
        :show-one-child="true"
        @collapse="onCollapse"
      />
    </template>

    <!-- MOBILE: the floating Cart + Top Parts fold into the page flow (per the
         composed mobile layout) — Cart at the top of the content, Top Parts
         above the footer. Mounted WITHOUT `floating` so they use the widgets'
         inline panel mode; the desktop floating stack below is gated to
         !isMobile, so only one instance of each ever mounts. -->
    <!-- MOBILE: the expandable cart body (the toggle bar lives in the sticky
         header above). Always mounted so it keeps the cart synced + emits
         counts; v-show toggles it open/closed beneath the header. -->
    <CartWidget v-if="isMobile && showPortalWidgets && !isCartRoute"
                v-show="mobileCartOpen" standalone :visible="!isCartRoute"
                class="jcs-mobile-widget jcs-mobile-cart"
                @counts-change="mobileCartCounts = $event" />

    <div id="routeView">
  <Breadcrumbs v-if="showGlobalBreadcrumbs" />
  <router-view :key="$route.fullPath"></router-view>
</div>

    <TopPartsWidget v-if="isMobile && showPortalWidgets && !isReportRoute" standalone
                class="jcs-mobile-widget jcs-mobile-top" />

    <!-- ===== GLOBAL DEBUG OVERLAY (admin-toggleable) =====
         Toggled by the "Debug overlay" checkbox in Account Setup -> Event
         Logs & History (persists localStorage.jcs_debugOverlay + emits the
         $cartBus "debug-overlay-toggle" event). Shows live diagnostics for the
         CURRENT page on every route, no per-page wiring. pointer-events:none
         so it never blocks the page. Contents come from buildDebugText(). -->
    <div v-if="debugOverlay && isMasterUser && !isPreviewFrame && isDevEnv" class="jcs-debug-overlay"
         :style="{ top: debugPos.top + 'px', left: debugPos.left + 'px' }">
      <div class="jcs-dbg-bar" @mousedown="startDebugDrag" title="Drag to move">
        <span class="jcs-dbg-title">🐞 {{ ($route && $route.path) || "" }}</span>
        <button type="button" class="jcs-dbg-btn" @click="copyDebugPrompt"
                title="Copy a ready-to-paste fix request for this page">{{ debugCopyLabel }}</button>
        <button type="button" class="jcs-dbg-btn" @click.stop="copyNavRules"
                @mousedown.stop
                title="Copy the CSS size/position RULES sizing the nav bar + this page's card (height-bug inspector) — works at any width, no console needed">🔍</button>
        <button type="button" class="jcs-dbg-btn" @click.stop="runAudioTest"
                @mousedown.stop
                title="Tour audio self-test — checks the neural voice endpoint → decode → playback and reports each stage (you should hear a test voice)">🔊</button>
        <button type="button" class="jcs-dbg-btn" @click.stop="toggleMobilePreview"
                @mousedown.stop
                :title="mobilePreviewOn ? 'Hide the mobile preview frames' : 'Mobile preview — 3 phone frames (Chrome / Safari / Firefox) of this page'">📱</button>
        <button type="button" class="jcs-dbg-btn" @click.stop="toggleComposer"
                @mousedown.stop
                :title="composerOn ? 'Hide the layout composer' : 'Layout composer — drag page parts into Left/Center/Right zones to build a layout + AI prompt'">📐</button>
        <button type="button" class="jcs-dbg-btn" @click.stop="toggleDebugCollapsed"
                @mousedown.stop
                :title="debugCollapsed ? 'Expand the debug panel' : 'Collapse to just the header'">{{ debugCollapsed ? "▢" : "_" }}</button>
        <button type="button" class="jcs-dbg-btn" @click="toggleDebugFromOverlay"
                title="Turn the debug overlay off">×</button>
      </div>
      <div v-show="!debugCollapsed" class="jcs-dbg-collapsible">
      <div class="jcs-dbg-theme">{{ debugTheme }}</div>
      <pre v-if="audioTestResult" class="jcs-dbg-body jcs-dbg-audio">{{ audioTestResult }}</pre>
      <pre class="jcs-dbg-body">{{ debugText }}</pre>
      <div class="jcs-dbg-topic">
        <div class="jcs-dbg-compare-label">Focus a known uniformity issue (adds fix guidance to the prompt):</div>
        <select class="jcs-dbg-select" v-model="debugTopic" @change="onDebugTopicChange">
          <option v-for="t in debugTopics" :key="t.key" :value="t.key">{{ t.label }}</option>
        </select>
        <div class="jcs-dbg-compare-label jcs-dbg-mt">Target CSS file (tells the prompt where to fix):</div>
        <select class="jcs-dbg-select" v-model="debugCssFile" @change="onDebugCssFileChange">
          <option v-for="f in debugCssFiles" :key="f.key" :value="f.key">{{ f.label }}</option>
        </select>
        <button v-if="selectedTopicHasPreview" type="button" class="jcs-dbg-previewbtn"
                @click="toggleDebugPreview">{{ debugPreviewOn ? "👁 Revert preview" : "👁 Preview fix live" }}</button>
        <!-- Live CSS color editor (focus = "Adjust CSS Styling"): pick an element,
             tweak text/background color live, copy an AI prompt. -->
        <div v-if="isAdjustCssFocus" class="jcs-dbg-cssedit">
          <div v-if="!debugCssFile" class="jcs-dbg-compare-label jcs-dbg-mt">
            ↑ Pick a target CSS file (e.g. darkmode.css) above to start.
          </div>
          <template v-else>
            <button type="button" class="jcs-dbg-previewbtn"
                    @click.stop="toggleCssPick" @mousedown.stop>
              {{ cssPickMode ? "🎯 Picking — click opens dropdowns; ALT+CLICK locks onto the exact element (e.g. a dropdown value). (Click to cancel)" : "🎯 Pick element on page" }}
            </button>
            <!-- Live hover cluster (pick mode): whatever's under the cursor on the
                 page + its neighbors — click a row to pick without leaving the panel. -->
            <div v-if="cssPickMode && cssHoverList.length" class="jcs-dbg-hoverbox">
              <div class="jcs-dbg-compare-label">👁 Under cursor — ALT+click on the page locks the exact element, or click a row here:</div>
              <div v-for="(h, hi) in cssHoverList" :key="'h' + hi"
                   class="jcs-dbg-cssitem jcs-dbg-hoveritem" :class="{ 'is-hover': hi === 0 }"
                   @click.stop="selectCssHover(hi)" @mousedown.stop>{{ h.label }}</div>
            </div>
            <input type="text" class="jcs-dbg-search jcs-dbg-mt" v-model="cssEditSearch"
                   placeholder="filter elements… (text, class, or tag — e.g. your cart)" />
            <div class="jcs-dbg-csslist">
              <div v-for="e in filteredCssEditList" :key="e.i"
                   class="jcs-dbg-cssitem" :class="{ 'is-sel': e.i === cssEditPick }"
                   @click.stop="selectCssEl(e.i)" @mousedown.stop>{{ e.label }}</div>
              <div v-if="!filteredCssEditList.length" class="jcs-dbg-cssmore">
                no matches — if you just opened a dropdown/menu, click “↻ rescan page” first (or hover it and click on the page).
              </div>
              <div v-else-if="cssEditMoreCount > 0" class="jcs-dbg-cssmore">
                +{{ cssEditMoreCount }} more — type above to narrow
              </div>
            </div>
            <button type="button" class="jcs-dbg-btn jcs-dbg-rescan"
                    @click.stop="rescanCssElements" @mousedown.stop>↻ rescan page ({{ cssEditList.length }})</button>
            <div v-if="cssEditSel" class="jcs-dbg-cssedit-body">
              <div class="jcs-dbg-compare-label jcs-dbg-mt">Editing: {{ cssEditDesc }}</div>
              <!-- State selector: the Text/Bg fields + swatches below edit the
                   chosen state. Each accumulates (set No Hover, then On Hover, then
                   On Click); the prompt emits a rule per state you touched. -->
              <div class="jcs-dbg-palette-label">
                State:
                <button type="button" class="jcs-dbg-tgt" :class="{ on: cssEditState === 'base' }"
                        @click.stop="setCssState('base')" @mousedown.stop>No Hover</button>
                <button type="button" class="jcs-dbg-tgt" :class="{ on: cssEditState === 'hover' }"
                        @click.stop="setCssState('hover')" @mousedown.stop>On Hover</button>
                <button type="button" class="jcs-dbg-tgt" :class="{ on: cssEditState === 'active' }"
                        @click.stop="setCssState('active')" @mousedown.stop>On Click</button>
              </div>
              <!-- Auto heads-up: the picked element's background is hidden behind
                   an opaque child (e.g. dropdown .item over .menu in dark mode).
                   One click switches to editing that child — so this trap is
                   self-explanatory instead of a mystery. -->
              <div v-if="cssEditCoveredBy" class="jcs-dbg-coverwarn">
                ⚠ This background is hidden behind its content — to recolor what you SEE,
                <button type="button" class="jcs-dbg-tgt"
                        @click.stop="selectCoveredChild" @mousedown.stop>edit {{ cssEditCoveredBy.sel }} →</button>
              </div>
              <div class="jcs-dbg-cssrow">
                <label>Text</label>
                <input type="color" v-model="cssEditColor" @input="applyCssEdit" @focus="cssEditTarget = 'text'" />
                <input type="text" class="jcs-dbg-hex" v-model="cssEditColor"
                       @input="applyCssEdit" @change="applyCssEdit" @focus="cssEditTarget = 'text'" placeholder="#rrggbb" />
              </div>
              <div class="jcs-dbg-cssrow">
                <label>Bg</label>
                <input type="color" v-model="cssEditBg" @input="applyCssEdit" @focus="cssEditTarget = 'bg'" />
                <input type="text" class="jcs-dbg-hex" v-model="cssEditBg"
                       @input="applyCssEdit" @change="applyCssEdit" @focus="cssEditTarget = 'bg'" placeholder="#rrggbb" />
              </div>
              <!-- Quick-pick palette of frequently used theme colors. A click
                   loads that color into the ACTIVE field (Text or Bg) and applies
                   it live. The first two are the selected element's ORIGINALS
                   (one-click reset of a single field). -->
              <div class="jcs-dbg-palette-label">
                Quick colors → applying to
                <button type="button" class="jcs-dbg-tgt" :class="{ on: cssEditTarget !== 'bg' }"
                        @click.stop="cssEditTarget = 'text'" @mousedown.stop>Text</button>
                <button type="button" class="jcs-dbg-tgt" :class="{ on: cssEditTarget === 'bg' }"
                        @click.stop="cssEditTarget = 'bg'" @mousedown.stop>Bg</button>
              </div>
              <div class="jcs-dbg-swatches">
                <button v-for="p in cssEditPalette" :key="p.c + p.n" type="button"
                        class="jcs-dbg-swatch" :style="{ background: p.c }"
                        :title="p.n + ' · ' + p.c"
                        @click.stop="applyCssSwatch(p.c)" @mousedown.stop></button>
              </div>
              <button type="button" class="jcs-dbg-previewbtn"
                      @click.stop="revertCssEdit" @mousedown.stop>↩ Revert live changes</button>
              <!-- Surrounding elements: ancestors / siblings / children of the
                   selection, so you can hop to nearby styling without searching. -->
              <div v-if="cssEditNeighbors.length" class="jcs-dbg-neighbors">
                <div class="jcs-dbg-compare-label jcs-dbg-mt">Nearby elements (click to adjust those):</div>
                <div v-for="(n, ni) in cssEditNeighbors" :key="'n' + ni"
                     class="jcs-dbg-cssitem jcs-dbg-neighbor"
                     @click.stop="selectCssNeighbor(ni)" @mousedown.stop>{{ n.label }}</div>
              </div>
              <div class="jcs-dbg-compare-label jcs-dbg-mt">📋 Paste to chat to apply:</div>
              <pre class="jcs-dbg-prompt">{{ cssEditPrompt }}</pre>
            </div>
          </template>
        </div>
      </div>
      <div class="jcs-dbg-compare">
        <div class="jcs-dbg-compare-label">Compare against (✓ = captured · click to set baseline):</div>
        <input type="text" class="jcs-dbg-search" v-model="debugRouteSearch"
               placeholder="filter pages…" />
        <div class="jcs-dbg-routes">
          <div v-for="r in filteredDebugRoutes" :key="r.path" class="jcs-dbg-route"
               :class="{ 'is-baseline': r.path === debugBaseline, 'is-uncaptured': !r.captured }"
               @click="setDebugBaseline(r.path)">
            <span class="jcs-dbg-route-box">{{ r.path === debugBaseline ? "×" : "" }}</span>
            <span class="jcs-dbg-route-mark">{{ r.captured ? "✓" : "·" }}</span>
            <span class="jcs-dbg-route-path">{{ r.path }}</span>
          </div>
        </div>
      </div>
      <div class="jcs-dbg-prompt-wrap">
        <div class="jcs-dbg-compare-label">📋 Paste to chat to fix this page:</div>
        <pre class="jcs-dbg-prompt">{{ debugPrompt }}</pre>
      </div>
      </div>
    </div>

    <!-- ===== MOBILE PREVIEW (debug console) =====================
         3 phone-sized LIVE previews of the current page, side by side, to the
         right of the debug console — labelled Chrome / Safari / Firefox, all the
         SAME width × height. Each iframe loads this same app (same origin) at a
         phone viewport with name "jcs-preview:<browser>"; the app tags <html>
         with that browser's quirk-profile class. They render on THIS browser's
         engine — the profiles only APPROXIMATE each browser's mobile CSS quirks
         (real engine differences need real devices). Suppressed inside the frames
         (isPreviewFrame) so they never recurse. -->
    <div v-if="mobilePreviewOn && isMasterUser && !isPreviewFrame" class="jcs-mobile-preview"
         :style="{ top: debugPos.top + 'px', left: (debugPos.left + 478) + 'px' }">
      <div class="jcs-mp-bar">
        <span class="jcs-mp-title">📱 Mobile preview</span>
        <select class="jcs-dbg-select jcs-mp-device" v-model.number="mobilePreviewDeviceIdx">
          <option v-for="(d, i) in mobilePreviewDevices" :key="i" :value="i">{{ d.label }} · {{ d.w }}×{{ d.h }}</option>
        </select>
        <button type="button" class="jcs-dbg-btn" @click.stop="reloadMobilePreview"
                title="Reload all 3 frames at the current page">↻</button>
        <button type="button" class="jcs-dbg-btn" @click.stop="copyHeaderDiagFromPreview"
                title="Copy the header layout diagnostic from the first preview frame (true phone width)">🔍 header</button>
        <button type="button" class="jcs-dbg-btn" @click.stop="toggleMobilePreview" title="Close">×</button>
      </div>
      <div class="jcs-mp-note">
        All 3 render on THIS browser's engine; the labels apply each browser's mobile CSS
        quirk profile (fonts / form controls / safe-area / scrollbars). For true engine
        differences use real devices or BrowserStack.
      </div>
      <div class="jcs-mp-frames">
        <div v-for="b in mobilePreviewBrowsers" :key="b.key" class="jcs-mp-phone"
             :style="{ width: mobilePreviewDevices[mobilePreviewDeviceIdx].w + 'px' }">
          <div class="jcs-mp-label">{{ b.label }}</div>
          <iframe class="jcs-mp-iframe"
                  :key="'pv-' + b.key + '-' + mobilePreviewNonce"
                  :name="'jcs-preview:' + b.key"
                  :src="mobilePreviewSrc"
                  :style="{ width: mobilePreviewDevices[mobilePreviewDeviceIdx].w + 'px', height: previewFrameHeight + 'px' }"></iframe>
        </div>
      </div>
    </div>

    <!-- ===== LAYOUT COMPOSER (debug console) =====================
         The BUILDER: drag page PARTS from the palette into the single stacked
         canvas (top → bottom), click a part to edit its font/size/colors, ↑↓
         reorders — it builds a paste-to-chat AI prompt (incl. per-element style
         intent), savable as a version (DB-backed via api/account/composer-layouts,
         localStorage fallback). The phone to the RIGHT renders the composed parts.
         Suppressed inside preview frames. -->
    <div v-if="composerOn && isMasterUser && !isPreviewFrame" class="jcs-composer"
         :style="{ top: debugPos.top + 'px', left: (debugPos.left + 478) + 'px' }">
      <div class="jcs-mp-bar">
        <span class="jcs-mp-title">📐 Layout composer · {{ ($route && $route.path) || "" }}</span>
        <button type="button" class="jcs-dbg-btn" @click.stop="clearComposer" title="Clear all zones">⌫</button>
        <button type="button" class="jcs-dbg-btn" @click.stop="toggleComposer" title="Close">×</button>
      </div>
      <div class="jcs-mp-note"><b style="color:#ffd24a">rev 50 · 6/16</b> — Drag parts into the canvas (they stack top → bottom); click a part to edit font / size / colors / align; ↑↓ reorders. Builds the AI prompt below; save as a version.</div>

      <!-- Selected-element style editor — every change also writes to the AI prompt. -->
      <div v-if="composerSelItem" class="jcs-cv-editor">
        <div class="jcs-cv-editor-title">
          <span>✎ {{ partLabel(composerSelItem.k) }}</span>
          <button type="button" class="jcs-dbg-btn" @click.stop="resetComposerElStyle" title="Clear this element's styles">reset</button>
        </div>
        <div class="jcs-cv-editor-grid">
          <label>Font
            <select v-model="composerSelItem.s.font">
              <option v-for="(f, fi) in composerFonts" :key="fi" :value="f">{{ f ? f.split(",")[0].replace(/'/g, "") : "default" }}</option>
            </select>
          </label>
          <label>Font px
            <input type="number" min="8" max="120" v-model="composerSelItem.s.size" placeholder="e.g. 16" />
          </label>
          <label>Width
            <input type="text" v-model="composerSelItem.s.w" placeholder="auto · 80% · 240" />
          </label>
          <label>Height
            <input type="text" v-model="composerSelItem.s.h" placeholder="auto · 120" />
          </label>
          <label>Text
            <input type="color" v-model="composerSelItem.s.color" />
          </label>
          <label>Fill
            <input type="color" v-model="composerSelItem.s.bg" />
          </label>
          <label>Align
            <select v-model="composerSelItem.s.align">
              <option value="">—</option>
              <option value="left">left</option>
              <option value="center">center</option>
              <option value="right">right</option>
            </select>
          </label>
        </div>
      </div>

      <div class="jcs-comp-body">
        <div class="jcs-comp-palette">
          <div class="jcs-comp-palette-title">Parts</div>
          <div v-for="p in composerParts" :key="p.key" class="jcs-comp-chip" draggable="true"
               @dragstart="onComposerDragStart(p.key)">{{ p.label }}</div>
        </div>
        <div class="jcs-cv-browser">
          <div class="jcs-cv-chrome">
            <span class="jcs-cv-dot"></span><span class="jcs-cv-dot"></span><span class="jcs-cv-dot"></span>
            <span class="jcs-cv-url">{{ ($route && $route.path) || "/" }}</span>
          </div>
          <div class="jcs-cv-viewport">
            <div class="jcs-cv-page jcs-cv-stack" @click.self="composerSelIdx = -1"
                 @dragover.prevent @drop.stop="onStackDrop(composerStack.length)">
              <div v-for="(item, i) in composerStack" :key="'cm-' + i"
                   class="jcs-cv-mock" :class="{ 'jcs-cv-sel': isComposerSel(i) }"
                   :style="mockStyle(item)" draggable="true"
                   @dragstart.stop="onMockDragStart(i)"
                   @dragover.prevent @drop.stop="onStackDrop(i)"
                   @click.stop="selectComposerEl(i)">
                <div class="jcs-cv-mock-tools">
                  <button type="button" @click.stop="moveComposerPart(i, -1)" title="Move up">↑</button>
                  <button type="button" @click.stop="moveComposerPart(i, 1)" title="Move down">↓</button>
                  <button type="button" @click.stop="removeComposerPart(i)" title="Remove">×</button>
                </div>
                <div class="jcs-cv-mock-inner" v-html="mockInner(item.k)"></div>
                <div class="jcs-cv-resize" draggable="false"
                     @mousedown.stop.prevent="startMockResize(i, $event)" title="Drag to resize"></div>
              </div>
              <div v-if="!composerStack.length" class="jcs-cv-blank">Drop parts here — they stack top → bottom</div>
            </div>
          </div>
        </div>
      </div>
      <div class="jcs-comp-save">
        <input type="text" class="jcs-dbg-search" v-model="composerSaveName" placeholder="version name…" />
        <button type="button" class="jcs-dbg-btn" @click.stop="saveComposerVersion">💾 Save version</button>
      </div>
      <div v-if="composerVersions.length" class="jcs-comp-versions">
        <div class="jcs-dbg-compare-label">Version history ({{ composerVersions.length }}) — click to load:</div>
        <div v-for="(v, i) in composerVersions" :key="i" class="jcs-comp-version">
          <span class="jcs-comp-version-name" @click.stop="loadComposerVersion(i)"
                :title="(v.route || '') + ' · ' + v.ts">{{ v.name }} · {{ v.ts }}</span>
          <button type="button" @click.stop="deleteComposerVersion(i)" title="Delete">×</button>
        </div>
      </div>
      <div class="jcs-dbg-compare-label jcs-dbg-mt">📋 Paste to chat to apply this layout (updates live as you add / move / restyle):</div>
      <pre class="jcs-dbg-prompt jcs-cv-prompt">{{ composerPrompt }}</pre>
    </div>

    <!-- ===== LAYOUT COMPOSER — LIVE MOBILE PREVIEW =================
         The COMPOSED page, built live from composerStack as INTERACTIVE parts
         (ComposerMockPart) at phone width — blank until parts are added; each part
         behaves like the published site (header "My Account" dropdown, nav, cart
         qty steppers, pill popovers …). NOT an iframe, NOT static. mockStyle applies
         the user's per-part font/size/resize. Suppressed in preview frames. -->
    <div v-if="composerOn && isMasterUser && !isPreviewFrame" class="jcs-composer-phone"
         :style="{ top: debugPos.top + 'px', left: (debugPos.left + 1210) + 'px' }">
      <div class="jcs-mp-bar">
        <span class="jcs-mp-title">📱 Live mobile preview</span>
        <select class="jcs-dbg-select" v-model.number="composerPhoneIdx" title="Phone size">
          <option v-for="(d, di) in composerPhones" :key="di" :value="di">{{ d.label }} · {{ d.w }}×{{ d.h }}</option>
        </select>
      </div>
      <div class="jcs-mp-note">Your composed page, live &amp; interactive at phone width — add the header and tap “My Account”, open the cart, tap the pills. Empty until you add parts.</div>
      <div class="jcs-cv-phone">
        <div class="jcs-cv-phone-page" :style="{ width: composerPhone.w + 'px', height: composerPhone.h + 'px' }">
          <!-- HOME: the full composed stack -->
          <template v-if="!phonePage">
            <composer-mock-part v-for="(item, i) in composerStack" :key="'lm-' + i"
                                :part-key="item.k" :style="mockStyle(item)" :sim="phoneSim"
                                @navigate="onPhoneNavigate" />
            <div v-if="!composerStack.length" class="jcs-cv-phone-blank">
              Add parts in the composer → they appear here, live and clickable.
            </div>
          </template>
          <!-- DESTINATION: persistent chrome (header / nav) + the sample page + footer -->
          <template v-else>
            <composer-mock-part v-for="(item, i) in phoneChrome" :key="'pc-' + i"
                                :part-key="item.k" :style="mockStyle(item)" :sim="phoneSim"
                                @navigate="onPhoneNavigate" />
            <composer-mock-page :page="phonePage" :sim="phoneSim"
                                @home="phonePage = null" @navigate="onPhoneNavigate" />
            <composer-mock-part v-for="(item, i) in phoneFooterParts" :key="'pf-' + i"
                                :part-key="item.k" :style="mockStyle(item)" :sim="phoneSim"
                                @navigate="onPhoneNavigate" />
          </template>
        </div>
      </div>
    </div>

    <portal-target name="error"></portal-target>

    <!-- Portal target for the floating utility toggle stack (bug-report,
         font-size, language). Head.vue renders the toggles via
         <portal to="util-toggles"> so they teleport here, sitting as a
         direct child of #app rather than inside .stickyHeader. That
         keeps them out of the sticky header's stacking context, so
         their z-index 10000 lives in the root context and unambiguously
         paints above the side rails (z 999) regardless of any
         ancestor's positioning. -->
    <portal-target name="util-toggles"></portal-target>

    <!-- Collapsed-pill rail. Each widget portals its pill into its OWN
         target here. (Originally one shared `multiple` target — but when the
         set of pills changed on cart expand/collapse, portal-vue reused
         DOM + click handlers across the remaining pills, so clicking Info
         opened the Guide modal and vice-versa. Separate targets keep each
         pill bound to its own widget.) `slim` makes an empty target render
         nothing (a comment node, not a flex item), so the flex `gap` only
         spaces the pills that are actually present. Lives at #app's root so
         z-index 9000 beats the side rails (z 999) and isn't trapped in a
         host page's stacking context. -->
    <div class="floating-pill-rail">
      <portal-target name="locked-image-pill" slim></portal-target>
      <portal-target name="cart-pill" slim></portal-target>
      <portal-target name="top-parts-pill" slim></portal-target>
      <portal-target name="report-chat-pill" slim></portal-target>
      <portal-target name="report-filters-pill" slim></portal-target>
      <portal-target name="report-email-pill" slim></portal-target>
      <portal-target name="report-tools-pill" slim></portal-target>
      <!-- LAYOUTS pill for the OTHER /reports/ pages (2026-08-05). The report host owns the real drawer and
           portals its own pill into the target above; the standalone reports (Manufacturing / Foundry /
           Work Order Insights) are separate components with no drawer, so on those pages this stand-in
           appears instead and takes helpdesk to the host with the drawer already open. Rendered only when
           the host's pill is absent, so the two can never both show. -->
      <div v-if="showLayoutsLauncher" class="jcs-layouts-pill" role="button" tabindex="0"
           title="Layouts — pick a report, arrange its cards, publish and rename"
           @click="openLayoutsDrawer" @keydown.enter.space.prevent="openLayoutsDrawer">
        <span class="jcs-layouts-pill-icon" aria-hidden="true">🗂</span>
        <span class="jcs-layouts-pill-label">Layouts</span>
      </div>
      <portal-target name="sales-insights-pill" slim></portal-target>
      <portal-target name="important-info-pill" slim></portal-target>
      <portal-target name="guide-pill" slim></portal-target>
      <portal-target name="account-setup-pill" slim></portal-target>
      <portal-target name="report-tokens-pill" slim></portal-target>
    </div>

    <!-- MOBILE: arrow handle for the right-edge toggle tray. On phones the
         Info/Guide/Setup pills (.floating-pill-rail) and the language/font/
         theme toggles (#utility-toggle-stack) are parked off-screen so they
         stop covering page content; this thin always-visible tab (~½ inch)
         signals they're there. Tap it — or swipe in from the right edge — to
         slide them out; tap again (or swipe right) to park them. Hidden on
         desktop, where the toggles float on the right edge as before. -->
    <button
      v-if="hasEdgeTray"
      type="button"
      class="jcs-edge-tray-handle"
      :class="{ open: edgeTrayOpen }"
      :aria-expanded="edgeTrayOpen ? 'true' : 'false'"
      aria-label="Quick tools"
      @click="toggleEdgeTray"
    >
      <span class="jcs-edge-tray-arrow" aria-hidden="true"></span>
      <span class="jcs-edge-tray-cap">TOOLS</span>
    </button>

    <!-- MOBILE rail peek hit-area (2026-07-26). The parked rail shows only a 3px red strip, which is
         far too thin to tap, so this invisible 22px column sits over it. Tap OR swipe-left reveals;
         it is removed while open so it can never swallow a tap meant for a revealed pill. -->
    <button
      v-if="hasEdgeTray && !edgeTrayOpen"
      type="button"
      class="jcs-rail-peek"
      aria-label="Show quick tools"
      :aria-expanded="edgeTrayOpen ? 'true' : 'false'"
      @click="toggleEdgeTray"
    ></button>

    <!-- ===== GLOBAL FLOATING CART + TOP 5 WIDGETS =====
         A single instance of each, shown on authenticated, non-admin
         portal pages once a company is selected. They render as
         collapsible right-edge panels: the collapsed cart pill
         teleports to the cart-pill target above and the Top 5 pill is
         fixed on its own, while the EXPANDED panels stack inside this
         container (floating mode). One source of truth for the cart
         across every page — the standalone widgets self-sync via the
         $cartBus "cart-updated" event.

         pointer-events:none on the container means it never blocks
         the page; the panels re-enable pointer events on themselves.
         The cart panel is hidden on the cart pages themselves (those
         pages ARE the cart); Top 5 still shows there. -->
    <div class="floating-widget-stack"
         :class="{
           'stack-collapsed': !widgetsExpanded && !lockedImagePanelActive,
           'stack-sticky': stickyPanelMode
         }"
         v-if="showPortalWidgets && !isMobile">
      <!-- Locked image (Search/Orders/RFQ) teleports here when expanded,
           docking at the TOP of the shared stack. The :has() rule below
           force-shows the stack whenever it actually contains the locked
           image, so it stays visible even with an empty cart. -->
      <portal-target name="locked-image-panel" slim></portal-target>
      <!-- Top-5 docked part diagram lands here — its own slot so it never
           collides with the Search/Orders/RFQ locked image above. -->
      <portal-target name="top5-locked-image-panel" slim></portal-target>
      <CartWidget standalone floating :visible="!isCartRoute"
                  @inline-visibility-change="onCartExpandedChange" />
      <TopPartsWidget v-if="!isReportRoute" standalone floating
                  @inline-visibility-change="onTopExpandedChange" />
    </div>

    <!-- Important Information — an always-on right-edge pill that opens
         a modal (it never joins the expandable stack above, so it's
         mounted independently here). Same auth/company gate. -->
    <ImportantInfoWidget v-if="showPortalWidgets && isCustomerRole" />

    <!-- Guide — always-on right-edge pill that opens a modal with a
         captioned MP4 walkthrough of the portal. Pill + modal only
         (never joins the expandable stack). Same auth/company gate. -->
    <HowItWorksWidget v-if="showPortalWidgets" />

    <!-- Live interactive tour overlay. Renders nothing until launched from the
         Guide chooser ('Live tour'); then drives the real page with a
         spotlight + cursor + voiceover, simulating the writes so nothing is
         ever ordered. -->
    <GuideTourWidget v-if="showPortalWidgets" />

    <!-- Admin-only tour BUILDER (type-to-voice + pick-to-highlight, live
         on-page preview). Renders nothing until launched from the Guide
         chooser's "Build / edit tour" (Master-gated there). -->
    <GuideTourBuilder v-if="showPortalWidgets" />

    <!-- Account Setup audit — MASTER/admin-only pill + modal (self-gated to
         the Master role inside the component, so customers never see it).
         Shows how the selected company is configured + flags setup gaps. -->
    <AccountSetupWidget v-if="showPortalWidgets" />

    <template>
      <Foot></Foot>
    </template>

    <!-- TEMP build tag — tiny corner marker so the live build is identifiable
         on a real device during the mobile redesign (ends the "which build am
         I on?" guessing). Remove when the mobile build is done. -->
    <div v-if="isDevEnv" class="jcs-build-tag" @click="runAudioTest"
         title="Tap to run the tour audio self-test (you'll hear a test voice if it works) and copy the report to paste to Claude">{{ buildTagCopied ? "✓ copied — paste to Claude" : "ocr2 · 🔊 audio test" }}</div>

    <!-- TEMP: copyable header-diagnostic overlay (mobile-header debug). The
         alert() version couldn't be selected on a phone, so this uses a
         readonly <textarea> (selectable) + an explicit Copy button. -->
    <div v-if="headerDiag.open" class="jcs-diag-overlay" @click.self="headerDiag.open = false">
      <div class="jcs-diag-box">
        <div class="jcs-diag-title">Header diagnostic — Copy, then paste to Claude</div>
        <textarea ref="diagTa" class="jcs-diag-text" readonly :value="headerDiag.text"
                  @focus="$event.target.select()"></textarea>
        <div class="jcs-diag-btns">
          <button type="button" class="jcs-diag-btn primary" @click="copyHeaderDiagText">
            {{ headerDiag.copied ? "✓ Copied!" : "Copy" }}
          </button>
          <button type="button" class="jcs-diag-btn" @click="headerDiag.open = false">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import menuitems from "@/assets/js/menu.js";
import httpClient from "@/assets/js/HttpClient";
import { installClickRecorder } from "@/assets/js/activity";
import Breadcrumbs from "@/components/Common/Breadcrumbs.vue";
import ComposerMockPart from "@/components/Common/ComposerMockPart.vue";
import ComposerMockPage from "@/components/Common/ComposerMockPage.vue";
import "expose-loader?$!expose-loader?jQuery!jquery";
import "semantic-ui-dropdown/dropdown.js";

export default {
  name: "app",
  components: {
    Breadcrumbs,
    ComposerMockPart,
    ComposerMockPage,
  },
  data() {
    return {
      menu: menuitems,
      collapsed: false,
      // True below the 992px mobile breakpoint (matches responsive.js + the
      // existing @media). On mobile the floating Cart/Top-Parts fold into the
      // page flow instead of the fixed right-edge stack. Updated in handleResize.
      isMobile: typeof window !== "undefined" && window.innerWidth < 992,
      // Mobile cart fold: starts as a compact bar; tap expands the inline cart.
      mobileCartOpen: false,
      // Order/quote counts pushed up from the inline cart (counts-change) for
      // the mobile cart bar's badges.
      mobileCartCounts: { orders: 0, quotes: 0 },
      // TEMP: build-tag tap copies a header layout diagnostic to the clipboard.
      buildTagCopied: false,
      // MOBILE: the right-edge toggle tray (Info/Guide/Setup pills + the
      // language/font/theme toggles) is parked off-screen behind a handle;
      // tapping the handle or swiping left opens it. (Touch-tracking scratch
      // vars are set imperatively on `this` — not declared here — because Vue
      // warns about reactive props whose names start with "_".)
      edgeTrayOpen: false,
      // TEMP: copyable header-diagnostic overlay (selectable text + Copy button).
      headerDiag: { open: false, text: "", copied: false },
      themes: ["", "default-theme"],
      selectedTheme: "default-theme",
      admin: false,
      // Selected company id, mirrored from localStorage (which isn't
      // reactive). Drives the gate for the global floating widgets.
      // Refreshed on every route change; company selection itself does
      // a full window reload (Head.vue selectCompany), so this is read
      // fresh on load anyway.
      companyId: (typeof localStorage !== "undefined" && localStorage.companyId) || "",
      // Whether each global floating widget currently shows its
      // EXPANDED panel (vs. a collapsed pill / nothing). Fed by the
      // widgets' inline-visibility-change events. When either is
      // expanded, portal page content reserves room on the right so
      // it doesn't sit under the panels (see .widgets-expanded CSS).
      cartExpanded: false,
      topExpanded: false,
      // rAF handle for the scroll-throttled widget-stack height clamp
      // (keeps the expanded panels from overlapping the footer).
      _stackRaf: null,
      // Pixels to push the floating cart/Top-5 stack DOWN so a locked zoom
      // image (Search/Orders/RFQ) can sit above the cart with a small gap.
      // 0 = no locked image → stack sits flush at the content top.
      // (The locked image now docks INSIDE the stack via the
      // locked-image-panel portal, so no push-down offset is needed — 0.)
      lockedImageOffset: 0,
      // True while a locked image is expanded into the stack — keeps the
      // stack visible even when the cart + Top 5 are both collapsed/empty.
      lockedImagePanelActive: false,
      // ===== GLOBAL DEBUG OVERLAY (admin-toggleable) =====
      // When on, a fixed overlay shows live layout/alignment diagnostics for
      // the CURRENT page (built by buildDebugText) on every route — no
      // per-page wiring. Toggled by the "Debug overlay" checkbox in Account
      // Setup -> Event Logs & History, which persists localStorage
      // jcs_debugOverlay and emits the $cartBus "debug-overlay-toggle" event.
      debugOverlay:
        typeof localStorage !== "undefined" &&
        localStorage.getItem("jcs_debugOverlay") === "1",
      // Collapsed = show only the title bar (the "_" button); persists so it
      // stays collapsed across route changes / reloads.
      debugCollapsed:
        typeof localStorage !== "undefined" &&
        localStorage.getItem("jcs_debugCollapsed") === "1",
      debugText: "",
      audioTestResult: "",   // 🔊 Tour audio self-test report (debug overlay)
      _debugTimer: null,
      _lastAlignVals: null,
      // Debug overlay comparison: the route picked as the baseline to diff
      // against (persisted; "" = auto-pick the reference page), the generated
      // copy-to-chat fix prompt, the route checklist, and the copy-button label.
      debugBaseline:
        (typeof localStorage !== "undefined" &&
          localStorage.getItem("jcs_debugBaseline")) ||
        "",
      debugPrompt: "",
      debugRoutes: [],
      debugCopyLabel: "Copy fix-prompt",
      // Debug overlay "focus a known uniformity issue" dropdown. Picking a
      // topic injects its fix guidance (the files/selectors involved) into the
      // copy-prompt. These are the recurring site-uniformity problems from this
      // project — keep adding as new classes emerge.
      debugTopic: "",
      debugTopics: [
        { key: "", label: "— focus a known uniformity issue —", guide: "" },
        { key: "adjustcss", label: "🎨 Adjust CSS Styling (live color editor)", guide: "Live color editor: choose the target CSS file (e.g. darkmode.css) below, then PICK an element on the page (or choose one from the list) and tweak its text/background color with a color picker or hex code — the change shows LIVE — then copy the ready-to-paste prompt it generates for chat." },
        { key: "redline", label: "Red line / brand stripe", guide: "The 3px red top stripe is the ::before on .jcs-portal-card > .container (steele-consistency-fix.css brand-stripe block) — NOT a real border-top, which dark/mid mode recolors gray. If a stripe is missing/gray/too-long: ensure the wrapper has class=jcs-portal-card with a direct-child .container, and the ::before right:384px reflow stays @media screen-guarded." },
        { key: "cartalign", label: "Cart / widget alignment", guide: "The floating cart pins to the page content card in App.vue alignWidgetStack (stripeCard override -> .jcs-portal-card > .container, plus #cartTop/#odTop and the dashboard/documents/savedcarts overrides). cart-vs-card should be ~0; if off, the card is not recognized — add class=jcs-portal-card. BUILD PAGES (/search, /orders/*, /rfq/* at >=992 = stickyPanelMode) ALSO pull the RELATIVE build card (#orderForm.create-order-form / .create-rfq-form) UP with a negative margin-top so its red line meets the position:FIXED cart. That pull MUST be computed in DOCUMENT space — `card.getBoundingClientRect().top + scrollY` vs the stable target (Math.max(96,headerClear)+off when sticky, docTop otherwise) — NOT the live stack rect and NOT a viewport-only gap. Why: onCartUpdatedRealign fires AFTER the add-to-cart MODAL while the page is often SCROLLED; a viewport-only gap omits scrollY, understates the pull, and leaves the card ~50px low until a manual scroll/refresh (worse on smaller screens, where you scroll more). SYMPTOM in the 🔍 dump: the build card has NO / too-small inline margin-top while its top sits below the cart; the readout's `align: … scrollY=` shows whether the last realign ran scrolled. The live stack rect also reads height:0 mid re-render right after the modal — another reason to use the stable target, not the rect." },
        { key: "headerclear", label: "Header clearance / top offset", guide: "Authenticated pages need body padding-top:245 to clear the fixed header (steele section 2). body:has(#requestAcct) zeroes it for Login/Registration; pages reusing #requestAcct must add class .acct-info-view so the rule (which is :not(.acct-info-view)) skips them. Symptom: content top=0, cart behind the header." },
        { key: "brandcard", label: "Branded white card", guide: "The white theme-aware card is the .jcs-portal-card > .container ::before. To give a page the look: add class=jcs-portal-card to its section wrapper plus a direct-child div.container. Used by /account/information and /report-bug." },
        { key: "reflow", label: "Widget reflow gutter", guide: "When the cart/Top-Parts panel expands, content reserves a right gutter (padding-right ~384-408px) via #app.widgets-expanded rules in BOTH App.vue and steele-consistency-fix.css. BOTH must be @media screen-guarded or the gutter leaks into print and narrows the printout." },
        { key: "print", label: "Print layout / width", guide: "Order/Quote printouts (OrderDetails/RFQDetails @media print) anchor on .jcs-print-doc and force the #orderDetails/#odTop/.container chain to width:100% so the Bootstrap container max-width and the widget gutter cannot narrow the page. The print-affecting steele gutter is @media screen-guarded." },
        { key: "darkmode", label: "Dark / mid mode card gray", guide: "html.dark-mode / html.mid-mode overrides live in darkmode.css / mid-mode.css. Cards/panels must use the theme SURFACE gray (dark #1f242b on page --bg #15181c), NEVER black or white — e.g. the floating cart (.staging-tray-wrapper) and Top Parts (.top-parts-widget/.top-parts-card) need background-color:#1f242b !important under html.dark-mode (mid-mode.css gets the mid-gray). Watch the THEME bg readout above: if cart/top5 show rgb(0,0,0) or transparent instead of rgb(31,36,43), a sub-element is overriding the surface. Also: stripes use a ::before background (real border-tops get recolored gray), and alignment lookups must be color-INDEPENDENT.", previewCss: "html.dark-mode .floating-widget-stack .staging-tray-wrapper, html.dark-mode .floating-widget-stack .top-parts-card { background-color: #1f242b !important; }" },
        { key: "matchcolor", label: "Match card color to baseline", guide: "Make THIS page's content card use the SAME dark/mid background as the checked baseline page. If they differ, the brand-stripe ::before color (steele-consistency-fix.css: html.dark-mode .jcs-portal-card > .container::before, currently #14171c) is out of step with the standard surface #1f242b (darkmode.css). Align them so every card reads the same gray. Check a baseline below, then hit Preview to see the match.", previewMatch: true },
        { key: "cardtocart", label: "Dark/mid: card → cart gray (#1f242b)", guide: "Make THIS page's content card the SAME surface gray as the floating cart + Top-Parts (dark #1f242b). By design (darkmode.css ~line 927) some content cards sit on the near-black content SHEET #15181c so they read 'black up to the rails'; this promotes the card UP to the #1f242b card surface so the content and the widgets match. STANDARD (firm): dark cards = #1f242b, mid = #b9bcc0. FIX: in darkmode.css add `html.dark-mode <selector> { background:#1f242b !important; }` — but MATCH the id-count of whatever base rule paints the card white. On SavedCarts/Documents/dashboard `#ao-table` sits inside `#acctOrders` and steele-consistency-fix.css (~L1199) has `#acctOrders #ao-table { background:#fff !important }` (2 ids), so a bare `html.dark-mode #ao-table` (1 id) LOSES and the card stays white — use `html.dark-mode #acctOrders #ao-table` (already in place for those). Keep the red ::before / border-top stripe. For mid mode add the mirror rule in mid-mode.css with its mid surface. This page's cardSel + route are in the measurements above; hit Preview to see it live.", previewCartColor: true },
        { key: "navreparent", label: "Nav band tall / My Account not bouncing back (responsive.js reparent)", guide: "Symptom A: the top nav BAND is taller than the menu row. Symptom B: a header element (esp. #header-account 'My Account') doesn't RETURN to the header after a dev-tools/viewport toggle (a REFRESH fixes it). Both come from src/assets/js/responsive.js, which MOVES #logo / form#search / #hc-items / #header-account between #main-nav (mobile, <992px) and #header-wrapper (desktop, >=992px) via jQuery on resize. Two failure modes: (1) an element left stuck in #main-nav inflates the brushed-steel band (bg background-size:220px 100% fills 100% of nav height); (2) the desktop 'move back' uses HARDCODED anchors — if an anchor element doesn't exist, the jQuery move silently no-ops and the element STRANDS in #main-nav. CONFIRMED REAL anchors (Head.vue): #logo → '#header-wrapper .logo-cont'; form#search → '#header-search'; #header-account → '#header-wrapper .header-utility-row' (inside .account-cont, ~L127). The legacy anchors '.hi-phone' and '#hc-items' DO NOT EXIST in the current markup (grep src: they appear only in responsive.js) — anything pointed at them strands; that was the 'everything bounced back except My Account' bug. The overlay AUTO-FLAGS stuck ids as 'responsive.js REPARENT STUCK' at >=992. FIXES: (a) debounce the resize handler to the SETTLED width; (b) point each desktop reparent at an anchor that EXISTS (grep-verify before trusting it); (c) responsive.js has a SELF-HEALING net (jcsHealReparent: records each element's desktop home, then forces any element still inside #main-nav back to that home) — keep it intact through the mobile refactor so a renamed/moved anchor can't reintroduce this; (d) steele-consistency-fix.css @media(min-width:992) net absolute-positions + vertically centers anything residual so the band can't inflate. NOTE: a stranded #header-account ALSO traps its dropdown under the search — see the 'Z-index / stacking' topic. KEY LESSON: JS DOM-reparenting is invisible to getComputedStyle — a flex container TALLER than its tallest child = extra reparented nodes; for ANY resize-triggered, refresh-fixable layout bug, check responsive.js FIRST and confirm every element it moves into #main-nav has a desktop branch pointing at a REAL anchor." },
        { key: "zindex", label: "Z-index / stacking (dropdown behind a layer)", guide: "A header menu/dropdown opening BEHIND another element. CONFIRMED topology for this site (use the 🔍 dump to re-verify): .stickyHeader (wraps <Head> + <MainNav>) is an UNCONDITIONAL stacking context at z-index:9200 !important (steele-consistency-fix.css, search 'Raised 1100'), set so header dropdowns clear the floating WIDGET STACK (z 8500) + PILL RAIL (z 9000) — both are direct #app children (siblings of .stickyHeader), so anything inside .stickyHeader paints OVER them (that's why a header dropdown is correctly over the cart). THE TRAP ('under the search but over the cart'): #header-search is z 999 INSIDE .stickyHeader; #main-nav has TWO competing rules — steele `#main-nav{z-index:1001}` AND app.css `#main-nav{z-index:9}` — SAME specificity, and app.css loads LATER, so z-index:9 WINS. When #header-account is stranded inside #main-nav (see the reparent topic), its dropdown (z 9600) is CAPPED by #main-nav's real context = 9, which is < the search's 999 → it opens UNDER the search. FIX: `#main-nav { z-index: 1001 !important }` in steele (beats app's 9, clears the 999 search, stays below .stickyHeader's 9200). GENERAL RULE: a dropdown's own z-index can't escape an ANCESTOR stacking context — raise the nearest ancestor context above the layer it's losing to. LESSON: when 🔍 shows two same-specificity z-index rules on one selector from DIFFERENT files, the COMPUTED winner is the LATER-loaded file (the app.css bundle loads after the steele <link>) — don't trust the higher number. The 🔍 dumps z-index/transform/isolation/position for #header-account, its .dropdown-menu, #header-search, and .floating-widget-stack." },
        { key: "sharedid", label: "Shared id / hidden duplicate", guide: "#requestAcct is reused by Registration/RegistrationSuccess/Account-Info. Element lookups must take the VISIBLE match (skip getClientRects().length==0), and id-keyed rules need :not(.acct-info-view) to single out the account page." },
        { key: "container", label: "Container max-width", guide: "Bootstrap .container has a per-breakpoint max-width; steele sizes #routeView .container. In print, override to width:100% to avoid a narrow, centered printout." },
      ],
      // Search filter for the compare-against route checklist.
      debugRouteSearch: "",
      // Label on the one-click auto-fix button (flips to a confirmation on copy).
      debugFixLabel: "⚡ Copy auto-fix for this page",
      // Live preview of the selected topic's fix (injects a temp <style> so the
      // change is visible on the page before committing it via a prompt).
      debugPreviewOn: false,
      // Always-visible theme readout (card / cart / Top-Parts background colors).
      debugTheme: "",
      // Draggable overlay position (px from top/left), persisted in
      // localStorage.jcs_debugPos. Drag by the title bar.
      debugPos: { top: 54, left: 8 },
      // ===== Mobile preview (3 phone frames, debug console) =====
      // True if THIS app instance is running INSIDE a preview iframe (its name is
      // "jcs-preview:<browser>") — suppresses the overlay + preview container so
      // the frames never recurse. Resolved once at boot (the iframe's name
      // attribute is set before the app loads, so window.name is ready).
      isPreviewFrame: (function () {
        try {
          return (
            window.self !== window.top &&
            typeof window.name === "string" &&
            window.name.indexOf("jcs-preview:") === 0
          );
        } catch (e) {
          return false;
        }
      })(),
      mobilePreviewOn: false,
      mobilePreviewSrc: "",
      mobilePreviewNonce: 0,
      mobilePreviewDeviceIdx: 1,
      mobilePreviewDevices: [
        { label: "iPhone SE", w: 375, h: 667 },
        { label: "iPhone 14", w: 390, h: 844 },
        { label: "Pixel 7", w: 412, h: 915 },
        { label: "Galaxy S8", w: 360, h: 740 },
      ],
      mobilePreviewBrowsers: [
        { key: "chrome", label: "Chrome" },
        { key: "safari", label: "Safari" },
        { key: "firefox", label: "Firefox" },
      ],
      // ===== Layout composer (drag page parts into L/C/R zones → AI prompt) =====
      // DB-backed: api/account/composer-layouts (Master-only). localStorage
      // (jcs_composerVersions) is the instant-boot + pre-deploy/offline fallback.
      composerOn: false,
      composerDragKey: null,
      composerDragFrom: null, // index being dragged to reorder (null = adding from palette)
      composerSaveName: "",
      composerStack: [], // single ordered list of placed parts (top → bottom)
      composerVersions: [],
      composerSelIdx: -1, // selected stack index (for the style editor)
      // Right-side LIVE phone renders the composed parts as INTERACTIVE components.
      composerPhoneIdx: 0,
      // When a link in the preview is clicked, show that sample page (null = composed home).
      phonePage: null,
      // Shared simulation state for the preview (the mock cart) so search + cart stay in sync across parts.
      phoneSim: { cart: [] },
      composerPhones: [
        { label: "iPhone", w: 390, h: 800 },
        { label: "Android", w: 360, h: 760 },
        { label: "Small", w: 320, h: 680 },
      ],
      composerFonts: [
        "",
        "Arial, sans-serif",
        "Georgia, serif",
        "'Times New Roman', serif",
        "'Courier New', monospace",
        "system-ui, sans-serif",
        "'Trebuchet MS', sans-serif",
        "Verdana, sans-serif",
      ],
      composerParts: [
        { key: "cart", label: "Cart (floating)" },
        { key: "topparts", label: "Top Parts (floating)" },
        { key: "info", label: "Important Info (floating)" },
        { key: "guide", label: "Guide (floating)" },
        { key: "setup", label: "Account Setup (floating)" },
        { key: "buildcard", label: "Search / build card" },
        { key: "content", label: "Content card" },
        { key: "header", label: "Header (logo / phone / account)" },
        { key: "nav", label: "Top nav menu" },
        { key: "breadcrumb", label: "Breadcrumb" },
        { key: "search", label: "Header search" },
        { key: "footer", label: "Footer" },
      ],
      // Target-CSS-file dropdown — tells the copy-prompt WHICH stylesheet to
      // edit. These are the three shared theme/structure files.
      debugCssFile: "",
      debugCssFiles: [
        { key: "", label: "— target CSS file (optional) —", guide: "" },
        { key: "steele", label: "steele-consistency-fix.css", guide: "public/steele-consistency-fix.css — the shared STRUCTURE + brand stylesheet (plain <link>, applies in print too, NON-hashed so hard-refresh after edits). Owns the brand-stripe card (.jcs-portal-card ::before), body header-clearance padding, the widget reflow gutter, and page panels. Light mode + layout, NOT theme colors." },
        { key: "darkmode", label: "darkmode.css", guide: "src/assets/css/frontend/darkmode.css — html.dark-mode overrides. Palette: --bg #15181c (page), --surface #1f242b (cards/panels), --input #11151a. To make a card GRAY not black in dark mode, give its panel background-color:#1f242b !important under html.dark-mode. Floating cart panel = .staging-tray-wrapper; Top Parts = .top-parts-widget / .top-parts-card / .top-parts-panel." },
        { key: "midmode", label: "mid-mode.css", guide: "src/assets/css/frontend/mid-mode.css — html.mid-mode overrides (the lighter mid theme, mirror of darkmode.css with the mid-gray surface). Apply the same card-background fix here with the mid palette." },
      ],
      // ===== Live CSS color editor (focus "Adjust CSS Styling") =====
      // Distinct styleable selectors found on the current page (friendly-labeled),
      // the selector being edited, and its working + original text/background
      // colors. applyCssEdit injects a temp <style> so a change shows LIVE; the
      // buildCssEditPrompt emits an AI prompt naming the target file + selector.
      cssEditList: [],
      cssEditSearch: "",
      cssEditPick: -1,
      cssEditSel: "",
      cssEditCurLabel: "",
      cssEditNeighbors: [],
      cssEditColor: "#000000",
      cssEditBg: "#000000",
      cssEditColorOrig: "",
      cssEditBgOrig: "",
      cssPickMode: false,
      // Which field a clicked palette swatch loads into ("text" | "bg").
      // Follows whichever color field you last focused.
      cssEditTarget: "text",
      // Which pseudo-state the color edits target: "base" (resting / No Hover),
      // "hover" (:hover / On Hover), or "active" (:active / On Click). Each state
      // accumulates its own {color,bg} in cssEditByState so all three can be set
      // and the prompt emits a rule per touched state.
      cssEditState: "base",
      cssEditByState: { base: null, hover: null, active: null },
      // When the picked element's own background is HIDDEN behind an opaque child
      // tiling over it (e.g. a dropdown .item over the .menu — only in dark/mid
      // mode, where the items get an opaque bg), this holds {sel,label} of that
      // child so the editor can offer "edit the rows instead".
      cssEditCoveredBy: null,
      // Live "under the cursor" hover cluster while in pick mode (the hovered
      // element + its parents/siblings/children). Nodes kept non-reactive in
      // _cssHoverEls; the highlight box is _cssHoverBox.
      cssHoverList: [],
      // Viewport-width gate for STICKY panel mode. The viewport-pinned sticky
      // sidebar (position:fixed + viewport-bounded height) is a DESKTOP/large
      // layout only — at lg+ (>=992px) where the build pages reserve a right
      // gutter for it. Below 992px the panels overlay as a drawer / collapse to
      // pills, so we DON'T pin a fixed panel there (it could cover the results
      // on a phone). Kept in sync in handleResize(); 992 matches the existing
      // @media (max-width: 991.98px) breakpoint used elsewhere in this file.
      viewportWide:
        typeof window !== "undefined" ? window.innerWidth >= 992 : true,
    };
  },
  created() {
    window.addEventListener("load", this.handleResize);
    window.addEventListener("resize", this.handleResize);
    // Closing the browser print dialog leaves the page mid-reflow; re-run
    // the resize handler so the sticky-header measurement (and the
    // breadcrumb / search alignment) settles back to normal.
    window.addEventListener("afterprint", this.handleResize);
    window.addEventListener("pageshow", this.handlePageRestore);
    document.addEventListener("visibilitychange", this.handleVisibilityRestore);
    // Re-clamp the floating widget stack as the footer scrolls into / out
    // of view so the expanded panels never overlap it. Passive — we only
    // read layout, never block the scroll.
    window.addEventListener("scroll", this.onWindowScroll, { passive: true });
    this.handleResize();
    // Inside a mobile-preview iframe (name "jcs-preview:<browser>"), tag <html>
    // with the browser quirk-profile class so steele-consistency-fix.css applies
    // that profile + hides the debug chrome. isPreviewFrame (data) already
    // suppresses the overlay + preview container so the frames never recurse.
    if (this.isPreviewFrame) {
      try {
        var _br = (window.name || "").slice("jcs-preview:".length) || "chrome";
        document.documentElement.classList.add("jcs-pv", "jcs-pv-" + _br);
      } catch (e) {
        /* ignore */
      }
    }
    // Load saved layout-composer versions: instant from localStorage; the
    // composer refreshes from the DB (api/account/composer-layouts) on open.
    try {
      var _cv = localStorage.getItem("jcs_composerVersions");
      if (_cv) this.composerVersions = JSON.parse(_cv) || [];
    } catch (e) {
      /* ignore */
    }
  },
  destroyed() {
    window.removeEventListener("load", this.handleResize);
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("afterprint", this.handleResize);
    window.removeEventListener("pageshow", this.handlePageRestore);
    document.removeEventListener("visibilitychange", this.handleVisibilityRestore);
    window.removeEventListener("scroll", this.onWindowScroll);
    // Tear down the live CSS color editor's temp <style> + element-pick handler.
    this.revertCssEdit();
    this._endCssPick();
    if (Vue.prototype.$cartBus) {
      Vue.prototype.$cartBus.$off("locked-image-change", this.onLockedImageChange);
      Vue.prototype.$cartBus.$off("cart-updated", this.onCartUpdatedRealign);
    }
    if (this._cartAlignTimer) {
      clearTimeout(this._cartAlignTimer);
      this._cartAlignTimer = null;
    }
    if (this._stackRaf) {
      cancelAnimationFrame(this._stackRaf);
      this._stackRaf = null;
    }
    if (this._stickyHeaderRO) {
      this._stickyHeaderRO.disconnect();
      this._stickyHeaderRO = null;
    }
    if (this._stickyHeaderRaf) {
      cancelAnimationFrame(this._stickyHeaderRaf);
      this._stickyHeaderRaf = null;
    }
    // Disconnect the build-card ResizeObserver / MutationObserver and cancel
    // any pending realign frame so they don't fire after teardown.
    this._disconnectBuildCard();
    document.body.classList.remove("dashboard");
  },
  watch: {
    // The mobile cart bar now shows/hides based on whether the cart has items, so the sticky-header height changes
    // when it appears/disappears → re-measure (applyStickyClasses sets body padding-top + --jcs-sticky-h) once the
    // bar has re-rendered, so page content still clears the header.
    mobileCartCounts() { this.$nextTick(this.applyStickyClasses); },
    // MOBILE edge tray: mirror the open flag onto <html> so the parked
    // toggle groups (.floating-pill-rail + #utility-toggle-stack) slide in
    // via CSS, and flip the handle arrow. Toggling a root-level class keeps
    // the two independent fixed groups in lock-step from one source of truth.
    edgeTrayOpen(v) {
      if (typeof document !== "undefined") {
        document.documentElement.classList.toggle("jcs-tray-open", !!v);
      }
    },
    // Add/remove the html.jcs-has-tray gate as the user moves between phone
    // portal pages and elsewhere; collapse the tray when it stops existing.
    hasEdgeTray(v) {
      if (typeof document !== "undefined") {
        document.documentElement.classList.toggle("jcs-has-tray", !!v);
      }
      if (!v) this.edgeTrayOpen = false;
    },
    currentPath() {
      this.updateAdminState();
      // Collapse the mobile toggle tray on navigation so it never covers the
      // freshly-loaded page.
      this.edgeTrayOpen = false;
      // Tear down the previous page's build-card observers. The :key on
      // <router-view> swaps the card element on navigation, so the old
      // observers point at a detached node; clearing them (and the cached
      // ref) lets the new page's build card re-attach cleanly in the
      // alignWidgetStack() call below, and stops a stale relative-card
      // observer from firing realigns for a page that no longer has one.
      this._disconnectBuildCard();
      // Leaving a build page drops any locked-image offset so the stack
      // returns to the content top on the next page (the locked image is
      // page-scoped and won't survive the route change anyway).
      this.lockedImageOffset = 0;
      // Keep the company gate in sync across SPA navigations.
      this.companyId =
        (typeof localStorage !== "undefined" && localStorage.companyId) || "";
      // The global cart + Top 5 widgets mount ONCE and never re-mount
      // on navigation, so they don't otherwise re-read the server cart
      // when the user lands on a page that changed it (e.g. adding a
      // part on Shop Assembly). Pinging cart-updated on every route
      // change makes the standalone widgets re-sync, guaranteeing the
      // floating cart is fresh app-wide regardless of whether the
      // source page emitted the event itself.
      if (this.$cartBus) {
        this.$cartBus.$emit("cart-updated");
      }
      setTimeout(() => {
        this.applyStickyClasses();
        // New route → different first-card position + page height. Re-align
        // the widget stack to the new content card and re-clamp vs footer.
        this.alignWidgetStack();
      }, 300);
    },
    // Re-measure the sticky header whenever the signed-in user changes.
    // The header grows to include greeting + company selector once a
    // user is loaded, and would otherwise be measured at its shorter
    // logged-out height — causing the nav to overlap the utility row.
    currentUser() {
      this.$nextTick(() => {
        // Slight delay to let any conditional rendering (v-if="currentUser"
        // blocks in Head.vue) finish so offsetHeight reflects the final
        // size.
        setTimeout(() => {
          this.applyStickyClasses();
          // The floating widgets appear once the user (and company) load,
          // so align the stack to the content card now that they're shown.
          this.alignWidgetStack();
        }, 150);
      });
    },
    // Re-align the floating stack whenever the cart / Top 5 expands or
    // collapses. Expanding reflows the page, and on the dashboard the
    // insights panel may have finished rendering after the initial
    // alignment — so re-measure to keep the stack flush with the content's
    // red top line (e.g. the insights panel) rather than a lower card that
    // was the only one rendered when alignment first ran.
    widgetsExpanded() {
      this.$nextTick(() => {
        this.alignWidgetStack();
      });
    },
    // Switching INTO or OUT of a sticky-panel route flips the stack between
    // position:fixed (viewport-pinned, viewport-bounded, internally
    // scrollable) and position:absolute (scroll-with-page). Re-align + clamp
    // so the new mode's top/max-height/overflow are applied immediately
    // rather than waiting for the next scroll/resize.
    stickyPanelMode() {
      this.$nextTick(() => {
        this.alignWidgetStack();
      });
    },
  },
  mounted() {
    // Activity trail: ONE delegated document listener for the whole app, installed here
    // rather than per component. Capture phase + passive, so it cannot interfere with any
    // handler, and it records WHICH control was clicked — never the value inside it.
    installClickRecorder();
    this.updateAdminState();
    this.applyStickyClasses();
    this.alignWidgetStack();
    // Build pages emit "locked-image-change" when a zoom image is locked or
    // closed; we offset the floating stack so the cart drops below it.
    if (!Vue.prototype.$cartBus) {
      Vue.prototype.$cartBus = new Vue();
    }
    Vue.prototype.$cartBus.$on("locked-image-change", this.onLockedImageChange);
    // Re-pin the stack + re-pull the relative build card after ANY cart change
    // (add / remove / PO# or Quote-Ref via the modal). The widgetsExpanded
    // watcher only fires on TOGGLE, so a 2nd add to an already-expanded cart
    // wouldn't re-align — which left the Search build card sitting ~50px low
    // until a manual refresh. Debounced so it runs once after the cart settles.
    Vue.prototype.$cartBus.$on("cart-updated", this.onCartUpdatedRealign);
    // Global debug overlay: react to the Account Setup checkbox toggling it,
    // and start the live refresh loop if it was already on (persisted).
    Vue.prototype.$cartBus.$on(
      "debug-overlay-toggle",
      this.onDebugOverlayToggle
    );
    try {
      var _dp = JSON.parse(localStorage.getItem("jcs_debugPos") || "null");
      if (_dp && typeof _dp.top === "number" && typeof _dp.left === "number")
        this.debugPos = _dp;
    } catch (e) {
      /* ignore */
    }
    if (this.debugOverlay) this.startDebugTimer();
    // MOBILE edge tray: a swipe-from-the-right-edge opens it and a swipe to
    // the right closes it — a bonus gesture alongside the always-visible
    // arrow handle. Passive listeners (we never preventDefault) so page
    // scrolling stays smooth. App.vue is the root and never unmounts, so
    // there's no teardown to leak.
    window.addEventListener("touchstart", this.onTrayTouchStart, {
      passive: true
    });
    window.addEventListener("touchend", this.onTrayTouchEnd, { passive: true });
    // Sync the tray gate class once on mount (the watcher only fires on change).
    if (typeof document !== "undefined") {
      document.documentElement.classList.toggle("jcs-has-tray", this.hasEdgeTray);
    }
  },
  computed: {
    ...mapGetters({ currentUser: "currentUser", effectiveRoleName: "effectiveRoleName" }),
    // Only the Customer role sees the INFO pill.
    isCustomerRole() { return this.effectiveRoleName === "Customer"; },
    currentPath() {
      return this.$route.fullPath;
    },
    // Show the global floating Cart + Top 5 widgets only for a
    // signed-in user, on a non-admin portal route, with a company
    // selected. The widgets additionally self-hide when their data
    // is empty (no cart items / no purchase history).
    showPortalWidgets() {
      return !!this.currentUser && !this.admin && !!this.companyId;
    },
    // The mobile edge tray only exists where there are toggles to reveal —
    // a signed-in portal page on a phone. Used to gate BOTH the arrow handle
    // (v-if) and the park-CSS (via the html.jcs-has-tray class), so login /
    // no-company pages never park the toggles behind a missing handle.
    hasEdgeTray() {
      return this.isMobile && this.showPortalWidgets;
    },
    // TRUE only on non-production hosts (sandbox / localhost). Used to gate the
    // temporary mobile-redesign dev markers (build tag + header diagnostics) so
    // they stay visible while testing on sandbox but NEVER show to customers on
    // the production domain — no manual stripping needed per release. Sandbox is
    // always served from a *sandbox* hostname; production is jcsteeleservice.com.
    isDevEnv() {
      var h =
        (typeof window !== "undefined" &&
          window.location &&
          window.location.hostname) ||
        "";
      return /sandbox|localhost|127\.0\.0\.1|::1/i.test(h);
    },
    // Master/admin detection — mirrors AccountSetupWidget.isMasterUser so the
    // admin-only layout composer / debug overlay / mobile preview render only
    // for a Master, defensively (their entry point is already Master-gated, but
    // this guards a stale localStorage debug flag on a shared browser).
    isMasterUser() {
      const u = this.currentUser || {};
      if (u.isAdmin === true || u.isMaster === true) return true;
      const matches = function (val) {
        if (!val) return false;
        const s =
          typeof val === "string"
            ? val
            : (val && val.name) || (val && val.role) || "";
        const low = String(s).toLowerCase();
        return low === "master" || low === "admin";
      };
      if (matches(u.role)) return true;
      if (Array.isArray(u.roles) && u.roles.some(matches)) return true;
      return false;
    },
    // Debug overlay: the compare-against route list filtered by the search box.
    filteredDebugRoutes() {
      var q = (this.debugRouteSearch || "").toLowerCase().trim();
      if (!q) return this.debugRoutes;
      return this.debugRoutes.filter(function (r) {
        return r.path.toLowerCase().indexOf(q) !== -1;
      });
    },
    // True when the focused topic ships a live-preview CSS snippet.
    selectedTopicHasPreview() {
      var t = this._topicByKey(this.debugTopic);
      return !!(t && (t.previewCss || t.previewMatch));
    },
    // True when the live CSS color editor focus ("Adjust CSS Styling") is chosen.
    isAdjustCssFocus() {
      return this.debugTopic === "adjustcss";
    },
    // Uniform display height for the 3 phone frames: the device height, capped to
    // ~74% of the viewport so the row fits (the page scrolls inside each frame).
    previewFrameHeight() {
      var d = this.mobilePreviewDevices[this.mobilePreviewDeviceIdx] || { h: 720 };
      var vh = (typeof window !== "undefined" && window.innerHeight) || 800;
      return Math.min(d.h, Math.round(vh * 0.74));
    },
    // The paste-to-chat prompt describing the composed layout (zones + order).
    composerPrompt() {
      return this.buildComposerPrompt();
    },
    composerSelItem() {
      var i = this.composerSelIdx;
      return i >= 0 && this.composerStack[i] ? this.composerStack[i] : null;
    },
    composerPhone() {
      return this.composerPhones[this.composerPhoneIdx] || { label: "iPhone", w: 390, h: 800 };
    },
    // Header / nav / breadcrumb stay as persistent chrome when you navigate to a page.
    phoneChrome() {
      return (this.composerStack || []).filter(function (it) {
        return ["header", "nav", "breadcrumb"].indexOf(it.k) >= 0;
      });
    },
    phoneFooterParts() {
      return (this.composerStack || []).filter(function (it) {
        return it.k === "footer";
      });
    },
    // Element list filtered by the search box (matches the friendly label, which
    // includes the tag/class + a sample of the element's text — so "your cart"
    // jumps straight to it).
    _cssMatches() {
      var q = (this.cssEditSearch || "").trim().toLowerCase();
      var all = this.cssEditList || [];
      if (!q) return all;
      return all.filter(function (e) {
        return e.label.toLowerCase().indexOf(q) >= 0;
      });
    },
    // Cap the RENDERED rows for performance ("every element" can be hundreds);
    // the search narrows past the cap.
    filteredCssEditList() {
      return this._cssMatches.slice(0, 250);
    },
    cssEditMoreCount() {
      return Math.max(0, this._cssMatches.length - 250);
    },
    // Friendly description of the element currently selected for color editing.
    cssEditDesc() {
      return this.cssEditCurLabel;
    },
    // The ready-to-paste AI prompt for the current color edit.
    cssEditPrompt() {
      return this.buildCssEditPrompt();
    },
    // Quick-pick swatches: the selected element's ORIGINAL colors (one-click
    // reset) followed by the site's canonical dark/mid theme palette + neutrals,
    // de-duped. Extend this list as the palette grows.
    cssEditPalette() {
      var pal = [];
      var seen = {};
      function push(c, n) {
        if (!c) return;
        var k = ("" + c).toLowerCase();
        if (seen[k]) return;
        seen[k] = 1;
        pal.push({ c: c, n: n });
      }
      push(this.cssEditColorOrig, "↺ original text");
      push(this.cssEditBgOrig, "↺ original background");
      push("#1f242b", "Card / cart surface — dark standard");
      push("#15181c", "Page bg / content sheet — dark");
      push("#11151a", "Input bg — dark");
      push("#0d0d12", "Deep / portal-card under — dark");
      push("#e3e7eb", "Light text — dark mode");
      push("#9aa0a6", "Muted text");
      push("#b9bcc0", "Card surface — mid standard");
      push("#c6c8cc", "Card ::before — mid");
      push("#e2231a", "Steele red — brand");
      push("#ffffff", "White");
      push("#000000", "Black");
      return pal;
    },
    // The cart pages ARE the cart, so the floating cart panel is
    // redundant there (Top 5 still shows). Matches /cart and /cart/*.
    isCartRoute() {
      var p = (this.$route && this.$route.path) || "";
      return p === "/cart" || p.indexOf("/cart/") === 0;
    },
    // The Customer Insights report is an analysis page, not a shopping page —
    // suppress the floating Top Parts widget there.
    isReportRoute() {
      var p = (this.$route && this.$route.path) || "";
      return p.indexOf("/reports") === 0;
    },
    // Show the stand-in Layouts pill on the /reports/ pages that are NOT the report host — the host
    // (CustomerInsights.vue) portals its own, fully-functional pill into report-tools-pill.
    // helpdesk@jcsteele.com + Master only, matching the host's isLayoutsAdmin. Note this checks the REAL
    // user, so it stays hidden while previewing as someone else.
    showLayoutsLauncher() {
      if (!this.isReportRoute || this.isPreviewFrame) return false;
      var p = (this.$route && this.$route.path) || "";
      if (p.indexOf("/reports/customer-insights") === 0) return false;   // host owns the pill there
      if (!this.isMasterUser) return false;
      var ru = (this.$store && this.$store.getters) ? this.$store.getters.realUser : null;
      return String((ru && ru.email) || "").trim().toLowerCase() === "helpdesk@jcsteele.com";
    },
    // ===== STICKY PANEL MODE =====
    // The build / shopping pages (Search, Create Order, Create Quote, and
    // the assembly / wear-part shop views) lay their content out as a tall
    // results list on the LEFT with the global floating widget stack (locked
    // image + cart + Top Parts) on the right. On those pages the right stack
    // must behave as a VIEWPORT-PINNED sticky sidebar: it stays on-screen as
    // the left list scrolls, is bounded by the viewport height (NOT the
    // results column / footer), and scrolls INTERNALLY when its own content
    // is taller than the available space — so Top Parts is always reachable
    // even after the left list ends.
    //
    // Everywhere else (most importantly the dashboard, /account) the stack
    // KEEPS its scroll-with-the-page behavior (position:absolute, no clamp)
    // so the dashboard request that introduced it isn't regressed.
    //
    // Detected by ROUTE (path-prefix), not by a page section id, because the
    // build pages all share this same left-list/right-stack layout and a
    // route test is the most robust signal. The dashboard is explicitly
    // excluded. Matches:
    //   /search, /search/index
    //   /orders/create | chooseassembly | shopassembly | shopwearparts
    //   /rfq/create   | chooseassembly | shopassembly | shopwearparts
    stickyPanelMode() {
      if (!this.showPortalWidgets) return false;
      // Desktop/large only — below 992px fall back to the existing drawer/pill
      // behavior so a fixed panel never covers the results on tablet/mobile.
      if (!this.viewportWide) return false;
      var p = ((this.$route && this.$route.path) || "").toLowerCase();
      if (p === "/account" || p.indexOf("/account/") === 0) return false;
      if (p === "/search" || p.indexOf("/search") === 0) return true;
      if (p.indexOf("/orders/") === 0) return true;
      if (p.indexOf("/rfq/") === 0) return true;
      return false;
    },
    // True when either global floating widget is showing its expanded
    // panel (not just a collapsed pill). Drives the .widgets-expanded
    // class on #app so portal page content reserves right-side room
    // and never sits under the panels.
    widgetsExpanded() {
      // The report hides all floating widgets (Cart / Top Parts are v-if="!isReportRoute"), so never
      // reserve their right-side gutter there — otherwise the report grid is squeezed into the left ~60%.
      if (this.isReportRoute) return false;
      return this.showPortalWidgets && (this.cartExpanded || this.topExpanded);
    },
    showGlobalBreadcrumbs() {
  const hiddenPaths = [
    "/",
    "/login",
    "/logout",
    "/registration",
    "/registrationsuccess",
    "/resetpassword",
    "/createnewpassword",
  ];

  // Hidden on mobile too (per the mobile redesign) — frees vertical space and
  // lets the page heading sit up under the nav.
  // Also hidden on the Sales Insights report (/reports): its own in-page filter
  // bar owns the date range + filters now (the header breadcrumb-row date
  // filters were retired), so dropping the breadcrumb row pulls the report
  // cards up under the nav. applyStickyClasses then skips the `sectionView`
  // top-spacing class (no #globalBreadcrumbs present), so there's no orphan gap.
  return !hiddenPaths.includes(this.$route.path) && !this.isMobile && !this.isReportRoute;
},
  },
  methods: {
    // Jump to the report host with the Layouts drawer already open. ?tools=1 is read by
    // CustomerInsights.vue on mount; the drawer itself lives there, so there is nothing to open here.
    openLayoutsDrawer() {
      this.$router.push({ path: "/reports/customer-insights", query: { tools: "1" } }).catch(function () { /* same route */ });
    },
    // ===== MOBILE EDGE TRAY (right-side toggles) =====
    // The Info/Guide/Setup pills and the language/font/theme toggles are
    // parked off-screen on phones behind a thin arrow handle; this toggles
    // them in/out. Bound to the handle's @click.
    toggleEdgeTray() {
      this.edgeTrayOpen = !this.edgeTrayOpen;
    },
    onTrayTouchStart(e) {
      if (!this.isMobile || !e.touches || !e.touches.length) {
        this._trayTouchX = null;
        return;
      }
      this._trayTouchX = e.touches[0].clientX;
      this._trayTouchY = e.touches[0].clientY;
    },
    onTrayTouchEnd(e) {
      if (
        this._trayTouchX == null ||
        !this.isMobile ||
        !e.changedTouches ||
        !e.changedTouches.length
      ) {
        this._trayTouchX = null;
        return;
      }
      var dx = e.changedTouches[0].clientX - this._trayTouchX;
      var dy = e.changedTouches[0].clientY - this._trayTouchY;
      var startX = this._trayTouchX;
      this._trayTouchX = null;
      // The Sales Insights favorites bar owns press-and-hold-then-drag on touch, and a ★ can sit inside
      // this right-edge zone — so a drag that ends near the edge would ALSO fling the tray open (or shut).
      // Its handler lives on `document`, which bubbles before this window listener, so __jcsFavDragAt is
      // always fresh by the time we get here. Mirror image of the __jcsTrayGestureAt handshake that
      // CustomerInsights.onSwipeEnd uses in the other direction.
      if (window.__jcsFavDragAt && Date.now() - window.__jcsFavDragAt < 400) return;
      // Ignore mostly-vertical gestures (page scroll) and small jitters.
      if (Math.abs(dx) < 45 || Math.abs(dx) <= Math.abs(dy)) return;
      var w = window.innerWidth || 400;
      if (dx < 0 && !this.edgeTrayOpen && startX > w - 36) {
        // Swipe leftward starting ON the peek strip → reveal the tray. History: this zone was 90px,
        // then narrowed to 28px so it stopped colliding with the report's own 36px right-edge swipe
        // (CustomerInsights openStory). That report swipe no longer exists — CustomerInsights
        // .onSwipeEnd now bails out of ANY left swipe starting within 56px of the right edge — so the
        // zone is back to 36px, matching the band the report used to claim. Every gesture that used to
        // open the Sales Insights drawer now reveals the toggle rail instead, and there is no dead
        // band between 36 and 28 where nothing at all happens.
        this.edgeTrayOpen = true;
        window.__jcsTrayGestureAt = Date.now();
      } else if (dx > 0 && this.edgeTrayOpen) {
        // Swipe rightward anywhere while open → park it again. Deliberately does NOT stamp
        // __jcsTrayGestureAt: the Insights drawer is reached by TAPPING the INSIGHTS pill in this very
        // tray, so edgeTrayOpen is still true behind the open drawer. Stamping here made the first
        // swipe-right park only the (already CSS-hidden) tray while the report's guard swallowed the
        // drawer close, forcing a second swipe. Both closes are the same "put it away" intent.
        this.edgeTrayOpen = false;
      }
    },
    // ===== GLOBAL DEBUG OVERLAY =====
    // Toggled from Account Setup -> Event Logs & History. buildDebugText() is
    // the single TEMPLATE to expand as diagnostics grow — it runs on every
    // route, so anything added there is available app-wide with no per-page
    // wiring.
    onDebugOverlayToggle(on) {
      this.debugOverlay = !!on;
      try {
        localStorage.setItem("jcs_debugOverlay", on ? "1" : "0");
      } catch (e) {
        /* ignore */
      }
      if (this.debugOverlay) {
        this.startDebugTimer();
        this.refreshDebugOverlay();
      } else {
        this.stopDebugTimer();
        this.debugText = "";
        this.removeDebugPreview();
        // Tear down the live CSS color editor's temp <style> + element picker.
        this.revertCssEdit();
        this._endCssPick();
      }
    },
    startDebugTimer() {
      this.stopDebugTimer();
      // Alignment settles asynchronously (route load, data fetch, breadcrumb),
      // so poll while the overlay is on instead of relying on a single align.
      this._debugTimer = setInterval(this.refreshDebugOverlay, 700);
    },
    stopDebugTimer() {
      if (this._debugTimer) {
        clearInterval(this._debugTimer);
        this._debugTimer = null;
      }
    },
    refreshDebugOverlay() {
      if (!this.debugOverlay) return;
      try {
        var sig = this.captureLayoutSignature();
        this.recordPageSignature(sig);
        this.debugRoutes = this.buildRouteList();
        this.debugTheme =
          "THEME card=" + sig.cardBg + "  cart=" + sig.cartBg +
          "  top5=" + sig.top5Bg;
        this.debugText = this.buildDebugText(sig);
        this.debugPrompt = this.buildPastePrompt(sig);
      } catch (e) {
        this.debugText = "debug error: " + (e && e.message);
      }
    },
    // The debug TEMPLATE — expand this as layout/alignment issues come up. It
    // feeds the global overlay on EVERY page AND records a per-route layout
    // "signature" so the overlay can COMPARE the current page against every
    // other page visited (with the overlay on) and flag formatting
    // differences — e.g. "content sits at top 0 while 8 other pages sit at
    // ~245", which is exactly how the /account/information header-clearance
    // bug surfaced.
    buildDebugText(sig) {
      var R = function (n) {
        return n == null || isNaN(n) ? null : Math.round(n);
      };
      if (!sig) {
        sig = this.captureLayoutSignature();
        this.recordPageSignature(sig);
      }
      var lines = [];
      lines.push(
        "DEBUG  route=" + sig.route + "  scrollY=" + R(window.scrollY || 0) +
          "  wide=" + this.viewportWide
      );
      lines.push(
        "bodyPadTop=" + sig.bodyPadTop + "  app.top=" + sig.appTop +
          "  routeView.top=" + sig.routeViewTop
      );
      lines.push(
        "NAV #main-nav h=" + sig.navH + "  stickyHeader h=" + sig.stickyH +
          " (should be stable; if the band 'heightens' on resize, h grows here)"
      );
      if (sig.navDbg) lines.push("NAVDBG " + sig.navDbg);
      var _navStuck = this._navReparentStuck();
      if (_navStuck.length)
        lines.push(
          "⚠ responsive.js REPARENT STUCK: " + _navStuck.join(", ") +
            " inside #main-nav at desktop (should be in #header-wrapper). Causes a tall " +
            "nav band and, if #header-account, its dropdown opens UNDER the search. FIX " +
            "in src/assets/js/responsive.js — desktop reparent anchors that EXIST: " +
            "#logo→'#header-wrapper .logo-cont', form#search→'#header-search', " +
            "#header-account→'#header-wrapper .header-utility-row' (NOT the legacy " +
            ".hi-phone/#hc-items, which don't exist in the markup). Keep jcsHealReparent's " +
            "self-heal. Full recipe in the 'navreparent' + 'zindex' topics below."
        );
      var a = this._lastAlignVals;
      if (a) {
        lines.push(
          "align: docTop=" + a.docTop + " topVal=" + a.topVal +
            " styleTop=" + a.styleTop + " sticky=" + a.sticky +
            " scrollY=" + a.scrollY
        );
      }
      lines.push(
        "CARD " + sig.cardSel + " top=" + sig.contentTop + " h=" + sig.cardH +
          " pos=" + sig.cardPos + " mt=" + sig.cardMarginTop +
          " pt=" + sig.cardPaddingTop
      );
      var diff =
        sig.stackTop != null && sig.contentTop != null
          ? sig.stackTop - sig.contentTop
          : null;
      lines.push(
        "STACK top=" + sig.stackTop + "  cart-vs-card=" +
          (diff == null ? "?" : diff + "px")
      );
      if (sig.cardClasses) lines.push("classes=[" + sig.cardClasses + "]");
      var baseRoute = this.debugBaseline || this._bestBaseline(sig);
      lines.push("---- COMPARE vs " + (baseRoute || "(none captured)") + " ----");
      var cmp = this.buildLayoutComparison(sig);
      if (cmp.length) {
        lines = lines.concat(cmp);
      } else if (!baseRoute) {
        lines.push("(visit other pages, or check one below, to compare)");
      } else {
        lines.push("OK - matches " + baseRoute + " on the checked metrics");
      }
      if (this.debugTopic) {
        var ftopic = this._topicByKey(this.debugTopic);
        if (ftopic)
          lines.push(
            "FOCUS: " + ftopic.label + " — guidance added to the copy-prompt"
          );
      }
      if (this.debugCssFile) {
        var fcss0 = this._cssFileByKey(this.debugCssFile);
        if (fcss0)
          lines.push("FILE: " + fcss0.label + " (guidance added to the prompt)");
      }
      return lines.join("\n");
    },
    // Snapshot the current page's key layout metrics: the first VISIBLE
    // "content card" (position/box) plus the header-clearance context. Generic
    // across pages — tries known top-card selectors; first rendered (>40px
    // tall) match wins.
    captureLayoutSignature() {
      var R = function (n) {
        return n == null || isNaN(n) ? null : Math.round(n);
      };
      var bodyCs = window.getComputedStyle(document.body);
      var app = document.getElementById("app");
      var rv = document.getElementById("routeView");
      var stack = document.querySelector(".floating-widget-stack");
      var cardSels = [
        "#acctInsights .insights-container",
        "#cartTop > .container",
        "#odTop > .container",
        ".jcs-portal-card > .container",
        "#ao-table",
        "#searchPart",
        ".create-order-form",
        ".create-rfq-form",
        "#account-content .content",
        "#infoCenter > .container",
        "#routeView .container",
      ];
      var card = null;
      var cardSel = "(none)";
      for (var i = 0; i < cardSels.length && !card; i++) {
        var els = document.querySelectorAll(cardSels[i]);
        for (var j = 0; j < els.length; j++) {
          var e = els[j];
          if (
            e.getClientRects().length &&
            e.getBoundingClientRect().height > 40
          ) {
            card = e;
            cardSel = cardSels[i];
            break;
          }
        }
      }
      var cr = card ? card.getBoundingClientRect() : null;
      var ccs = card ? window.getComputedStyle(card) : null;
      return {
        route: (this.$route && this.$route.path) || "",
        theme: document.documentElement.classList.contains("dark-mode")
          ? "dark"
          : document.documentElement.classList.contains("mid-mode")
          ? "mid"
          : "light",
        bodyPadTop: R(parseFloat(bodyCs.paddingTop)),
        appTop: app ? R(app.getBoundingClientRect().top) : null,
        routeViewTop: rv ? R(rv.getBoundingClientRect().top) : null,
        // Nav bar (#main-nav, the brushed-steel menu) + the whole sticky header
        // heights. The nav background is background-size:220px 100% (fills 100%
        // of the nav height), so if the nav reflows TALLER on a resize the band
        // looks "heightened" and body padding-top shifts everything below. These
        // should be STABLE across pages/states; a diff vs a refresh baseline
        // pinpoints the heightening.
        navH: (function () {
          var n = document.getElementById("main-nav");
          return n ? R(n.getBoundingClientRect().height) : null;
        })(),
        stickyH: (function () {
          var s = document.querySelector(".stickyHeader");
          return s ? R(s.getBoundingClientRect().height) : null;
        })(),
        // Breakdown of WHAT inside #main-nav is making it grow (the +27px the
        // flex/collapse fix didn't catch): the navbar's own flex-wrap /
        // align-items / padding, plus each child's display + height. Whichever
        // number jumps between the before/after capture IS the culprit.
        navDbg: (function () {
          var n = document.getElementById("main-nav");
          if (!n) return null;
          // The .container is ~26px taller than its 41px menu child — dump its
          // OWN box (display/padding/min-height/align/wrap), its ::before/::after
          // pseudo heights, and the collapse's margin. Whichever of these is ~26
          // (or grows between captures) IS the 27px culprit.
          var cont = n.querySelector(".container") || n;
          var c = window.getComputedStyle(cont);
          var ncs = window.getComputedStyle(n);
          var H = function (el) {
            return el ? Math.round(el.getBoundingClientRect().height) : "-";
          };
          var kids = Array.prototype.slice
            .call(cont.children)
            .map(function (k) {
              var label = k.id
                ? "#" + k.id
                : ((k.className || "").toString().trim().split(" ")[0] ||
                    k.tagName.toLowerCase());
              var ks = window.getComputedStyle(k);
              return (
                label + ":" +
                Math.round(k.getBoundingClientRect().height) + ":" +
                ks.display + ":" + ks.position
              );
            })
            .join(", ");
          return (
            "navbar[h=" + H(n) + " minH=" + ncs.minHeight + " ai=" +
            ncs.alignItems + " pad=" + ncs.paddingTop + "/" + ncs.paddingBottom +
            "] cont[h=" + H(cont) + " self=" + c.alignSelf + " minH=" +
            c.minHeight + " ai=" + c.alignItems + " fw=" + c.flexWrap +
            "] kids(h:disp:pos)=[" + kids + "]"
          );
        })(),
        cardSel: cardSel,
        contentTop: cr ? R(cr.top) : null,
        cardH: cr ? R(cr.height) : null,
        cardPos: ccs ? ccs.position : null,
        cardMarginTop: ccs ? R(parseFloat(ccs.marginTop)) : null,
        cardPaddingTop: ccs ? R(parseFloat(ccs.paddingTop)) : null,
        cardClasses: card
          ? (card.id ? "#" + card.id + " " : "") + (card.className || "")
          : "",
        stackTop:
          stack && stack.getClientRects().length
            ? R(stack.getBoundingClientRect().top)
            : null,
        // Theme backgrounds (the card incl ::before, where brand-stripe pages
        // paint their surface) — feeds the THEME readout + the color diff.
        cardBg: this._cardBg(card),
        cartBg: this._bgOf(".floating-widget-stack .staging-tray-wrapper"),
        top5Bg: this._bgOf(".floating-widget-stack .top-parts-card"),
        bodyBg: this._bgOf("body"),
        stackBg: this._bgOf(".floating-widget-stack"),
      };
    },
    // Effective card background: the element's own bg, or its ::before bg when
    // the element itself is transparent (brand-stripe cards paint the ::before).
    _cardBg(el) {
      if (!el) return "—";
      try {
        var cs = window.getComputedStyle(el);
        var bg = cs.backgroundColor;
        if (!bg || bg === "rgba(0, 0, 0, 0)" || bg === "transparent") {
          var bef = window.getComputedStyle(el, "::before");
          if (
            bef &&
            bef.backgroundColor &&
            bef.backgroundColor !== "rgba(0, 0, 0, 0)"
          )
            return bef.backgroundColor + "(::before)";
        }
        return bg;
      } catch (e) {
        return "?";
      }
    },
    // Persist per-route signatures (localStorage) so the overlay can compare
    // across pages within and across sessions. Latest visit per route wins.
    recordPageSignature(sig) {
      if (!this._pageSigs) {
        try {
          this._pageSigs =
            JSON.parse(localStorage.getItem("jcs_debugPageSigs") || "{}") || {};
        } catch (e) {
          this._pageSigs = {};
        }
      }
      if (sig && sig.route && sig.contentTop != null) {
        this._pageSigs[sig.route] = sig;
        try {
          localStorage.setItem(
            "jcs_debugPageSigs",
            JSON.stringify(this._pageSigs)
          );
        } catch (e) {
          /* ignore quota */
        }
      }
    },
    // Most common value in a list, bucketed to 5px — the "expected" norm.
    _modeOf(arr) {
      var buckets = {};
      var best = null;
      var bestN = 0;
      arr.forEach(function (v) {
        var b = Math.round(v / 5) * 5;
        buckets[b] = (buckets[b] || 0) + 1;
        if (buckets[b] > bestN) {
          bestN = buckets[b];
          best = b;
        }
      });
      return best;
    },
    // Compare the current signature against the others and flag differences
    // that read as formatting bugs: content not clearing the header, the cart
    // off its card, an off body padding, or an unusual card position.
    buildLayoutComparison(cur) {
      var sigs = this._pageSigs || {};
      var self = this;
      var baseRoute = this.debugBaseline || this._bestBaseline(cur);
      var others;
      if (baseRoute && sigs[baseRoute] && baseRoute !== cur.route) {
        // Diff against the single page the admin checked (or the default
        // reference), so differences are page-vs-page, not vs a vague majority.
        others = [sigs[baseRoute]];
      } else {
        others = Object.keys(sigs)
          .filter(function (r) {
            return r !== cur.route;
          })
          .map(function (r) {
            return sigs[r];
          });
      }
      var out = [];
      if (!others.length) return out;
      var pick = function (key) {
        return others
          .map(function (s) {
            return s[key];
          })
          .filter(function (v) {
            return v != null;
          });
      };
      // Header clearance is judged by bodyPadTop (below) — the reliable signal.
      // NOT by the card's measured top, which varies with per-page content and
      // tab state (e.g. a tall nested #ao-table read top=134) and produced false
      // "not clearing the header" flags on pages that were actually fine.
      // cart vs its own card.
      if (cur.stackTop != null && cur.contentTop != null) {
        var d = cur.stackTop - cur.contentTop;
        if (Math.abs(d) > 12)
          out.push("! cart is " + d + "px off this card (want ~0)");
      }
      // body padding-top.
      var pads = pick("bodyPadTop");
      if (pads.length && cur.bodyPadTop != null) {
        var normPad = self._modeOf(pads);
        if (normPad != null && Math.abs(cur.bodyPadTop - normPad) > 10)
          out.push(
            "! body padding-top=" + cur.bodyPadTop + "; others ~" + normPad
          );
      }
      // card position (most pages static/relative).
      var positions = {};
      others.forEach(function (s) {
        if (s.cardPos) positions[s.cardPos] = (positions[s.cardPos] || 0) + 1;
      });
      var normPos = Object.keys(positions).sort(function (x, y) {
        return positions[y] - positions[x];
      })[0];
      // Position only matters if it actually breaks cart alignment. A
      // jcs-portal-card is `relative` (it NEEDS that for its ::before stripe)
      // while #ao-table pages are `static` — both are correct. Only flag a
      // position difference when the cart is NOT flush, so we stop crying wolf.
      var posGap =
        cur.stackTop != null && cur.contentTop != null
          ? Math.abs(cur.stackTop - cur.contentTop)
          : 0;
      if (normPos && cur.cardPos && cur.cardPos !== normPos && posGap > 12)
        out.push(
          "! card position:" + cur.cardPos + "; others:" + normPos +
            " (cart off by " + posGap + "px)"
        );
      // THE color standard: in dark/mid mode a content card should read the SAME
      // surface as the floating cart + Top-Parts (#1f242b in dark). Same-page,
      // same-theme — no baseline needed — so it's the reliable color signal (NOT
      // "match some other page's card", which legitimately varies). Surfaced here
      // so the Copy fix-prompt SUGGESTS it automatically.
      if (this._cardOffCart(cur)) {
        var want = this._cardSurfaceFor(cur.theme);
        out.push(
          "! card bg " + cur.cardBg + " should be " + want.hex + " (" + want.rgb +
            ") — the STANDARD " + cur.theme +
            "-mode card surface, matching the cart/Top-Parts" +
            ' (use the "card -> cart gray" topic)'
        );
      }
      // Widget consistency across pages: the cart / Top-Parts should be the same
      // gray on every page. (Card-vs-baseline-CARD is intentionally NOT compared
      // — the standard is the cart above, not another page's card, which can be
      // a legitimately different gray until every page is unified.)
      if (others.length === 1) {
        var b = others[0];
        var rgb = function (v) {
          return v && v.indexOf("rgb") === 0;
        };
        if (b.theme && cur.theme && b.theme === cur.theme) {
          // Only compare when BOTH sides captured a real rgb — skips the
          // confusing "vs baseline —" when the baseline never measured a cart.
          if (rgb(cur.cartBg) && rgb(b.cartBg) && cur.cartBg !== b.cartBg)
            out.push("! cart color " + cur.cartBg + " vs baseline " + b.cartBg);
          if (rgb(cur.top5Bg) && rgb(b.top5Bg) && cur.top5Bg !== b.top5Bg)
            out.push(
              "! Top-Parts color " + cur.top5Bg + " vs baseline " + b.top5Bg
            );
        }
      }
      // Nav band + sticky-header heights — should be IDENTICAL on every page and
      // every state. A diff = the brushed-steel nav reflowed taller/shorter (the
      // "heightening" seen on resize); its bg is background-size:220px 100% so it
      // fills 100% of the nav height, and body padding-top tracks the header.
      if (others.length === 1) {
        var nb = others[0];
        if (
          cur.navH != null &&
          nb.navH != null &&
          Math.abs(cur.navH - nb.navH) > 2
        )
          out.push(
            "! nav #main-nav h=" + cur.navH + " vs baseline " + nb.navH +
              " — nav band reflowed (the heightening)"
          );
        if (
          cur.stickyH != null &&
          nb.stickyH != null &&
          Math.abs(cur.stickyH - nb.stickyH) > 2
        )
          out.push(
            "! sticky header h=" + cur.stickyH + " vs baseline " + nb.stickyH
          );
      }
      // responsive.js reparent check — header elements stuck inside #main-nav at
      // desktop are THE usual cause of a tall/"heightened" nav band (and the
      // "flex container taller than its tallest child" paradox). Flag loudly so
      // the Copy fix-prompt names the real culprit (JS DOM-reparenting), not CSS.
      var navStuck = this._navReparentStuck();
      if (navStuck.length)
        out.push(
          "! responsive.js REPARENT STUCK: " + navStuck.join(", ") +
            " inside #main-nav at desktop (should be in #header-wrapper) — inflates the " +
            "nav band; a stuck #header-account also opens its dropdown UNDER the search. " +
            "FIX (src/assets/js/responsive.js): point each desktop reparent at a REAL " +
            "anchor — #logo→'#header-wrapper .logo-cont', form#search→'#header-search', " +
            "#header-account→'#header-wrapper .header-utility-row'; .hi-phone/#hc-items " +
            "DON'T EXIST. Plus steele #main-nav{z-index:1001 !important} (app.css z-index:9 " +
            "beats steele's 1001 and sits under the search's 999). See navreparent/zindex topics."
        );
      return out;
    },
    // Default comparison baseline when the admin hasn't checked one: the
    // known-good reference page (/account/information) if captured, else any
    // other captured route.
    _bestBaseline(cur) {
      var sigs = this._pageSigs || {};
      if (sigs["/account/information"] && cur.route !== "/account/information")
        return "/account/information";
      var keys = Object.keys(sigs).filter(function (r) {
        return r !== cur.route;
      });
      return keys.length ? keys[0] : "";
    },
    // The full list of site routes (from the router) for the overlay's
    // compare-against checklist, each flagged whether its layout signature has
    // been captured yet. Captured routes can be compared without browsing to
    // them; uncaptured ones need one visit to record a signature.
    buildRouteList() {
      var sigs = this._pageSigs || {};
      var out = [];
      var seen = {};
      try {
        var all =
          (this.$router &&
            this.$router.getRoutes &&
            this.$router.getRoutes()) ||
          (this.$router &&
            this.$router.options &&
            this.$router.options.routes) ||
          [];
        all.forEach(function (r) {
          var p = r && r.path;
          if (!p || p.indexOf(":") !== -1 || p.indexOf("*") !== -1) return;
          if (p.indexOf("/admin") === 0) return;
          if (seen[p]) return;
          seen[p] = 1;
          out.push({ path: p, captured: !!sigs[p] });
        });
      } catch (e) {
        /* ignore — fall back to whatever is captured */
      }
      // Include captured routes the router list didn't surface (dynamic paths).
      Object.keys(sigs).forEach(function (p) {
        if (!seen[p]) {
          seen[p] = 1;
          out.push({ path: p, captured: true });
        }
      });
      out.sort(function (a, b) {
        return a.path < b.path ? -1 : a.path > b.path ? 1 : 0;
      });
      return out;
    },
    // Set (or clear, if re-clicked) the comparison baseline and persist it.
    setDebugBaseline(path) {
      this.debugBaseline = this.debugBaseline === path ? "" : path;
      try {
        localStorage.setItem("jcs_debugBaseline", this.debugBaseline);
      } catch (e) {
        /* ignore */
      }
      this.refreshDebugOverlay();
    },
    // Build a ready-to-paste chat prompt that fully specs the current page's
    // problem against the baseline — so an admin can hand it to the AI with no
    // back-and-forth. This is the "remove the guessing" payload.
    buildPastePrompt(cur) {
      var sigs = this._pageSigs || {};
      var baseRoute = this.debugBaseline || this._bestBaseline(cur);
      var base = sigs[baseRoute];
      // Guard: an already-correct page (recognized card + cleared header + flush
      // cart) needs no recipe — say so instead of handing over a bogus fix.
      var dpx =
        cur.stackTop != null && cur.contentTop != null
          ? cur.stackTop - cur.contentTop
          : null;
      var recog =
        cur.cardSel &&
        cur.cardSel !== "(none)" &&
        cur.cardSel.indexOf("#routeView") !== 0;
      if (
        !this.debugTopic &&
        dpx != null &&
        Math.abs(dpx) <= 12 &&
        cur.bodyPadTop != null &&
        cur.bodyPadTop >= 100 &&
        recog &&
        !this._cardOffCart(cur)
      ) {
        return (
          cur.route + " already looks correct: card=" + cur.cardSel +
          " (a recognized portal card), header cleared (bodyPadTop=" +
          cur.bodyPadTop + "), cart flush (cart-vs-card=" + dpx +
          "px). No jcs-portal-card fix needed. For a SPECIFIC change (e.g. " +
          "match the card color to the cart), pick it from the \"focus a known " +
          "uniformity issue\" dropdown and copy again. (Any COLOR diff flagged " +
          "is only a theme mismatch with the baseline — compare in the same theme.)"
        );
      }
      var diffs = this.buildLayoutComparison(cur);
      var fmt = function (s) {
        if (!s) return "(not captured — open that page once with the overlay on)";
        var d =
          s.stackTop != null && s.contentTop != null
            ? s.stackTop - s.contentTop + "px"
            : "?";
        return (
          "card=" + s.cardSel + " top=" + s.contentTop + " h=" + s.cardH +
          " pos=" + s.cardPos + " mt=" + s.cardMarginTop + " pt=" +
          s.cardPaddingTop + "; bodyPadTop=" + s.bodyPadTop +
          "; navH=" + s.navH + "; stickyH=" + s.stickyH +
          "; cart-vs-card=" + d + "; cardBg=" + s.cardBg +
          "; classes=[" + s.cardClasses + "]"
        );
      };
      var L = [];
      L.push(
        "Fix the layout/formatting of the page at " + cur.route +
          (base ? " so it matches " + baseRoute + "." : ".")
      );
      L.push("");
      if (diffs.length) {
        L.push("Differences the debug overlay detected:");
        diffs.forEach(function (d) {
          L.push("- " + d.replace(/^!\s*/, ""));
        });
      } else {
        L.push(
          "The page looks wrong but the overlay didn't auto-flag a metric. Measurements below — compare them and tell me what's off, or fix to match the reference."
        );
      }
      L.push("");
      L.push("Current page " + cur.route + ":");
      L.push("  " + fmt(cur));
      if (base) {
        L.push("Reference page " + baseRoute + ":");
        L.push("  " + fmt(base));
      }
      var ftopic = this._topicByKey(this.debugTopic);
      if (ftopic && ftopic.guide) {
        L.push("");
        L.push("Likely area — " + ftopic.label + ":");
        L.push("  " + ftopic.guide);
      }
      var fcss = this._cssFileByKey(this.debugCssFile);
      if (fcss && fcss.guide) {
        L.push("");
        L.push("Target file — " + fcss.label + ":");
        L.push("  " + fcss.guide);
      }
      L.push("");
      if (ftopic && ftopic.key) {
        L.push(
          'Please apply the fix described under "' + ftopic.label + '" to ' +
            cur.route + ". This page's content card selector is " + cur.cardSel +
            " — use a PAGE-SCOPED selector so other pages sharing that id aren't " +
            "affected. Then build and confirm via the THEME readout above."
        );
      } else if (
        cur.cardSel &&
        cur.cardSel !== "(none)" &&
        cur.cardSel.indexOf("#routeView") !== 0 &&
        cur.stackTop != null &&
        cur.contentTop != null &&
        Math.abs(cur.stackTop - cur.contentTop) <= 12 &&
        cur.bodyPadTop != null &&
        cur.bodyPadTop >= 100
      ) {
        // The page ALREADY has a recognized card, a flush cart, and a cleared
        // header — so the generic "add jcs-portal-card + alignWidgetStack +
        // brand-stripe" recipe is a TRAP (it's already there). Tell the AI to
        // fix ONLY the listed diffs (usually just the card color), not re-run the
        // plumbing or change position. This is the consensus from the
        // /account/information test, baked in for future chats.
        L.push(
          cur.route + " ALREADY has a recognized content card (" + cur.cardSel +
            "), the floating cart is flush (cart-vs-card ~0), and the header is " +
            "cleared. So DO NOT re-add a wrapper class, touch App.vue " +
            "alignWidgetStack, re-run the brand-stripe block, or change position " +
            "(relative is correct for a jcs-portal-card's ::before stripe). Fix " +
            "ONLY the differences listed above. For a card-COLOR diff, set " +
            cur.cardSel + "'s background (and its ::before if it's a " +
            "jcs-portal-card) to the standard surface (#1f242b dark / #b9bcc0 mid) " +
            "in darkmode.css / mid-mode.css, MATCHING the id-count of whatever base " +
            "rule currently paints it (e.g. steele's 2-id #acctOrders #ao-table)."
        );
      } else {
        L.push(
          "Please make " + cur.route +
            " use the same content-card + floating-cart-alignment treatment as " +
            (baseRoute || "the working portal pages") +
            ": give its top wrapper a distinguishing class, register that selector in App.vue alignWidgetStack's stripeCard override AND in steele-consistency-fix.css's brand-stripe block (white card + 3px red top stripe + widget reflow), and confirm cart-vs-card is ~0."
        );
      }
      return L.join("\n");
    },
    copyDebugPrompt() {
      var self = this;
      var text = this.debugPrompt || "";
      var done = function () {
        self.debugCopyLabel = "Copied!";
        setTimeout(function () {
          self.debugCopyLabel = "Copy fix-prompt";
        }, 1500);
      };
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done, function () {
            self._fallbackCopy(text);
            done();
          });
        } else {
          this._fallbackCopy(text);
          done();
        }
      } catch (e) {
        this._fallbackCopy(text);
        done();
      }
    },
    _fallbackCopy(text) {
      try {
        var ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (e) {
        /* ignore */
      }
    },
    // TEMP (mobile-header debug): build a readout of the live header's structure
    // + computed flex props + geometry, so the actual rendered layout can be
    // inspected instead of guessed. doc/win let us target the MAIN document or a
    // same-origin preview iframe (a true 390px viewport, where the mobile
    // @media is actually active). Remove with the build tag.
    _headerDiagText(doc, win, tag) {
      function describe(sel) {
        var el = doc.querySelector(sel);
        if (!el) return sel + " → (not found)";
        var cs = win.getComputedStyle(el);
        var r = el.getBoundingClientRect();
        var p = el.parentElement;
        return (
          sel +
          " → parent=" +
          (p ? p.tagName.toLowerCase() + "." + (p.className || "").toString().trim().replace(/\s+/g, ".") : "?") +
          " | rect[top=" + Math.round(r.top) + " left=" + Math.round(r.left) +
          " w=" + Math.round(r.width) + " h=" + Math.round(r.height) + "]" +
          " | order=" + cs.order + " flex=" + cs.flex + " display=" + cs.display +
          " dir=" + cs.flexDirection + " wrap=" + cs.flexWrap +
          " align=" + cs.alignItems + " justify=" + cs.justifyContent + " pos=" + cs.position
        );
      }
      var lines = ["=== HEADER DIAG (" + (tag || "main") + ") vw=" + win.innerWidth + " ==="];
      [
        "#header-wrapper .row.align-items-start",
        "#header-wrapper .logo-cont",
        "#header-wrapper #logo",
        "#header-wrapper .contact-cont",
        "#header-wrapper .header-contact",
        "#header-wrapper .account-cont",
        "#header-wrapper .account-identity",
        "#header-wrapper .header-utility-row"
      ].forEach(function (s) {
        lines.push(describe(s));
      });
      // ICON SIZES (the ::before circles + the contact button) + the sticky-
      // header height that the overlays anchor to. Pinpoints "icons different
      // sizes" / "dropdowns open at the wrong spot".
      try {
        lines.push("--- icons + overlay anchor ---");
        var cbtn = doc.querySelector("#header-wrapper .header-contact-icon");
        if (cbtn) {
          var cr = cbtn.getBoundingClientRect();
          lines.push("contact button = " + Math.round(cr.width) + "x" + Math.round(cr.height));
        }
        var comp = doc.querySelector("#header-wrapper .company-selector");
        if (comp) {
          var cs1 = win.getComputedStyle(comp, "::before");
          lines.push("company ::before = " + cs1.width + "x" + cs1.height + " margin=" + cs1.margin);
        }
        var acct = doc.querySelector("#header-wrapper #header-account .nav-link");
        if (acct) {
          var cs2 = win.getComputedStyle(acct, "::before");
          lines.push("account ::before = " + cs2.width + "x" + cs2.height + " margin=" + cs2.margin + " top=" + cs2.top + " pos=" + cs2.position);
        }
        // Measure each header sub-component to locate any vertical gap that
        // inflates the sticky header.
        [".stickyHeader", ".jcs-mobile-cart-bar", "#header-wrapper", "#main-nav", "#main-nav .navbar-collapse", "#main-nav .navbar-nav"].forEach(function (s) {
          var el = doc.querySelector(s);
          if (el) {
            var r = el.getBoundingClientRect();
            var zs = win.getComputedStyle(el);
            lines.push(s + " = top " + Math.round(r.top) + " h " + Math.round(r.height) + " bottom " + Math.round(r.bottom) + " z=" + zs.zIndex + " pos=" + zs.position);
          }
        });
        lines.push("--jcs-sticky-h = " + (win.getComputedStyle(doc.documentElement).getPropertyValue("--jcs-sticky-h").trim() || "(unset)"));
        lines.push("--jcs-header-bottom = " + (win.getComputedStyle(doc.documentElement).getPropertyValue("--jcs-header-bottom").trim() || "(unset)"));
        // If the contact modal is OPEN, measure it (the build tag sits above
        // the backdrop so tapping it doesn't close the modal).
        var mcont = doc.querySelector(".hc-contact-modal");
        if (mcont) {
          var mccs = win.getComputedStyle(mcont);
          lines.push(".hc-contact-modal (.modal) = pos=" + mccs.position + " z=" + mccs.zIndex);
          var md = mcont.querySelector(".modal-dialog");
          if (md) {
            var mr = md.getBoundingClientRect();
            var mcs = win.getComputedStyle(md);
            lines.push("  .modal-dialog = top " + Math.round(mr.top) + " h " + Math.round(mr.height) + " | pos=" + mcs.position + " top=" + mcs.top + " z=" + mcs.zIndex);
          }
          // Ancestor chain (pos + z-index) — reveals which stacking context the
          // modal is trapped in (e.g. inside .stickyHeader z=9200 vs body).
          var chain = [];
          var n = mcont;
          while (n && n.tagName && n.tagName.toLowerCase() !== "html" && chain.length < 9) {
            var ns = win.getComputedStyle(n);
            var cls = (typeof n.className === "string" && n.className.trim()) ? "." + n.className.trim().split(/\s+/)[0] : "";
            chain.push(n.tagName.toLowerCase() + (n.id ? "#" + n.id : "") + cls + "[" + ns.position + ",z" + ns.zIndex + (ns.transform !== "none" ? ",tf" : "") + "]");
            n = n.parentElement;
          }
          lines.push("  modal ancestry: " + chain.join(" > "));
        } else {
          lines.push("contact modal: (not open — open it, then tap this tag)");
        }
      } catch (e) {
        lines.push("icon/sticky diag error: " + (e && e.message ? e.message : e));
      }
      var row = doc.querySelector("#header-wrapper .row.align-items-start");
      if (row) {
        lines.push("--- row children in DOM order ---");
        Array.prototype.forEach.call(row.children, function (c, i) {
          var cs = win.getComputedStyle(c);
          var r = c.getBoundingClientRect();
          lines.push(
            i + ": " + c.tagName.toLowerCase() + "." + (c.className || "").toString().trim().replace(/\s+/g, ".") +
            " order=" + cs.order + " top=" + Math.round(r.top) + " left=" + Math.round(r.left) +
            " w=" + Math.round(r.width) + " h=" + Math.round(r.height)
          );
        });
        var html = row.outerHTML.replace(/\s+/g, " ");
        if (html.length > 1800) html = html.slice(0, 1800) + "…";
        lines.push("--- row.outerHTML ---");
        lines.push(html);
      }
      return lines.join("\n");
    },
    _openDiag(text) {
      var self = this;
      this.headerDiag.text = text;
      this.headerDiag.copied = false;
      this.headerDiag.open = true;
      this.$nextTick(function () {
        var ta = self.$refs.diagTa;
        if (ta) {
          try {
            ta.focus();
            ta.select();
          } catch (e) {
            /* ignore */
          }
        }
      });
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).catch(function () {});
        }
      } catch (e) {
        /* ignore */
      }
    },
    // Build-tag tap → diagnostic for THIS window (whatever width it is).
    copyHeaderDiag() {
      this._openDiag(this._headerDiagText(document, window, "main"));
    },
    // Preview-bar button → diagnostic for the first mobile-preview iframe, which
    // renders at a true 390px viewport (so the mobile @media IS active). This is
    // the readout I actually need — captured at phone width, not desktop.
    copyHeaderDiagFromPreview() {
      var ifr = document.querySelector(".jcs-mp-iframe");
      if (!ifr) {
        this._openDiag("(no preview iframe found — turn ON the 📱 Mobile preview, wait for it to load, then tap this again)");
        return;
      }
      try {
        var d = ifr.contentDocument || (ifr.contentWindow && ifr.contentWindow.document);
        var w = ifr.contentWindow;
        if (!d || !w || !d.querySelector("#header-wrapper")) {
          this._openDiag("(preview iframe not ready — wait for it to finish loading, then tap again)");
          return;
        }
        this._openDiag(this._headerDiagText(d, w, "PREVIEW iframe"));
      } catch (e) {
        this._openDiag("(could not read preview iframe: " + (e && e.message ? e.message : e) + ")");
      }
    },
    // Copy the diagnostic text from the overlay's textarea to the clipboard.
    copyHeaderDiagText() {
      var self = this;
      var ta = this.$refs.diagTa;
      var text = this.headerDiag.text || "";
      if (ta) {
        try {
          ta.focus();
          ta.select();
          ta.setSelectionRange(0, text.length);
        } catch (e) {
          /* ignore */
        }
      }
      var done = function () {
        self.headerDiag.copied = true;
        setTimeout(function () {
          self.headerDiag.copied = false;
        }, 2000);
      };
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done, function () {
            try {
              document.execCommand("copy");
            } catch (e) {
              /* ignore */
            }
            done();
          });
        } else {
          try {
            document.execCommand("copy");
          } catch (e) {
            /* ignore */
          }
          done();
        }
      } catch (e) {
        done();
      }
    },
    // Walk every stylesheet and return the rules whose selector matches `el` AND
    // set any of `props` — each tagged with its @media (ON/off at the current
    // width), the !important flag, and the source file. This is the console
    // "why is this element sized/positioned like this?" inspector baked into the
    // overlay (the 🔍 button). CORS sheets are skipped. See also _whyTall usage.
    _matchedRules(el, props) {
      var hits = [];
      if (!el) return hits;
      var walk = function (list, ctx) {
        for (var i = 0; i < list.length; i++) {
          var r = list[i];
          if (r.type === 1 && r.selectorText) {
            var sel = null;
            var parts = r.selectorText.split(",");
            for (var p = 0; p < parts.length; p++) {
              try {
                if (el.matches(parts[p].trim())) {
                  sel = parts[p].trim();
                  break;
                }
              } catch (e) {
                /* unsupported selector (e.g. ::pseudo) */
              }
            }
            if (sel) {
              var setp = props.filter(function (k) {
                return r.style.getPropertyValue(k);
              });
              if (setp.length) {
                var f =
                  r.parentStyleSheet && r.parentStyleSheet.href
                    ? r.parentStyleSheet.href.split("/").pop().split("?")[0]
                    : "inline";
                hits.push(
                  "    [" + (ctx || "all") + "] " + sel + " { " +
                    setp
                      .map(function (k) {
                        return (
                          k + ":" + r.style.getPropertyValue(k) +
                          (r.style.getPropertyPriority(k) ? "!" : "")
                        );
                      })
                      .join("; ") + " } <" + f + ">"
                );
              }
            }
          } else if (r.type === 4 && r.cssRules) {
            var on = false;
            try {
              on = window.matchMedia(r.conditionText).matches;
            } catch (e) {
              /* ignore */
            }
            walk(r.cssRules, "@media(" + r.conditionText + ")[" + (on ? "ON" : "off") + "]");
          } else if (r.type === 12 && r.cssRules) {
            walk(r.cssRules, ctx);
          }
        }
      };
      for (var s = 0; s < document.styleSheets.length; s++) {
        try {
          var cr = document.styleSheets[s].cssRules;
          if (cr) walk(cr, "");
        } catch (e) {
          /* CORS sheet — skip */
        }
      }
      return hits;
    },
    // 🔍 button: copy the size/position rules for the nav-height suspects (+ this
    // page's content card) to the clipboard — so a tall-band / stuck-height bug
    // can be diagnosed from the overlay at ANY width, including the dev-tools-
    // CLOSED (>=992) stuck state a docked-console script can't reach.
    // 🔊 Tour audio self-test — walks the neural-voice pipeline (endpoint →
    // decode → playback) and reports each stage in the overlay (+ copies it).
    // Runs inside this click, so resuming the AudioContext is gesture-allowed;
    // if playback works you'll HEAR a test voice.
    async runAudioTest() {
      var self = this;
      var L = [];
      function log(s) { L.push(s); self.audioTestResult = L.join("\n"); }
      log("— Tour audio self-test —");
      var AC = window.AudioContext || window.webkitAudioContext;
      log("AudioContext supported: " + !!AC);
      if (!AC) { log("→ this browser has no Web Audio; neural voice can't play here."); return; }
      var ctx;
      try { ctx = new AC(); } catch (e) { log("ctx create FAILED: " + (e && e.message || e)); return; }
      log("ctx.state initial: " + ctx.state);
      try { await ctx.resume(); } catch (e) {}
      log("ctx.state after resume: " + ctx.state);

      var blob = null;
      try {
        var r = await httpClient.post("/guidetour/tts/preview",
          { text: "This is an audio test for the Steele service portal.", voice: "en-US-AriaNeural" },
          { responseType: "blob" });
        log("tts/preview HTTP: " + (r && r.status));
        blob = r && r.data;
        log("response body: " + (blob ? (blob.type + ", " + blob.size + " bytes") : "none"));
      } catch (e) {
        var er = e && e.response;
        log("tts/preview ERROR: HTTP " + (er ? er.status : "?") + " — " + (e && e.message || e));
        log("→ the voice endpoint failed (backend not deployed, or Azure key/region not read).");
        return;
      }
      if (!blob || !blob.size) { log("→ empty response: Azure not configured (AzureSpeech__Key/__Region) or 204."); return; }
      if (String(blob.type).indexOf("audio") !== 0) { log("→ response is NOT audio (likely a JSON/error body)."); return; }

      var ab;
      try { ab = await blob.arrayBuffer(); log("arrayBuffer: " + ab.byteLength + " bytes"); }
      catch (e) { log("arrayBuffer FAILED: " + (e && e.message || e)); return; }

      var buf;
      try { buf = await ctx.decodeAudioData(ab.slice(0)); log("decodeAudioData: OK — " + buf.duration.toFixed(2) + "s"); }
      catch (e) { log("decodeAudioData FAILED: " + (e && e.message || e) + "  ← THIS is why there's no sound"); return; }

      try {
        var src = ctx.createBufferSource();
        src.buffer = buf; src.connect(ctx.destination); src.start(0);
        log("playback: started — you should HEAR a voice now. ctx.state=" + ctx.state);
      } catch (e) { log("playback FAILED: " + (e && e.message || e)); }

      // Also test the SAVED narration voice (what the tour actually uses). Aria
      // above proves the endpoint/decode/playback all work; if THIS voice returns no
      // audio, it isn't available on this Azure resource — that's why the tour is
      // silent (the tour now auto-falls back to Aria, but the admin should pick a
      // supported voice in the 🔊 list).
      var savedVoice = "";
      try { savedVoice = localStorage.getItem("jcs_tour_voice") || ""; } catch (e) {}
      if (savedVoice && savedVoice.toLowerCase() !== "en-us-arianeural") {
        log("— saved tour voice: " + savedVoice + " —");
        try {
          var r2 = await httpClient.post("/guidetour/tts/preview",
            { text: "Testing the selected narration voice.", voice: savedVoice },
            { responseType: "blob" });
          var b2 = r2 && r2.data;
          log("saved-voice HTTP: " + (r2 && r2.status) + " · body: " + (b2 ? (b2.type + ", " + b2.size + " bytes") : "none"));
          if (b2 && b2.size && String(b2.type).indexOf("audio") === 0) {
            var ab2 = await b2.arrayBuffer();
            var buf2 = await ctx.decodeAudioData(ab2.slice(0));
            var s2 = ctx.createBufferSource(); s2.buffer = buf2; s2.connect(ctx.destination);
            s2.start(ctx.currentTime + (buf ? buf.duration + 0.4 : 0.4));   // after the Aria clip so they don't overlap
            log("saved-voice playback: OK — you'll hear it right after the Aria clip. This voice works.");
          } else {
            log("→ saved voice “" + savedVoice + "” produced NO audio on this Azure resource — THIS is why the tour was silent. The tour now auto-falls back to Aria; pick a supported voice in the 🔊 list to change it.");
          }
        } catch (e2) {
          var er2 = e2 && e2.response;
          log("saved-voice ERROR: HTTP " + (er2 ? er2.status : "?") + " — " + (e2 && e2.message || e2) + "  ← saved voice unavailable; tour falls back to Aria.");
        }
      } else {
        log("— saved tour voice = Aria (or unset) — same as the test above —");
      }
      try { if (navigator.clipboard) navigator.clipboard.writeText(self.audioTestResult); } catch (e) {}
      this.buildTagCopied = true;
      setTimeout(function () { self.buildTagCopied = false; }, 8000);
    },
    copyNavRules() {
      var self = this;
      var PROPS = [
        "height", "min-height", "max-height", "position", "display",
        "padding", "padding-top", "padding-bottom", "align-items",
        "align-self", "flex-direction", "flex-wrap",
        "z-index", "transform", "isolation", "overflow",
      ];
      var cur = this.captureLayoutSignature();
      var specs = [
        ["#main-nav", document.getElementById("main-nav")],
        ["#main-nav > .container", document.querySelector("#main-nav > .container")],
        ["#header-account", document.getElementById("header-account")],
        // dropdown + the layers it can hide behind (z-index/stacking diagnosis).
        ["#header-account .dropdown-menu (My Account flyout)", document.querySelector("#header-account .dropdown-menu")],
        ["#logo", document.getElementById("logo")],
        ["#header-search", document.getElementById("header-search")],
        [".floating-widget-stack", document.querySelector(".floating-widget-stack")],
      ];
      if (cur.cardSel && cur.cardSel !== "(none)") {
        try {
          specs.push([cur.cardSel + " (card)", document.querySelector(cur.cardSel)]);
        } catch (e) {
          /* invalid selector */
        }
      }
      var out = [
        "SIZE/POSITION RULE INSPECTOR  (wide=" + window.innerWidth +
          ", >=992? " + (window.innerWidth >= 992) + ")  route=" + cur.route,
      ];
      specs.forEach(function (sp) {
        var el = sp[1];
        out.push("==== " + sp[0] + (el ? "" : "  (not found)") + " ====");
        if (!el) return;
        var cs = window.getComputedStyle(el);
        out.push(
          "  rect.h=" + Math.round(el.getBoundingClientRect().height) +
            " height=" + cs.height + " min-height=" + cs.minHeight +
            " max-height=" + cs.maxHeight + " position=" + cs.position +
            " display=" + cs.display + " pad=" + cs.paddingTop + "/" +
            cs.paddingBottom
        );
        if (el.getAttribute("style"))
          out.push("  INLINE: " + el.getAttribute("style"));
        var rules = self._matchedRules(el, PROPS);
        if (rules.length) {
          out.push("  RULES:");
          rules.forEach(function (h) {
            out.push(h);
          });
        } else {
          out.push("  (no matching size/position rules)");
        }
      });
      var txt = out.join("\n");
      try {
        console.log(txt);
      } catch (e) {
        /* ignore */
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(txt).then(
            function () {},
            function () {
              self._fallbackCopy(txt);
            }
          );
        } else {
          this._fallbackCopy(txt);
        }
      } catch (e) {
        this._fallbackCopy(txt);
      }
      // Brief confirmation on the COPY button label (shared, harmless).
      this.debugCopyLabel = "🔍 rules copied — paste in chat";
      setTimeout(function () {
        self.debugCopyLabel = "Copy fix-prompt";
      }, 2000);
    },
    toggleDebugFromOverlay() {
      this.onDebugOverlayToggle(false);
    },
    // Collapse/expand the overlay to just its title bar ("_" button).
    toggleDebugCollapsed() {
      this.debugCollapsed = !this.debugCollapsed;
      try {
        localStorage.setItem(
          "jcs_debugCollapsed",
          this.debugCollapsed ? "1" : "0"
        );
      } catch (e) {
        /* ignore */
      }
    },
    // Drag the overlay by its title bar (persists position in jcs_debugPos).
    // Drags that start on a button are ignored so Copy / × still click.
    startDebugDrag(e) {
      if (e.target && e.target.closest && e.target.closest("button")) return;
      var self = this;
      var startX = e.clientX;
      var startY = e.clientY;
      var origLeft = this.debugPos.left;
      var origTop = this.debugPos.top;
      var move = function (ev) {
        var nl = origLeft + (ev.clientX - startX);
        var nt = origTop + (ev.clientY - startY);
        nl = Math.max(0, Math.min(nl, (window.innerWidth || 1200) - 60));
        nt = Math.max(0, Math.min(nt, (window.innerHeight || 800) - 28));
        self.debugPos = { left: nl, top: nt };
      };
      var up = function () {
        document.removeEventListener("mousemove", move);
        document.removeEventListener("mouseup", up);
        try {
          localStorage.setItem("jcs_debugPos", JSON.stringify(self.debugPos));
        } catch (err) {
          /* ignore */
        }
      };
      document.addEventListener("mousemove", move);
      document.addEventListener("mouseup", up);
      e.preventDefault();
    },
    _topicByKey(key) {
      var list = this.debugTopics || [];
      for (var i = 0; i < list.length; i++) {
        if (list[i].key === key) return list[i];
      }
      return null;
    },
    _cssFileByKey(key) {
      var list = this.debugCssFiles || [];
      for (var i = 0; i < list.length; i++) {
        if (list[i].key === key) return list[i];
      }
      return null;
    },
    // Computed background-color of the first element matching `sel` — used by
    // the THEME readout so a card that's black instead of the theme gray is
    // visible at a glance.
    _bgOf(sel) {
      try {
        var el = document.querySelector(sel);
        if (!el) return "—";
        return window.getComputedStyle(el).backgroundColor;
      } catch (e) {
        return "?";
      }
    },
    // Live-preview the selected topic's fix by injecting (or removing) a temp
    // <style>. Lets an admin SEE the change before committing it via a prompt.
    toggleDebugPreview() {
      var id = "jcs-debug-preview";
      var existing = document.getElementById(id);
      if (existing) {
        if (existing.parentNode) existing.parentNode.removeChild(existing);
        this.debugPreviewOn = false;
        return;
      }
      var t = this._topicByKey(this.debugTopic);
      if (!t) return;
      var css = t.previewMatch
        ? this._buildMatchPreviewCss()
        : t.previewCartColor
        ? this._buildCartColorPreviewCss()
        : t.previewCss || "";
      if (!css) return;
      var st = document.createElement("style");
      st.id = id;
      st.textContent = css;
      document.head.appendChild(st);
      this.debugPreviewOn = true;
    },
    // Dynamic preview for "Match card color to baseline": set THIS page's card
    // (incl ::before) to the baseline page's captured card background color.
    _buildMatchPreviewCss() {
      var cur = this.captureLayoutSignature();
      var base =
        (this._pageSigs || {})[this.debugBaseline || this._bestBaseline(cur)];
      if (!base || !base.cardBg || !cur.cardSel || cur.cardSel === "(none)")
        return "";
      var color = base.cardBg.replace("(::before)", "").trim();
      if (color.indexOf("rgb") !== 0) return "";
      var sel = cur.cardSel;
      return (
        "html.dark-mode " + sel + ", html.dark-mode " + sel + "::before, " +
        "html.mid-mode " + sel + ", html.mid-mode " + sel + "::before " +
        "{ background-color: " + color + " !important; }"
      );
    },
    // The FIRM standard for content-card backgrounds (user, 2026-06-13): every
    // card reads the SAME surface as the floating cart + Top-Parts — dark
    // #1f242b, mid #b9bcc0. Light-mode cards are the steele white default and
    // are NOT standardized here. This is the single source the overlay uses to
    // judge / preview / suggest card colors, so a compare always targets it.
    _cardSurfaceFor(theme) {
      if (theme === "dark") return { rgb: "rgb(31, 36, 43)", hex: "#1f242b" };
      if (theme === "mid") return { rgb: "rgb(185, 188, 192)", hex: "#b9bcc0" };
      return null;
    },
    _normRgb(s) {
      return (s || "").replace(/\s+/g, "").toLowerCase();
    },
    // Dynamic preview for "card -> cart gray": set THIS page's content card
    // (incl ::before) to the STANDARD surface (#1f242b dark / #b9bcc0 mid) so it
    // matches the floating cart + Top-Parts.
    _buildCartColorPreviewCss() {
      var cur = this.captureLayoutSignature();
      if (!cur.cardSel || cur.cardSel === "(none)") return "";
      var want = this._cardSurfaceFor(cur.theme);
      var color = want
        ? want.hex
        : cur.cartBg && cur.cartBg.indexOf("rgb") === 0
        ? cur.cartBg
        : "#1f242b";
      var themeSel = cur.theme === "mid" ? "html.mid-mode " : "html.dark-mode ";
      var sel = cur.cardSel;
      return (
        themeSel + sel + ", " + themeSel + sel + "::before " +
        "{ background-color: " + color + " !important; }"
      );
    },
    // Does THIS page's content card differ from the STANDARD surface (#1f242b
    // dark / #b9bcc0 mid)? (dark/mid only). Used by both the comparison and the
    // "already correct" guard so an aligned page with a wrong card color is not
    // called done.
    _cardOffCart(cur) {
      cur = cur || this.captureLayoutSignature();
      var want = this._cardSurfaceFor(cur.theme);
      if (!want) return false;
      var card = cur.cardBg ? cur.cardBg.replace("(::before)", "").trim() : "";
      if (card.indexOf("rgb") !== 0) return false;
      return this._normRgb(card) !== this._normRgb(want.rgb);
    },
    // responsive.js (src/assets/js/responsive.js) REPARENTS header elements
    // (#logo, #header-account, #hc-items, form#search) INTO #main-nav at <992px
    // (mobile) and back to #header-wrapper at >=992px on resize. If the move
    // BACK to desktop doesn't fire (a resize / dev-tools toggle race), they stay
    // stuck inside the navbar at desktop and INFLATE the brushed-steel band
    // (~42->~69px) + show a duplicate logo. Detect it so it's a ONE-LINE flag,
    // not a multi-round hunt. (A flex container measured taller than its tallest
    // child === extra reparented nodes crammed into it — invisible to CSS.)
    _navReparentStuck() {
      var nav = document.getElementById("main-nav");
      if (!nav || window.innerWidth < 992) return [];
      var stuck = [];
      ["#logo", "#header-account", "#hc-items", "form#search"].forEach(function (s) {
        var el = nav.querySelector(s);
        if (el && el.getClientRects().length) stuck.push(s);
      });
      return stuck;
    },
    removeDebugPreview() {
      var existing = document.getElementById("jcs-debug-preview");
      if (existing && existing.parentNode)
        existing.parentNode.removeChild(existing);
      this.debugPreviewOn = false;
    },
    // Picking a Target CSS file also flips the SITE into that mode so you can
    // PREVIEW edits there: darkmode.css → dark, mid-mode.css → mid,
    // steele-consistency-fix.css → light. ("" = no target → leave theme as-is.)
    // Drives the exact same <html> class + localStorage as Head.vue's toggle,
    // and emits "color-mode-changed" so the toggle icon stays in sync.
    onDebugCssFileChange() {
      this._applyColorModeForCssFile(this.debugCssFile);
      // Switching the Target CSS also switches the site mode (above). Re-sync the
      // live editor to the NEW mode so it stays truthful: the old preview was
      // scoped to the PREVIOUS theme (so it stopped matching → vanished), and the
      // "resting colors were …" note was frozen at pick time. Drop the old
      // preview, re-read the element's resting colors in THIS mode, and re-apply
      // the preview with the new theme prefix so it's visible + accurate again.
      if (this.isAdjustCssFocus && this.cssEditSel && this._cssCurEl) {
        this.revertCssEdit();
        this._reseedCssOrigs();
        this.applyCssEdit();
        // The element's neighbors / "background hidden" status can also differ by
        // mode (e.g. items become opaque in dark/mid) — recompute them.
        this._buildCssNeighbors(this._cssCurEl);
        var covered = this._cssCoveredChild(this._cssCurEl);
        this._cssCoveredNode = covered ? covered.node : null;
        this.cssEditCoveredBy = covered ? { sel: covered.sel, label: covered.label } : null;
      }
      this.refreshDebugOverlay();
    },
    _applyColorModeForCssFile(key) {
      var mode =
        key === "darkmode" ? "dark" :
        key === "midmode" ? "mid" :
        key === "steele" ? "light" : null;
      if (!mode) return; // no target selected → don't change the current theme
      var html = document.documentElement;
      html.classList.remove("dark-mode", "mid-mode");
      if (mode === "dark") html.classList.add("dark-mode");
      else if (mode === "mid") html.classList.add("mid-mode");
      try {
        localStorage.colorMode = mode;
        localStorage.darkMode = mode === "dark" ? "1" : ""; // back-compat
      } catch (e) {
        /* ignore */
      }
      if (Vue.prototype.$cartBus) Vue.prototype.$cartBus.$emit("color-mode-changed", mode);
    },
    // ===== Mobile preview (3 phone frames) =====
    toggleMobilePreview() {
      this.mobilePreviewOn = !this.mobilePreviewOn;
      if (this.mobilePreviewOn) {
        this.composerOn = false; // shares the right-of-console spot
        this.reloadMobilePreview();
      }
    },
    // Point all 3 phone frames at the CURRENT route (absolute, same-origin) and
    // force a fresh load by bumping the nonce (each iframe's :key includes it).
    reloadMobilePreview() {
      this.mobilePreviewNonce++;
      var base;
      try {
        base = window.location.origin + ((this.$route && this.$route.fullPath) || "/");
      } catch (e) {
        base = "/";
      }
      // Cache-bust the iframe URL. Re-keying the iframe alone reloads the SAME
      // url, which the browser serves from cache — so ↻ appeared to do nothing
      // and the preview kept showing an older bundle. A unique ?_pv= per click
      // forces a fresh document (and thus the latest app.[hash].js).
      var sep = base.indexOf("?") >= 0 ? "&" : "?";
      this.mobilePreviewSrc = base + sep + "_pv=" + this.mobilePreviewNonce;
    },
    // ===== Layout composer =====
    toggleComposer() {
      this.composerOn = !this.composerOn;
      if (this.composerOn) {
        this.mobilePreviewOn = false; // shares the right-side spot
        this.phonePage = null;
        this.phoneSim = { cart: [] };
        this._loadComposerVersionsFromServer();
      }
    },
    onPhoneNavigate(page) {
      this.phonePage = page === "home" ? null : page;
    },
    partLabel(key) {
      for (var i = 0; i < this.composerParts.length; i++) {
        if (this.composerParts[i].key === key) return this.composerParts[i].label;
      }
      return key;
    },
    onComposerDragStart(key) {
      this.composerDragKey = key;
      this.composerDragFrom = null;
    },
    onMockDragStart(i) {
      if (this._mockResizing) return;
      this.composerDragFrom = i;
      this.composerDragKey = null;
    },
    // Drag the corner grip to resize a part — live width/height onto the part
    // (so the mock resizes AND the prompt updates). Plain mouse tracking; the
    // canvas/phone aren't scaled, so screen px map 1:1 to element px.
    startMockResize(i, ev) {
      var self = this;
      var el = ev.currentTarget.parentNode; // the .jcs-cv-mock
      if (!el) return;
      this._mockResizing = true;
      this.composerSelIdx = i;
      // Width tracks as a % of the row, so the space freed by shrinking a part
      // is a clean percentage another part can fill; height stays px.
      var container = el.parentNode;
      var cw = container ? container.clientWidth : el.offsetWidth;
      try {
        if (container && window.getComputedStyle) {
          var cs = window.getComputedStyle(container);
          cw -= (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
        }
      } catch (e) {
        /* ignore */
      }
      if (cw < 1) cw = el.offsetWidth || 1;
      var startX = ev.clientX;
      var startY = ev.clientY;
      var startW = el.offsetWidth;
      var startH = el.offsetHeight;
      var move = function (e) {
        var it = self.composerStack[i];
        if (!it) return;
        var px = startW + (e.clientX - startX);
        it.s.w = Math.max(15, Math.min(100, Math.round((px / cw) * 100))) + "%";
        it.s.h = Math.max(24, Math.round(startH + (e.clientY - startY))) + "px";
      };
      var up = function () {
        self._mockResizing = false;
        document.removeEventListener("mousemove", move);
        document.removeEventListener("mouseup", up);
      };
      document.addEventListener("mousemove", move);
      document.addEventListener("mouseup", up);
    },
    // Drop onto the stack at index `at` (a mock's index, or stack.length to
    // append). Handles BOTH adding from the palette and reordering an existing
    // part — so it works the same in the composer canvas and the live phone.
    onStackDrop(at) {
      var stack = this.composerStack;
      if (this.composerDragKey) {
        var item = this._composerPlaced(this.composerDragKey);
        this.composerDragKey = null;
        var idx = at == null ? stack.length : Math.min(at, stack.length);
        // Auto-fill: simulate the row layout of the parts before the drop —
        // each %-width part fills part of a row, a 100%/no-% part is a full row —
        // and give the new part whatever % is LEFT on the last row, so it fills
        // the free space a resize created (at whatever size that space is).
        var rowUsed = 0;
        for (var j = 0; j < idx && j < stack.length; j++) {
          var m = /^\s*(\d+(?:\.\d+)?)%\s*$/.exec((stack[j].s && stack[j].s.w) || "");
          var w = m ? parseFloat(m[1]) : 100;
          if (w >= 100) rowUsed = 0;
          else if (rowUsed + w > 100) rowUsed = w;
          else rowUsed += w;
        }
        var free = 100 - rowUsed;
        if (free > 0 && free < 100) item.s.w = Math.round(free) + "%";
        stack.splice(idx, 0, item);
        this.composerSelIdx = idx;
      } else if (this.composerDragFrom != null) {
        var from = this.composerDragFrom;
        this.composerDragFrom = null;
        if (from < 0 || from >= stack.length) return;
        var moved = stack.splice(from, 1)[0];
        var to = at == null ? stack.length : at;
        if (from < to) to--; // the removed item shifted everything after it
        to = Math.max(0, Math.min(to, stack.length));
        stack.splice(to, 0, moved);
        this.composerSelIdx = to;
      }
    },
    moveComposerPart(i, dir) {
      var stack = this.composerStack;
      var j = i + dir;
      if (j < 0 || j >= stack.length) return;
      var item = stack.splice(i, 1)[0];
      stack.splice(j, 0, item);
      if (this.composerSelIdx === i) this.composerSelIdx = j;
    },
    removeComposerPart(i) {
      this.composerStack.splice(i, 1);
      if (this.composerSelIdx === i) this.composerSelIdx = -1;
      else if (this.composerSelIdx > i) this.composerSelIdx--;
    },
    clearComposer() {
      this.composerStack = [];
      this.composerSelIdx = -1;
      this.phonePage = null;
      this.phoneSim = { cart: [] };
    },
    // Each placed part carries per-element style overrides (font/size/etc).
    _composerPlaced(key, s) {
      s = s || {};
      return {
        k: key,
        s: {
          font: s.font || "",
          size: s.size || "",
          w: s.w || "",
          h: s.h || "",
          color: s.color || "",
          bg: s.bg || "",
          align: s.align || "",
        },
      };
    },
    // Back-compat: flatten old {left,center,right} zones (or a {stack}/bare array,
    // of strings or objects) into the single ordered stack.
    _composerNormalizeStack(src) {
      var self = this;
      var flat = [];
      if (src && typeof src === "object" && !Array.isArray(src) &&
          (src.left || src.center || src.right)) {
        flat = [].concat(src.left || [], src.center || [], src.right || []);
      } else if (src && src.stack && Array.isArray(src.stack)) {
        flat = src.stack;
      } else if (Array.isArray(src)) {
        flat = src;
      }
      return flat.map(function (e) {
        if (e && typeof e === "object") return self._composerPlaced(e.k || e.key, e.s);
        return self._composerPlaced(e);
      });
    },
    selectComposerEl(i) {
      this.composerSelIdx = i;
    },
    isComposerSel(i) {
      return this.composerSelIdx === i;
    },
    resetComposerElStyle() {
      var it = this.composerSelItem;
      if (!it) return;
      it.s.font = ""; it.s.size = ""; it.s.w = ""; it.s.h = "";
      it.s.color = ""; it.s.bg = ""; it.s.align = "";
    },
    // Style overrides applied to a mock's wrapper. As well as the direct
    // properties, we publish CSS custom properties (--cmp-color / --cmp-bg /
    // --cmp-fs / --cmp-font / --cmp-align) which the interactive mocks' own
    // scoped CSS reads (e.g. dropdown values use `var(--cmp-color, #222)`).
    // Custom properties inherit through the DOM regardless of <style scoped>,
    // so setting them here lets the editor recolor/resize text that has its
    // own explicit color — which a plain inherited `color` could never reach.
    mockStyle(item) {
      var s = (item && item.s) || {};
      var o = {};
      if (s.font) { o.fontFamily = s.font; o["--cmp-font"] = s.font; }
      if (s.size) { o.fontSize = s.size + "px"; o["--cmp-fs"] = s.size + "px"; }
      if (s.w) o.width = /[%a-z]/i.test(s.w) ? s.w : s.w + "px";
      if (s.h) o.height = /[%a-z]/i.test(s.h) ? s.h : s.h + "px";
      if (s.color) { o.color = s.color; o["--cmp-color"] = s.color; }
      if (s.bg) { o.background = s.bg; o["--cmp-bg"] = s.bg; }
      if (s.align) { o.textAlign = s.align; o["--cmp-align"] = s.align; }
      return o;
    },
    // Static, author-controlled HTML mock per part (safe for v-html — no user data).
    mockInner(key) {
      var red = "#c8302a";
      switch (key) {
        case "header":
          return '<div style="display:flex;align-items:center;justify-content:space-between;padding:6px 10px;border-bottom:2px solid ' + red + '">' +
            '<div style="font-weight:800;color:' + red + '">&#9670; STEELE</div>' +
            '<div style="color:#555">(800) 278-3353</div>' +
            '<div style="display:flex;align-items:center;gap:5px;color:' + red + '"><span style="width:13px;height:13px;border-radius:50%;background:' + red + ';display:inline-block"></span>My Account &#9662;</div></div>';
        case "nav":
          return '<div style="display:flex;gap:14px;padding:7px 12px;background:#2a2f36;color:#e6e8eb">' +
            '<span>Home</span><span>Order</span><span>RFQ</span><span>Documents</span><span>History</span></div>';
        case "breadcrumb":
          return '<div style="padding:4px 10px;color:#777">Home &#8250; ' + ((this.$route && this.$route.path) || "") + '</div>';
        case "search":
          return '<div style="display:flex;padding:8px 10px;gap:6px"><span style="flex:1;border:1px solid #cfd3d7;border-radius:4px;padding:6px 8px;color:#999;background:#fff">Search parts, machines&#8230;</span><span style="background:' + red + ';color:#fff;border-radius:4px;padding:6px 12px">Search</span></div>';
        case "content":
          return '<div style="border:1px solid #e2e5e8;border-top:3px solid ' + red + ';border-radius:6px;padding:12px;background:#fff"><div style="font-weight:700;margin-bottom:8px">Page content</div>' + this._mockLines(4) + '</div>';
        case "buildcard":
          return '<div style="border:1px solid #e2e5e8;border-top:3px solid ' + red + ';border-radius:6px;padding:12px;background:#fff"><div style="font-weight:700;margin-bottom:8px">Build your order</div><div style="border:1px solid #cfd3d7;border-radius:4px;padding:6px 8px;color:#999;margin-bottom:8px">Add a part&#8230;</div>' + this._mockLines(2) + '</div>';
        case "cart":
          return '<div style="border:1px solid ' + red + ';border-radius:8px;background:#fff;overflow:hidden"><div style="background:' + red + ';color:#fff;padding:6px 10px;font-weight:700">&#128722; Your Cart</div><div style="padding:8px 10px">' + this._mockRow("DSD S 0488 SR", "&#215;8") + this._mockRow("DBE S 0130 CI", "&#215;1") + '<div style="display:flex;justify-content:space-between;border-top:1px solid #eee;margin-top:6px;padding-top:6px;font-weight:700">Total<span>$3,257.40</span></div><div style="background:' + red + ';color:#fff;text-align:center;border-radius:5px;padding:6px;margin-top:8px">Review &amp; Place</div></div></div>';
        case "topparts":
          return '<div style="border:1px solid #e2e5e8;border-radius:8px;background:#fff;overflow:hidden"><div style="background:#2a2f36;color:#fff;padding:6px 10px;font-weight:700">&#9733; Top 5 Parts</div><div style="padding:8px 10px">' + this._mockRow("PSA S 3407 SP", "+") + this._mockRow("NDH S 2011 PC", "+") + this._mockRow("DSD S 0486 SR", "+") + '</div></div>';
        case "info":
          return this._mockPill("&#8505;&#65039;", "Important Info");
        case "guide":
          return this._mockPill("&#127916;", "Guide");
        case "setup":
          return this._mockPill("&#9881;&#65039;", "Account Setup");
        case "footer":
          return '<div style="background:#2a2f36;color:#cfd3d7;padding:10px;text-align:center">&#169; JC Steele &amp; Sons &#183; Service Portal &#183; (800) 278-3353</div>';
        default:
          return '<div style="padding:8px">' + this.partLabel(key) + '</div>';
      }
    },
    _mockLines(n) {
      var out = "";
      for (var i = 0; i < n; i++) {
        var w = 62 + ((i * 37) % 36);
        out += '<div style="height:7px;border-radius:3px;background:#e9ecef;margin-bottom:6px;width:' + w + '%"></div>';
      }
      return out;
    },
    _mockRow(label, tag) {
      return '<div style="display:flex;justify-content:space-between;padding:3px 0;border-bottom:1px solid #f1f1f1"><span>' + label + '</span><span style="color:#c8302a;font-weight:700">' + tag + '</span></div>';
    },
    _mockPill(icon, label) {
      return '<div style="display:inline-flex;align-items:center;gap:6px;background:#c8302a;color:#fff;border-radius:18px;padding:7px 14px;font-weight:700">' + icon + ' ' + label + '</div>';
    },
    buildComposerPrompt() {
      var self = this;
      var stack = this.composerStack || [];
      if (!stack.length)
        return "Drag parts from the palette into the composer to compose a layout — then this prompt fills in.";
      // Group parts into rows the same way the canvas does (greedy fill by width)
      // so the prompt describes side-by-side sections, not just a flat list.
      var pctOf = function (s) {
        var m = /^\s*(\d+(?:\.\d+)?)%\s*$/.exec((s && s.w) || "");
        return m ? parseFloat(m[1]) : 100;
      };
      var rows = [];
      var cur = [];
      var used = 0;
      stack.forEach(function (item) {
        var w = pctOf(item.s);
        if (w >= 100) {
          if (cur.length) rows.push(cur);
          rows.push([item]);
          cur = [];
          used = 0;
        } else if (used + w > 100) {
          if (cur.length) rows.push(cur);
          cur = [item];
          used = w;
        } else {
          cur.push(item);
          used += w;
        }
      });
      if (cur.length) rows.push(cur);
      var lines = [];
      rows.forEach(function (row, i) {
        if (row.length === 1) {
          var it = row[0];
          var st = self._composerStyleText(it.s);
          lines.push(i + 1 + ". " + self.partLabel(it.k) + (st ? " — " + st : ""));
        } else {
          var segs = row.map(function (it) {
            var st = self._composerStyleText(it.s);
            return self.partLabel(it.k) + (st ? " (" + st + ")" : "");
          });
          lines.push(i + 1 + ". SIDE BY SIDE — " + segs.join("  |  "));
        }
      });
      var route = (this.$route && this.$route.path) || "the page";
      return (
        "Lay out " + route + " top → bottom in these rows (a row marked SIDE BY SIDE means those sections sit on one row at the given widths; otherwise full-width):\n\n" +
        lines.join("\n") +
        "\n\nThe per-element notes (font / size / width / height / text color / fill / align) are the desired styling for that section. " +
        "Apply using the established patterns — floating widgets (Cart / Top Parts / Info / Guide / Setup) via the portal + alignWidgetStack system in App.vue; structural sections (header, nav, breadcrumb, content, footer) via their containers / steele-consistency-fix.css. Keep it responsive (rows stack on mobile). Rebuild the frontend after editing."
      );
    },
    _composerStyleText(s) {
      if (!s) return "";
      var p = [];
      if (s.font) p.push("font " + s.font);
      if (s.size) p.push(s.size + "px");
      if (s.w) p.push("width " + (/[%a-z]/i.test(s.w) ? s.w : s.w + "px"));
      if (s.h) p.push("height " + (/[%a-z]/i.test(s.h) ? s.h : s.h + "px"));
      if (s.color) p.push("text " + s.color);
      if (s.bg) p.push("fill " + s.bg);
      if (s.align) p.push("align " + s.align);
      return p.join(", ");
    },
    saveComposerVersion() {
      var name =
        (this.composerSaveName || "").trim() || "Layout " + (this.composerVersions.length + 1);
      var route = (this.$route && this.$route.path) || "";
      var stack = JSON.parse(JSON.stringify(this.composerStack));
      var prompt = this.composerPrompt;
      var self = this;
      // DB-first (api/account/composer-layouts, Master-only). Falls back to
      // localStorage if the backend/table isn't deployed yet or the call fails.
      httpClient
        .post("/account/composer-layouts", {
          name: name,
          route: route,
          layoutJson: JSON.stringify(stack),
          promptJson: prompt,
        })
        .then(function (res) {
          var d = (res && res.data) || {};
          var ts = "";
          try {
            ts = d.createdAtUtc
              ? new Date(d.createdAtUtc).toLocaleString()
              : new Date().toLocaleString();
          } catch (e) {
            /* ignore */
          }
          self.composerVersions.unshift({
            id: d.id || null,
            name: name,
            ts: ts,
            route: route,
            stack: stack,
            prompt: prompt,
            by: d.createdBy || "",
            _server: true,
          });
          self.composerSaveName = "";
        })
        .catch(function () {
          self._saveComposerVersionLocal(name, route, stack, prompt);
        });
    },
    _saveComposerVersionLocal(name, route, stack, prompt) {
      var ts = "";
      try { ts = new Date().toLocaleString(); } catch (e) { /* ignore */ }
      this.composerVersions.unshift({
        id: null,
        name: name,
        ts: ts,
        route: route,
        stack: stack,
        prompt: prompt,
        _server: false,
      });
      this._persistComposerVersions();
      this.composerSaveName = "";
    },
    loadComposerVersion(i) {
      var v = this.composerVersions[i];
      if (v) this.composerStack = this._composerNormalizeStack(v.stack || v.zones);
      this.composerSelIdx = -1;
    },
    deleteComposerVersion(i) {
      var v = this.composerVersions[i];
      if (!v) return;
      var self = this;
      if (v._server && v.id) {
        httpClient
          .delete("/account/composer-layouts/" + v.id)
          .then(function () {
            self.composerVersions.splice(i, 1);
          })
          .catch(function () {
            // Still remove locally so the UI stays responsive.
            self.composerVersions.splice(i, 1);
            self._persistComposerVersions();
          });
        return;
      }
      this.composerVersions.splice(i, 1);
      this._persistComposerVersions();
    },
    _persistComposerVersions() {
      try {
        localStorage.setItem(
          "jcs_composerVersions",
          JSON.stringify(this.composerVersions.slice(0, 50))
        );
      } catch (e) {
        /* ignore */
      }
    },
    _loadComposerVersionsFromServer() {
      var self = this;
      httpClient
        .get("/account/composer-layouts")
        .then(function (res) {
          var rows = (res && res.data) || [];
          if (Object.prototype.toString.call(rows) === "[object Array]") {
            self.composerVersions = rows.map(function (r) {
              return self._composerRowToVersion(r);
            });
          }
        })
        .catch(function () {
          // Backend not deployed / table missing / not Master — keep localStorage.
        });
    },
    _composerRowToVersion(r) {
      var stack = [];
      try {
        stack = this._composerNormalizeStack(JSON.parse(r.layoutJson || "[]"));
      } catch (e) {
        /* ignore */
      }
      var ts = "";
      try {
        ts = r.createdAtUtc ? new Date(r.createdAtUtc).toLocaleString() : "";
      } catch (e) {
        /* ignore */
      }
      return {
        id: r.id,
        name: r.name,
        ts: ts,
        route: r.route || "",
        stack: stack,
        prompt: r.promptJson || "",
        by: r.createdBy || "",
        _server: true,
      };
    },
    onDebugTopicChange() {
      // Switching topics reverts any live preview (it belonged to the old one).
      this.removeDebugPreview();
      // Entering / leaving the live CSS color editor focus.
      if (this.isAdjustCssFocus) {
        this.rescanCssElements();
      } else {
        this.revertCssEdit();
        this._endCssPick();
        this.cssEditSel = "";
      }
      this.refreshDebugOverlay();
    },
    // ===== Live CSS color editor ("Adjust CSS Styling" focus) =====
    // Plain-language names for known portal classes so the element list reads in
    // friendly terms; unknown classes fall back to a humanized class + sample text.
    _cssFriendlyDict() {
      return {
        "insights-queue-row-code": "Cart/order row — part code",
        "insights-queue-row-price": "Cart/order row — price",
        "insights-queue-row-qty": "Cart/order row — quantity",
        "insights-order-group-title": "Cart — order/PO group title",
        "insights-order-group-head": "Cart — order group header",
        "insights-selected-title": "Cart — section title",
        "staging-tray-header": "Cart panel — header bar",
        "staging-tray-title": "Cart panel — title",
        "staging-tray-total": "Cart panel — total amount",
        "staging-tray-total-label": "Cart panel — total label",
        "staging-tray-wrapper": "Cart panel — surface",
        "top-parts-card": "Top Parts — card surface",
        "top-parts-title": "Top Parts — title",
        "ao-table": "Content card — table surface",
        "insights-container": "Dashboard — insights card",
        "dashboard-card": "Dashboard / Top-Parts card",
        "create-order-form": "Build page — content card",
        "create-rfq-form": "RFQ build — content card",
        "nav-link": "Top menu — link",
      };
    },
    _humanizeClass(c) {
      return ("" + (c || ""))
        .replace(/[-_]+/g, " ")
        .replace(/\b\w/g, function (m) { return m.toUpperCase(); })
        .trim();
    },
    // Most useful CSS selector for an element: its id, else its most "semantic"
    // class (skipping Bootstrap/utility classes), else the bare tag.
    _cssSelectorFor(el) {
      if (!el || !el.tagName) return "";
      var tag = el.tagName.toLowerCase();
      if (["html", "body", "script", "style", "link", "meta", "svg", "path"].indexOf(tag) >= 0)
        return "";
      if (el.id && /^[a-zA-Z][\w-]*$/.test(el.id)) return "#" + el.id;
      var cn = el.className;
      if (cn && typeof cn !== "string" && cn.baseVal != null) cn = cn.baseVal; // SVG
      var classes = ("" + (cn || "")).split(/\s+/).filter(Boolean);
      function isUtil(c) {
        // Layout utilities + TRANSIENT state/animation classes (visible, selected,
        // transition, open…) — never the right primary selector for theming, so a
        // dropdown like .menu.transition.visible resolves to .menu, not .transition.
        if (/^(row|col|flex|active|show|shown|fade|fadein|collapse|collapsed|expanded|container|clearfix|btn|nav|visible|invisible|hidden|selected|open|opened|closed|transition|disabled|loading|focus|hover)$/.test(c))
          return true;
        if (/^(col-|text-|justify-|align-|d-|p[xytblr]?-|m[xytblr]?-|w-|h-|order-|offset-|btn-|bg-|border)/.test(c))
          return true;
        return false;
      }
      var best = "";
      classes.forEach(function (c) {
        if (isUtil(c)) return;
        if (!/^[a-zA-Z][\w-]*$/.test(c)) return; // skip escaped / weird classes
        if (c.length > best.length) best = c; // prefer the longest (most specific)
      });
      if (!best) return tag;
      // Generic component-library classes (.item / .text / .menu / .label …) are
      // reused widely AND often lose specificity to the library's own rules. If
      // the chosen class is one of these, qualify it with the nearest meaningful
      // ANCESTOR (an id, else a distinctive class) so the selector targets THIS
      // component and wins — e.g. a Semantic-UI dropdown value becomes
      // "#MachineSelect .item" instead of a bare ".item" that hits every list.
      if (/^(item|text|menu|label|title|content|header|body|icon|link|cell|value|name|list|field|wrapper|inner|outer|box|panel|entry|option)$/.test(best)) {
        var ctx = this._cssAncestorContext(el);
        if (ctx) return ctx + " ." + best;
      }
      return "." + best;
    },
    // Nearest meaningful ancestor selector for qualifying a generic class: a real
    // id (skipping the page-root ids), else a distinctive (non-utility) class,
    // within a few hops. Returns "" if none found.
    _cssAncestorContext(el) {
      var skip = { app: 1, routeView: 1, mainBody: 1, "header-wrapper": 1 };
      var a = el.parentElement,
        hops = 0;
      while (a && a !== document.body && hops < 5) {
        if (a.id && /^[a-zA-Z][\w-]*$/.test(a.id) && !skip[a.id]) return "#" + a.id;
        a = a.parentElement;
        hops++;
      }
      a = el.parentElement;
      hops = 0;
      while (a && a !== document.body && hops < 5) {
        var cn = a.className;
        if (cn && typeof cn !== "string" && cn.baseVal != null) cn = cn.baseVal;
        var cls = ("" + (cn || "")).split(/\s+/).filter(Boolean);
        for (var i = 0; i < cls.length; i++) {
          var c = cls[i];
          if (
            /^[a-zA-Z][\w-]*$/.test(c) &&
            c.length >= 5 &&
            !/^(item|text|menu|label|title|content|header|body|icon|link|cell|value|name|list|field|wrapper|inner|outer|box|panel|active|selected|visible|hidden|transition|container|row|col)/.test(c)
          )
            return "." + c;
        }
        a = a.parentElement;
        hops++;
      }
      return "";
    },
    _cssSampleText(el) {
      var t = "";
      try {
        Array.prototype.forEach.call(el.childNodes, function (n) {
          if (!t && n.nodeType === 3 && n.textContent.trim()) t = n.textContent.trim();
        });
        if (!t) t = ("" + (el.textContent || "")).trim();
      } catch (e) {
        /* ignore */
      }
      t = t.replace(/\s+/g, " ");
      return t.length > 22 ? t.slice(0, 22) + "…" : t;
    },
    // Full tag/id/ALL-classes signature, e.g. "div.menu.transition.visible". Used
    // in the label so the search box matches ANY of the element's classes — not
    // just the single editing selector (_cssSelectorFor picks one).
    _cssFullSig(el) {
      if (!el || !el.tagName) return "";
      var sig = el.tagName.toLowerCase();
      if (el.id) sig += "#" + el.id;
      var cn = el.className;
      if (cn && typeof cn !== "string" && cn.baseVal != null) cn = cn.baseVal;
      ("" + (cn || ""))
        .split(/\s+/)
        .filter(Boolean)
        .forEach(function (c) {
          sig += "." + c;
        });
      return sig;
    },
    _friendlyCssLabel(el, sel) {
      var dict = this._cssFriendlyDict();
      // Friendly name keys off the LAST class in the selector (works whether the
      // selector is ".item" or a contextual "#MachineSelect .item").
      var classMatches = ("" + (sel || "")).match(/\.[A-Za-z][\w-]*/g);
      var key = classMatches && classMatches.length ? classMatches[classMatches.length - 1].slice(1) : "";
      var base =
        dict[key] ||
        (key ? this._humanizeClass(key) : el && el.tagName ? el.tagName.toLowerCase() : sel);
      var txt = this._cssSampleText(el);
      // The full signature is appended so searching ANY class (e.g. "menu",
      // "transition", "visible") finds the element even though the editing
      // selector is just the primary class (.menu).
      var sig = this._cssFullSig(el);
      var sigPart = sig && sig !== sel ? "  ·  (" + sig + ")" : "";
      return base + "  ·  " + sel + sigPart + (txt ? "  ·  “" + txt + "”" : "");
    },
    // Scan EVERY visible element on the page (skip non-visual tags + the overlay),
    // friendly-labeled, for the searchable list. NOT deduped — every instance is
    // listed (the search narrows; editing still targets the element's SELECTOR,
    // so it affects all matching elements, which is what you want for theme CSS).
    // Cheap: NO getComputedStyle here (only on selection) so "every element" stays
    // fast. DOM nodes are kept in a NON-reactive parallel array so Vue doesn't try
    // to observe them — _cssScanEls[i] <-> cssEditList[{ i, ... }].
    rescanCssElements() {
      var self = this;
      var out = [];
      var els = [];
      var nodes;
      try {
        nodes = document.body.querySelectorAll("*");
      } catch (e) {
        nodes = [];
      }
      var SKIP = ["script", "style", "link", "meta", "svg", "path", "br", "head", "template", "noscript", "defs", "g"];
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (els.length >= 800) break;
        if (el.closest && el.closest(".jcs-debug-overlay")) continue;
        var tag = (el.tagName || "").toLowerCase();
        if (SKIP.indexOf(tag) >= 0) continue;
        var rect;
        try {
          rect = el.getBoundingClientRect();
        } catch (e) {
          continue;
        }
        if (!rect || rect.width === 0 || rect.height === 0) continue; // visible only
        var sel = self._cssSelectorFor(el);
        if (!sel) continue;
        out.push({ i: els.length, sel: sel, label: self._friendlyCssLabel(el, sel) });
        els.push(el);
      }
      this._cssScanEls = els;
      this.cssEditList = out;
      this.cssEditPick = -1;
    },
    _rgbToHex(rgb) {
      if (!rgb) return "";
      if (rgb.charAt(0) === "#") return rgb;
      var m = rgb.match(/\d+/g);
      if (!m || m.length < 3) return "";
      function h(x) {
        x = parseInt(x, 10).toString(16);
        return x.length === 1 ? "0" + x : x;
      }
      return "#" + h(m[0]) + h(m[1]) + h(m[2]);
    },
    // html.dark-mode / html.mid-mode prefix matching the chosen target CSS file
    // (steele = unscoped). Used for both the live preview and the emitted rule.
    _cssEditThemePrefix() {
      if (this.debugCssFile === "darkmode") return "html.dark-mode ";
      if (this.debugCssFile === "midmode") return "html.mid-mode ";
      return "";
    },
    // Which properties the user actually changed (color / background) → [prop,val].
    // Selector suffix per state ("base" = resting, hover/active = pseudo-classes).
    _cssStatePseudo(st) {
      if (st === "hover") return ":hover";
      if (st === "active") return ":active";
      return "";
    },
    // Changed color/bg for a state's stored values vs the element's ORIGINAL
    // (resting) colors — so a :hover rule carries only the hover overrides, etc.
    _cssEditChangedPropsFor(v) {
      var props = [];
      if (!v) return props;
      if (v.color && this._normRgb(v.color) !== this._normRgb(this.cssEditColorOrig))
        props.push(["color", v.color]);
      if (v.bg && this._normRgb(v.bg) !== this._normRgb(this.cssEditBgOrig))
        props.push(["background-color", v.bg]);
      return props;
    },
    // Save the live inputs into the active state's bucket.
    _saveCssActiveState() {
      if (!this.cssEditByState) this.cssEditByState = { base: null, hover: null, active: null };
      this.cssEditByState[this.cssEditState] = { color: this.cssEditColor, bg: this.cssEditBg };
    },
    // All per-state rules that have changes → [{ st, pseudo, props }]. Drives BOTH
    // the live <style> and the prompt, so editing No-Hover + On-Hover + On-Click
    // accumulates into separate rules.
    _buildCssEditRules() {
      var self = this;
      var rules = [];
      ["base", "hover", "active"].forEach(function (st) {
        var v = (self.cssEditByState || {})[st];
        var props = self._cssEditChangedPropsFor(v);
        if (props.length) rules.push({ st: st, pseudo: self._cssStatePseudo(st), props: props });
      });
      return rules;
    },
    _seedCssEditFrom(el) {
      var cs = window.getComputedStyle(el);
      var color = this._rgbToHex(cs.color) || "#dddddd";
      var bg = this._rgbToHex(cs.backgroundColor) || "#1f242b";
      this.cssEditColor = color;
      this.cssEditBg = bg;
      this.cssEditColorOrig = color;
      this.cssEditBgOrig = bg;
    },
    // Refresh ONLY the "resting" original colors from the element's CURRENT
    // computed style (e.g. after the mode switches), WITHOUT touching the user's
    // chosen Text/Bg values. Call after reverting any preview so it reads natural.
    _reseedCssOrigs() {
      if (!this._cssCurEl) return;
      var cs = window.getComputedStyle(this._cssCurEl);
      var c = this._rgbToHex(cs.color);
      var b = this._rgbToHex(cs.backgroundColor);
      if (c) this.cssEditColorOrig = c;
      if (b) this.cssEditBgOrig = b;
    },
    _isOpaque(bg) {
      if (!bg || bg === "transparent") return false;
      var m = bg.match(/rgba?\(([^)]+)\)/i);
      if (!m) return true; // a named/hex color → treat as opaque
      var p = m[1].split(",");
      if (p.length >= 4) return parseFloat(p[3]) >= 0.95;
      return true; // rgb(...) is opaque
    },
    // If an OPAQUE child tiles across the element (covering ≥60% of its width +
    // full height), editing THIS element's background is hidden behind it. Return
    // that child so the editor can say "edit the rows instead" — this is what
    // makes the dropdown .item-over-.menu trap (dark/mid only) self-explanatory.
    _cssCoveredChild(el) {
      try {
        var er = el.getBoundingClientRect();
        if (!er.width || !er.height) return null;
        var kids = el.children || [];
        for (var i = 0; i < kids.length && i < 30; i++) {
          var k = kids[i];
          if (k.closest && k.closest(".jcs-debug-overlay")) continue;
          var kr = k.getBoundingClientRect();
          if (kr.width < er.width * 0.6 || kr.height < er.height * 0.4) continue;
          if (!this._isOpaque(window.getComputedStyle(k).backgroundColor)) continue;
          var sel = this._cssSelectorFor(k);
          if (sel) return { sel: sel, label: this._friendlyCssLabel(k, sel), node: k };
        }
      } catch (e) {
        /* ignore */
      }
      return null;
    },
    // Select an element by its index in the scanned list (from the searchable
    // list). Drives the color editors + the "nearby elements" panel.
    selectCssEl(i) {
      this.cssEditPick = i;
      var el = (this._cssScanEls || [])[i];
      if (el) this.selectCssNode(el);
    },
    // Core selection (from the list, a neighbor, or the on-page picker): revert
    // any live preview, read the element's NATURAL colors, and rebuild the
    // surrounding-elements list.
    selectCssNode(el) {
      if (!el) return;
      this.revertCssEdit();
      this._cssCurEl = el;
      var sel = this._cssSelectorFor(el);
      this.cssEditSel = sel;
      this.cssEditCurLabel = this._friendlyCssLabel(el, sel);
      this._seedCssEditFrom(el);
      // Reset the per-state buckets + start on the resting ("No Hover") state.
      this.cssEditState = "base";
      this.cssEditByState = { base: null, hover: null, active: null };
      this._buildCssNeighbors(el);
      // Heads-up if this element's background is hidden behind opaque content
      // (e.g. a dropdown .item tiling over .menu in dark mode) — point at the
      // child to edit instead.
      var covered = this._cssCoveredChild(el);
      this._cssCoveredNode = covered ? covered.node : null;
      this.cssEditCoveredBy = covered ? { sel: covered.sel, label: covered.label } : null;
    },
    selectCssNeighbor(ni) {
      var el = (this._cssNeighborEls || [])[ni];
      if (el) {
        this.cssEditPick = -1; // neighbors aren't necessarily in the capped list
        this.selectCssNode(el);
      }
    },
    // "Edit the rows instead" — switch the selection to the opaque child that's
    // covering the current element's background.
    selectCoveredChild() {
      if (this._cssCoveredNode) {
        this.cssEditPick = -1;
        this.selectCssNode(this._cssCoveredNode);
      }
    },
    // Build the "surrounding elements" list for the selection: a few ancestors,
    // the prev/next siblings, and the direct children that carry a useful
    // selector — so you can hop to nearby styling without searching for it.
    _buildCssNeighbors(el) {
      var self = this;
      var items = [];
      var nodes = [];
      function add(node, rel) {
        if (!node || node.nodeType !== 1 || node === el) return;
        if (node.closest && node.closest(".jcs-debug-overlay")) return;
        var tag = (node.tagName || "").toLowerCase();
        if (["script", "style", "svg", "path", "br", "template", "noscript"].indexOf(tag) >= 0) return;
        var sel = self._cssSelectorFor(node);
        if (!sel) return;
        nodes.push(node);
        items.push({ label: rel + "  " + self._friendlyCssLabel(node, sel) });
      }
      var anc = el.parentElement,
        up = 0;
      while (anc && anc !== document.body && up < 3) {
        add(anc, "↑ parent");
        anc = anc.parentElement;
        up++;
      }
      add(el.previousElementSibling, "↔ prev");
      add(el.nextElementSibling, "↔ next");
      var kids = el.children ? Array.prototype.slice.call(el.children, 0, 14) : [];
      kids.forEach(function (k) {
        add(k, "↓ child");
      });
      this._cssNeighborEls = nodes;
      this.cssEditNeighbors = items;
    },
    // Inject / update a temp <style> so the color change shows LIVE, scoped to the
    // target file's theme. Only the properties the user changed are written.
    applyCssEdit() {
      var id = "jcs-debug-cssedit";
      var existing = document.getElementById(id);
      if (this.cssEditSel) this._saveCssActiveState();
      var rules = this.cssEditSel ? this._buildCssEditRules() : [];
      if (!rules.length) {
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
        return;
      }
      var prefix = this._cssEditThemePrefix();
      var sel = this.cssEditSel;
      var css = rules
        .map(function (r) {
          var body = r.props
            .map(function (p) {
              return p[0] + ": " + p[1] + " !important;";
            })
            .join(" ");
          return prefix + sel + r.pseudo + " { " + body + " }";
        })
        .join("\n");
      if (!existing) {
        existing = document.createElement("style");
        existing.id = id;
        document.head.appendChild(existing);
      }
      existing.textContent = css;
    },
    revertCssEdit() {
      var existing = document.getElementById("jcs-debug-cssedit");
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    },
    // Load a palette swatch into the active field (Text / Bg) and apply it live.
    applyCssSwatch(hex) {
      if (this.cssEditTarget === "bg") this.cssEditBg = hex;
      else this.cssEditColor = hex;
      this.applyCssEdit();
    },
    buildCssEditPrompt() {
      if (!this.isAdjustCssFocus) return "";
      if (!this.debugCssFile)
        return "Pick a target CSS file (e.g. darkmode.css) above to start.";
      if (!this.cssEditSel)
        return "Pick an element on the page (or choose one from the list) to edit its color.";
      if (this.cssEditSel) this._saveCssActiveState();
      var rules = this._buildCssEditRules();
      if (!rules.length)
        return "Adjust the text or background color above (per state: No Hover / On Hover / On Click) — then this prompt will be ready to paste.";
      var prefix = this._cssEditThemePrefix();
      var sel = this.cssEditSel;
      var stName = { base: "resting", hover: "hover (:hover)", active: "click (:active)" };
      var block = rules
        .map(function (r) {
          return (
            "/* " + stName[r.st] + " */\n" +
            prefix + sel + r.pseudo + " {\n  " +
            r.props
              .map(function (p) {
                return p[0] + ": " + p[1] + " !important;";
              })
              .join("\n  ") +
            "\n}"
          );
        })
        .join("\n\n");
      var fileByKey = this._cssFileByKey(this.debugCssFile) || {};
      var fileName = fileByKey.label || this.debugCssFile;
      return (
        "In " + fileName + ", set the following (this styles: " + this.cssEditDesc +
        "; resting colors were color " + this.cssEditColorOrig + " / background " + this.cssEditBgOrig + "):\n\n" +
        block + "\n\nUse a PAGE-SCOPED selector if " + sel +
        " is shared by other pages, and match the id-count of any higher-specificity base rule so this wins (that's why !important is included). Rebuild the frontend after editing."
      );
    },
    // Switch the edit STATE (resting / :hover / :active). Saves the current inputs
    // into the old state, then loads the new state's values (or the resting seed
    // if that state is untouched — a real :hover/:active computed style can't be
    // read from JS, so resting is the start point). The preview rebuilds.
    setCssState(st) {
      if (this.cssEditSel) this._saveCssActiveState();
      this.cssEditState = st;
      var v = (this.cssEditByState || {})[st];
      if (v) {
        this.cssEditColor = v.color;
        this.cssEditBg = v.bg;
      } else {
        this.cssEditColor = this.cssEditColorOrig || this.cssEditColor;
        this.cssEditBg = this.cssEditBgOrig || this.cssEditBg;
      }
      this.applyCssEdit();
    },
    // Click-to-pick an element on the page (dev-tools-style). The capture-phase
    // handler swallows the click so the page doesn't navigate; clicks on the
    // overlay itself are ignored so its own controls keep working.
    toggleCssPick() {
      if (this.cssPickMode) {
        this._endCssPick();
        return;
      }
      var self = this;
      this.cssPickMode = true;
      try {
        document.body.style.cursor = "crosshair";
      } catch (e) {
        /* ignore */
      }
      this._cssPickHandler = function (ev) {
        var t = ev.target;
        if (t && t.closest && t.closest(".jcs-debug-overlay")) return; // overlay UI works normally
        // ALT / CTRL / CMD + click = "lock onto" the EXACT element under the
        // cursor, even inside a dropdown/menu. It bypasses the openable pass-
        // through below and swallows the click (so the control doesn't act/select),
        // so you can pick a dropdown VALUE directly on the page without it changing
        // as you move to the panel. THIS is how to grab a dropdown value/menu.
        var forcePick = ev.altKey || ev.ctrlKey || ev.metaKey;
        if (!forcePick) {
          // SMART pick: a PLAIN click on an OPENABLE control (dropdown / select /
          // popover) is allowed THROUGH so it can open — then Alt+click a revealed
          // value to pick it. Plain clicks elsewhere pick directly.
          var openable =
            t.closest &&
            t.closest(
              '.dropdown, .ui.dropdown, select, [aria-haspopup], [data-toggle], [role="combobox"], [role="listbox"], [role="menu"]'
            );
          if (openable) return; // let it open; stay in pick mode
        }
        ev.preventDefault();
        ev.stopPropagation();
        self.cssEditPick = -1;
        self.selectCssNode(t);
        self._endCssPick();
      };
      document.addEventListener("click", this._cssPickHandler, true);
      // Live hover-inspect: while picking, whatever's under the cursor is
      // highlighted on the page AND its local cluster (the element + its
      // parents / siblings / children) is shown in the panel's "Under cursor"
      // box, so you narrow to + pick the right thing just by hovering. Capture
      // phase + a same-element guard keep it cheap. Only runs in pick mode (which
      // only exists in the "Adjust CSS Styling" focus).
      this._cssHoverMoveHandler = function (ev) {
        var t = ev.target;
        // Over the overlay → FREEZE: cancel any pending cluster update so the rows
        // you're reaching for don't change as the cursor crosses the page.
        if (!t || (t.closest && t.closest(".jcs-debug-overlay"))) {
          clearTimeout(self._cssHoverTimer);
          return;
        }
        if (t === self._cssHoverLast) return; // same element — nothing changed
        self._cssHoverLast = t;
        self._positionCssHoverBox(t); // highlight follows immediately (feedback)
        // DEBOUNCE the panel cluster: commit only once the cursor SETTLES (~140ms)
        // on an element. A quick swipe toward the panel passes over intermediate
        // elements without committing them, so the cluster you paused on (e.g. the
        // open dropdown's menu + values) stays put and its "≣ list/value" rows stay
        // clickable — fixing "the list changes before I can click the row".
        clearTimeout(self._cssHoverTimer);
        self._cssHoverTimer = setTimeout(function () {
          var cluster = self._cssHoverCluster(t);
          self._cssHoverEls = cluster.nodes;
          self.cssHoverList = cluster.items;
        }, 140);
      };
      document.addEventListener("mousemove", this._cssHoverMoveHandler, true);
    },
    _endCssPick() {
      this.cssPickMode = false;
      try {
        document.body.style.cursor = "";
      } catch (e) {
        /* ignore */
      }
      if (this._cssPickHandler) {
        document.removeEventListener("click", this._cssPickHandler, true);
        this._cssPickHandler = null;
      }
      if (this._cssHoverMoveHandler) {
        document.removeEventListener("mousemove", this._cssHoverMoveHandler, true);
        this._cssHoverMoveHandler = null;
      }
      clearTimeout(this._cssHoverTimer);
      this._clearCssHover();
      this._removeCssHoverBox();
    },
    // Build the live hover cluster: the hovered element first, then its parents,
    // prev/next siblings, and direct children — the "narrowed" list under the
    // cursor. Cheap (no getComputedStyle); nodes kept non-reactive in parallel.
    _cssHoverCluster(el) {
      var self = this;
      var items = [];
      var nodes = [];
      function add(node, rel) {
        if (!node || node.nodeType !== 1) return;
        if (node.closest && node.closest(".jcs-debug-overlay")) return;
        var tag = (node.tagName || "").toLowerCase();
        if (["script", "style", "svg", "path", "br", "template", "noscript"].indexOf(tag) >= 0) return;
        var sel = self._cssSelectorFor(node);
        if (!sel) return;
        nodes.push(node);
        items.push({ label: rel + self._friendlyCssLabel(node, sel) });
      }
      add(el, "👁 ");
      // If the hovered element IS or CONTAINS an open menu/listbox, surface its
      // VALUE items right away — so dropdown values are pickable without hovering
      // each row precisely (the #1 pain with Semantic-UI selects: the values live
      // in a .menu inside the box, two levels down). Listed first so they're
      // front-and-center. They all share the same selector (#MachineSelect .item),
      // so picking any one recolors every value.
      var menu =
        el.matches && el.matches('.menu, [role="listbox"], [role="menu"]')
          ? el
          : el.querySelector
          ? el.querySelector('.menu, [role="listbox"], [role="menu"]')
          : null;
      if (menu) {
        if (menu !== el) add(menu, "≣ list ");
        var opts = menu.querySelectorAll('.item, [role="option"], li');
        for (var oi = 0, added = 0; oi < opts.length && added < 6; oi++) {
          add(opts[oi], "≣ value ");
          added++;
        }
      }
      var anc = el.parentElement,
        up = 0;
      while (anc && anc !== document.body && up < 2) {
        add(anc, "↑ ");
        anc = anc.parentElement;
        up++;
      }
      add(el.previousElementSibling, "↔ ");
      add(el.nextElementSibling, "↔ ");
      var kids = el.children ? Array.prototype.slice.call(el.children, 0, 8) : [];
      kids.forEach(function (k) {
        add(k, "↓ ");
      });
      return { items: items, nodes: nodes };
    },
    // Pick an element from the live hover cluster (a row in the "Under cursor"
    // box), then exit pick mode — same as clicking it on the page.
    selectCssHover(hi) {
      var el = (this._cssHoverEls || [])[hi];
      if (el) {
        this.cssEditPick = -1;
        this.selectCssNode(el);
        this._endCssPick();
      }
    },
    _clearCssHover() {
      this.cssHoverList = [];
      this._cssHoverEls = [];
      this._cssHoverLast = null;
    },
    // A pointer-events:none highlight box drawn over the hovered element. Below
    // the overlay (z 2147483646) but above page content, so it never blocks the
    // mousemove / click.
    _ensureCssHoverBox() {
      if (this._cssHoverBox) return this._cssHoverBox;
      var b = document.createElement("div");
      b.id = "jcs-css-hover-box";
      b.style.cssText =
        "position:fixed;pointer-events:none;z-index:2147483640;" +
        "border:2px solid #19ff19;background:rgba(25,255,25,0.12);" +
        "border-radius:2px;display:none;box-sizing:border-box;";
      document.body.appendChild(b);
      this._cssHoverBox = b;
      return b;
    },
    _positionCssHoverBox(el) {
      var b = this._ensureCssHoverBox();
      try {
        var r = el.getBoundingClientRect();
        b.style.top = r.top + "px";
        b.style.left = r.left + "px";
        b.style.width = r.width + "px";
        b.style.height = r.height + "px";
        b.style.display = "block";
      } catch (e) {
        /* ignore */
      }
    },
    _removeCssHoverBox() {
      if (this._cssHoverBox && this._cssHoverBox.parentNode) {
        this._cssHoverBox.parentNode.removeChild(this._cssHoverBox);
      }
      this._cssHoverBox = null;
    },
    // One-click "auto-fix": a deterministic, apply-this-recipe prompt that makes
    // the current page match the chosen baseline using the known jcs-portal-card
    // pattern (same fix we applied to /report-bug). The browser overlay can't
    // edit source, so this puts the recipe on the clipboard for one paste.
    buildAutoFixPrompt() {
      var cur = this.captureLayoutSignature();
      var base = this.debugBaseline || this._bestBaseline(cur);
      var t = this._topicByKey(this.debugTopic);
      // Guard: if the page already has a RECOGNIZED card, a cleared header, and
      // a flush cart, the standard recipe is a no-op (or worse — it double-cards
      // an #ao-table page). Say "already correct" instead of emitting the recipe.
      // (e.g. /account/savedcarts: #ao-table, cart-vs-card=0, is correct AT
      // position:static — that diff is benign for in-component-card pages.)
      var diffPx =
        cur.stackTop != null && cur.contentTop != null
          ? cur.stackTop - cur.contentTop
          : null;
      var recognized =
        cur.cardSel &&
        cur.cardSel !== "(none)" &&
        cur.cardSel.indexOf("#routeView") !== 0;
      if (
        diffPx != null &&
        Math.abs(diffPx) <= 12 &&
        cur.bodyPadTop != null &&
        cur.bodyPadTop >= 100 &&
        recognized
      ) {
        return (
          cur.route + " already looks correct: card=" + cur.cardSel +
          " (a recognized portal card), header cleared (bodyPadTop=" +
          cur.bodyPadTop + "), cart flush (cart-vs-card=" + diffPx +
          "px). No jcs-portal-card fix needed — applying it would stack a " +
          "second card. If a SPECIFIC visual problem remains (e.g. a color), " +
          "describe that instead."
        );
      }
      var notClearing = cur.bodyPadTop != null && cur.bodyPadTop < 100;
      var d =
        cur.stackTop != null && cur.contentTop != null
          ? cur.stackTop - cur.contentTop + "px"
          : "?";
      var L = [];
      L.push(
        "AUTO-FIX: apply our standard portal-page fix so " + cur.route +
          " matches " + base + " — the SAME treatment we applied to /report-bug."
      );
      L.push("");
      L.push("Recipe (see the frontend-portal-card-system memory):");
      var n = 1;
      L.push(
        n++ + '. Add class="jcs-portal-card" to ' + cur.route +
          "'s top section wrapper, with its content in a direct-child <div class=\"container\">. That alone gives the branded red-stripe card AND pins the floating cart flush."
      );
      if (notClearing) {
        L.push(
          n++ + ". This page is NOT clearing the header (bodyPadTop=" +
            cur.bodyPadTop +
            "). It is caught by the body:has(#acctLogin / #requestAcct) zeroing, whose exclusion is :not(.jcs-portal-card) — so step 1 also restores the 245px header clearance. Confirm content top becomes ~245."
        );
      }
      L.push(
        n++ + ". Drop any hero banner / centered narrow column so the content is full-width in the card (as on /report-bug)."
      );
      L.push(n++ + ". Build, then confirm: content top ~245, cart-vs-card ~0, red stripe shows.");
      if (t && t.guide) {
        L.push("");
        L.push("Focus — " + t.label + ": " + t.guide);
      }
      var fcss = this._cssFileByKey(this.debugCssFile);
      if (fcss && fcss.guide) {
        L.push("");
        L.push("Target file — " + fcss.label + ": " + fcss.guide);
      }
      L.push("");
      L.push(
        "Measured now: card=" + cur.cardSel + " top=" + cur.contentTop +
          " pos=" + cur.cardPos + " bodyPadTop=" + cur.bodyPadTop +
          " cart-vs-card=" + d + "."
      );
      return L.join("\n");
    },
    copyAutoFixPrompt() {
      var self = this;
      var text = this.buildAutoFixPrompt();
      var done = function () {
        self.debugFixLabel = "Copied — paste in chat to apply";
        setTimeout(function () {
          self.debugFixLabel = "⚡ Copy auto-fix for this page";
        }, 2200);
      };
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done, function () {
            self._fallbackCopy(text);
            done();
          });
        } else {
          this._fallbackCopy(text);
          done();
        }
      } catch (e) {
        this._fallbackCopy(text);
        done();
      }
    },
    updateAdminState() {
      const matchedRoot =
        this.$route.matched && this.$route.matched.length
          ? this.$route.matched[0]
          : null;

      const isAdminRoute = !!(
        matchedRoot &&
        matchedRoot.props &&
        matchedRoot.props.default &&
        matchedRoot.props.default.admin
      );

      this.admin = isAdminRoute;

      if (this.admin || this.currentUser) {
        document.body.classList.add("dashboard");
      } else {
        document.body.classList.remove("dashboard");
      }
    },
    handlePageRestore() {
      this.reinitializeLayout();
    },
    handleVisibilityRestore() {
      if (document.visibilityState === "visible") {
        this.reinitializeLayout();
      }
    },
    reinitializeLayout() {
      this.$nextTick(() => {
        this.handleResize();
        this.applyStickyClasses();

        setTimeout(() => {
          this.handleResize();
          this.applyStickyClasses();
          window.dispatchEvent(new Event("resize"));
        }, 150);
      });
    },
    applyStickyClasses() {
      // Sales Insights (/reports) hides the fixed breadcrumb bar (see
      // showGlobalBreadcrumbs). The breadcrumb reserved a ~90px band inside the
      // body's padding-top (245px in steele-consistency-fix.css), so without
      // this the band is an empty gap between the nav and the first card. The
      // body.report-route CSS rule (below, desktop-only) pulls content up to the
      // nav bottom. Toggle the class up front so it tracks the route even if the
      // DOM isn't ready for the measurement work further down.
      if (this.isReportRoute) document.body.classList.add("report-route");
      else document.body.classList.remove("report-route");

      const routeElement = document.getElementById("routeView");
      // Bail out if the DOM isn't ready yet — this method can fire from
      // handleResize() which is called during created() before the
      // template renders, in which case routeElement and the rest of
      // the elements we need to read won't exist yet. The next call
      // (from mounted, or from the currentUser/currentPath watchers)
      // will re-run this once the DOM is available.
      if (!routeElement) {
        return;
      }

      const hasGlobalBreadcrumb = document.body.contains(
        document.getElementById("globalBreadcrumbs")
      );
      const hasShoppingPO = document.body.contains(
        document.getElementById("shoppingPO")
      );

      if (this.currentUser || this.admin) {
        document.body.classList.add("dashboard");

        // Measure the sticky header's actual rendered height and set it
        // as body padding-top, so the first visible page element
        // (#globalBreadcrumbs) sits cleanly below the nav. Replaces the
        // hardcoded 210px / 170px values which can drift out of sync as
        // the header changes (extra rows, language picker, etc.) and lets
        // the inline style win against any other CSS attempting to set
        // body padding-top from elsewhere.
        const stickyEl = document.querySelector(".stickyHeader");
        if (stickyEl) {
          // Add a small buffer so content doesn't sit flush against the
          // nav's bottom edge.
          const buffer = 4;
          document.body.style.paddingTop =
            stickyEl.offsetHeight + buffer + "px";
          // Expose the sticky-header height as a CSS var so mobile overlays
          // (cart drop-down, company/account dropdowns, contact modal) anchor
          // their TOP to the bottom of the WHOLE header chrome (cart bar +
          // header + nav) — not a hardcoded offset that drifts when the bar or
          // header height changes (this is what made the dropdowns overlap the
          // header after the cart bar moved to the top).
          document.documentElement.style.setProperty(
            "--jcs-sticky-h",
            stickyEl.offsetHeight + "px"
          );
          // Also expose the BOTTOM of the header block (cart bar + logo/icons
          // row) EXCLUDING the nav — header expansions drop from HERE (the
          // user's "orange line", right below the icons) covering the nav +
          // content, instead of from the full sticky header bottom.
          var hwEl = document.getElementById("header-wrapper");
          if (hwEl) {
            document.documentElement.style.setProperty(
              "--jcs-header-bottom",
              Math.round(hwEl.getBoundingClientRect().bottom) + "px"
            );
          }

          // Pin the floating header search to align vertically with the
           // breadcrumb row. Anchor it relative to the actual breadcrumb
           // element when present so the alignment is exact regardless
           // of breadcrumb row height variations.
          const searchEl = document.getElementById("header-search");
          if (searchEl) {
            const breadcrumbEl = document.getElementById("globalBreadcrumbs");
            if (breadcrumbEl) {
              const bcRect = breadcrumbEl.getBoundingClientRect();
              // Center the search input vertically within the breadcrumb
              // row. Search input is ~40px tall; breadcrumb row varies.
              const searchHeight = searchEl.offsetHeight || 40;
              const top = bcRect.top + (bcRect.height - searchHeight) / 2;
              searchEl.style.top = top + "px";
            } else {
              // Fallback: just past the sticky header.
              searchEl.style.top = stickyEl.offsetHeight + 4 + "px";
            }
          }
          // Observe the sticky header so a SETTLED height change re-applies this
          // measurement. The window-resize path above can read the header
          // mid-reflow (devtools dock/undock, print-dialog close) and catch a
          // too-short height, leaving the breadcrumb + header-search row riding
          // up under the nav and STUCK there (the setTimeout retry is just a
          // guess). ResizeObserver fires post-layout, so the re-apply always
          // uses the stable height. Self-guarded (set up once); the body
          // padding / search top it writes don't change the header's own box,
          // so there's no feedback loop.
          if (!this._stickyHeaderRO && typeof ResizeObserver !== "undefined") {
            var _selfSH = this;
            this._stickyHeaderRO = new ResizeObserver(function () {
              if (_selfSH._stickyHeaderRaf)
                cancelAnimationFrame(_selfSH._stickyHeaderRaf);
              _selfSH._stickyHeaderRaf = requestAnimationFrame(function () {
                _selfSH.applyStickyClasses();
              });
            });
            this._stickyHeaderRO.observe(stickyEl);
          }
        }
      }

      if (hasGlobalBreadcrumb || hasShoppingPO) {
        routeElement.classList.add("sectionView");
      } else {
        routeElement.classList.remove("sectionView");
      }

      if (!hasGlobalBreadcrumb && hasShoppingPO) {
        const element = document.getElementById("shoppingPO");
        element && element.classList.add("removeTopSpace");
      } else {
        const element = document.getElementById("shoppingPO");
        element && element.classList.remove("removeTopSpace");
      }
    },
    handleResize() {
      // Track the collapsed (mobile-nav) state in BOTH directions. This was a
      // one-way latch: it only ever SET collapsed=true at <=1030 and never
      // reset it, so once the viewport went narrow (e.g. docking dev tools)
      // #app kept its `collapsed` class at desktop width — leaving the MOBILE
      // navbar layout applied (10px navbar padding + position:absolute logo/
      // account), which inflated the brushed-steel band from ~42px to ~69px and
      // stuck there until a manual refresh. Found via a console box-model dump:
      // #main-nav pad=10/10 with only the toggler in flow. Now it tracks width
      // both ways so closing dev tools (>1030) restores the desktop nav.
      this.collapsed = window.innerWidth <= 1030;

      // Keep the sticky-mode viewport gate current. Crossing the 992px
      // boundary flips stickyPanelMode, whose watcher re-aligns the stack
      // (fixed+clamped above 992, absolute/scroll-with-page below).
      this.viewportWide = window.innerWidth >= 992;
      this.isMobile = window.innerWidth < 992;

      // Re-measure the sticky header after viewport changes — its row
      // count and total height can change at smaller widths.
      this.applyStickyClasses();

      // A resize fired by closing the print dialog or docking / undocking
      // dev tools arrives mid-reflow, so the synchronous read above can
      // catch a too-short header — leaving the breadcrumb / search row
      // riding up under the nav (and sticking there). Re-measure once
      // layout has settled to snap it back to the correct offset.
      requestAnimationFrame(() => this.applyStickyClasses());
      setTimeout(() => this.applyStickyClasses(), 150);

      // direction: "downward" — keep this in sync with the per-page
      // wizard inits in Orders/Create.vue, RFQ/Create.vue, and Search/Index.vue.
      // Without it, this resize-driven re-init would clobber the
      // direction setting those pages apply, and the Machine /
      // Assembly dropdowns would flip back to opening upward (which
      // clips items above the viewport since the menu can be tall).
      $(".ui.dropdown").dropdown({ fullTextSearch: true, direction: "downward" });

      // Viewport changed → re-align + re-clamp the widget stack.
      this.alignWidgetStack();
    },
    onCollapse(collapsed) {
      this.collapsed = collapsed;
    },
    // The two global widgets report whether their expanded panel is
    // showing. We mirror that into cartExpanded / topExpanded so the
    // widgetsExpanded computed (→ .widgets-expanded class) can reserve
    // right-side room in the page content only while a panel is open.
    onCartExpandedChange(v) {
      this.cartExpanded = !!v;
      // A panel just expanded/collapsed — re-clamp so the (now taller
      // or shorter) stack still stops above the footer.
      this.constrainWidgetStack();
    },
    onTopExpandedChange(v) {
      this.topExpanded = !!v;
      this.constrainWidgetStack();
    },
    // Throttle scroll-driven re-clamps to one per animation frame.
    onWindowScroll() {
      if (this._stackRaf) return;
      this._stackRaf = requestAnimationFrame(() => {
        this._stackRaf = null;
        this.constrainWidgetStack();
      });
    },
    // Align the floating widget stack's TOP with the page's first content
    // card — the card's red top border ("red line") — so the cart panel's
    // red line sits flush with the content's. The stack is position:fixed
    // at a hardcoded top by default, but each page's first card starts at a
    // different Y (the Account dashboard pushes its insights card down below
    // the welcome heading; build pages start near the top), so one fixed
    // value can't line up everywhere. We measure the first red-bordered card
    // inside #routeView and pin the stack to its document-top, falling back
    // to the CSS default if none is found. Runs on mount / route change /
    // user load / resize — NOT on scroll (the anchor is the at-rest
    // position; the fixed stack floats from there as the user scrolls).
    alignWidgetStack() {
      this.$nextTick(() => {
        var stack = this.$el && this.$el.querySelector
          ? this.$el.querySelector(".floating-widget-stack")
          : null;
        if (!stack) return;
        // Clear any prior relative-card pull (applied at the end of this
        // method) so the measurements below read the card's NATURAL layout
        // position. Recomputing the pull from scratch each run keeps it
        // feedback-safe (no drift across resizes / route changes).
        var preCard = document.querySelector(
          "#orderForm.create-order-form, .create-order-form, .create-rfq-form"
        );
        if (preCard && preCard.style && preCard.style.marginTop) {
          preCard.style.marginTop = "";
        }
        var docTop = this._findContentRedLineTop();
        // Dashboard: pin the cart FLUSH with the Insights CARD specifically.
        // The "Welcome Back" heading now lives inside #acctInsights, above the
        // card, so the generic red-line scan can land on the wrapper (leaving
        // the cart too high). Target the rendered insights card directly — it
        // only exists on the dashboard, so this no-ops on every other page.
        // alignWidgetStack runs at rest (mount / route / load / resize), so
        // getBoundingClientRect + scrollY is scroll-independent here.
        var insightsCard = document.querySelector("#acctInsights .insights-container");
        if (insightsCard) {
          var icr = insightsCard.getBoundingClientRect();
          docTop = icr.top + (window.scrollY || window.pageYOffset || 0);
        }
        // Cart / Order Details / Quote Details: pin FLUSH with their content
        // card directly. Their red top line is drawn on a full-height ::before
        // (the shared brand-stripe), and detecting that GENERICALLY in
        // _findContentRedLineTop matched the wrong element on /documents and
        // regressed it. So target the known card explicitly — exactly like the
        // dashboard insights override above. #cartTop / #odTop only exist on
        // those pages, so this no-ops everywhere else. (Any page tagged
        // .jcs-portal-card gets the SAME ::before brand-stripe card in
        // steele-consistency-fix.css and pins flush here too — currently
        // /account/information and /report-bug. It's a generic class, not a
        // bare #requestAcct, since that id is shared with Login/Registration.)
        // Defensive: iterate and take the first VISIBLE match (display:none /
        // unrendered elements report no client rects) so a hidden duplicate is
        // never picked.
        var stripeCard = null;
        var _spCands = document.querySelectorAll(
          "#cartTop > .container, #odTop > .container, .jcs-portal-card > .container"
        );
        for (var _spi = 0; _spi < _spCands.length; _spi++) {
          var _spEl = _spCands[_spi];
          if (
            _spEl.getClientRects().length &&
            _spEl.getBoundingClientRect().height > 0
          ) {
            stripeCard = _spEl;
            break;
          }
        }
        if (stripeCard) {
          var scr = stripeCard.getBoundingClientRect();
          docTop = scr.top + (window.scrollY || window.pageYOffset || 0);
        }
        // Documents (#Documents #ao-table) and Saved Carts (#SavedCarts
        // #ao-table) draw their red top line as a REAL border-top set
        // in-component. In dark / mid mode the theme recolors that border to
        // gray, so the color-based _findContentRedLineTop scan can't see it and
        // the stack drops to the low CSS default — the "cart low on
        // documents/savedcarts, other pages OK" bug. (Every other page is
        // either ::before-striped, which is color-immune, or already has an
        // explicit override like these.) Target the cards by id
        // (color-independent) so they pin flush in EVERY theme. #ao-table is
        // reused across pages, so scope to each page's section id — those only
        // exist on their own page, so this no-ops elsewhere.
        var aoCard = document.querySelector(
          "#Documents #ao-table, #SavedCarts #ao-table"
        );
        if (aoCard) {
          var aor = aoCard.getBoundingClientRect();
          docTop = aor.top + (window.scrollY || window.pageYOffset || 0);
        }
        var off = this.lockedImageOffset || 0;
        // The stack is now position:ABSOLUTE relative to its offset parent
        // (#app, which is position:relative). docTop above is in DOCUMENT
        // space (offsetTop accumulation / getBoundingClientRect+scrollY, both
        // measured from the document origin). Convert document space → offset-
        // parent space by subtracting #app's own document-top, so the stack's
        // `top` lands the cart's red line flush with the content card REGARDLESS
        // of scroll — and the stack then scrolls WITH the page. (When it was
        // position:fixed this code set a document-space value that only matched
        // the viewport because alignWidgetStack runs at rest; absolute makes the
        // alignment scroll-independent and correct.)
        // topVal stays in DOCUMENT space below — it's reused to align the
        // sticky/fixed build card's own `top` (viewport/sticky space), which is
        // unchanged. Only the STACK's top moves to offset-parent space here.
        var appEl = document.getElementById("app");
        var appDocTop = appEl
          ? appEl.getBoundingClientRect().top +
            (window.scrollY || window.pageYOffset || 0)
          : 0;
        // STICKY MODE vs NON-STICKY MODE diverge here on how the stack's `top`
        // is interpreted:
        //
        //  • NON-STICKY (dashboard): stack is position:ABSOLUTE inside #app, so
        //    its `top` is OFFSET-PARENT space (topVal − #app's document-top) and
        //    it scrolls WITH the page — the dashboard behavior we must keep.
        //
        //  • STICKY (build pages): stack is position:FIXED (viewport-pinned).
        //    Its `top` is VIEWPORT space and is SCROLL-INDEPENDENT (the point of
        //    "fixed" — it stays put as the left list scrolls). We pin it just
        //    below the fixed sticky header (headerClear), which equals the
        //    card's red line at rest since these pages' first card starts right
        //    under the breadcrumb. A locked-image offset (off) nudges it down.
        //    constrainWidgetStack() then bounds its height to the viewport and
        //    turns on internal scroll so the WHOLE column (incl. Top Parts)
        //    stays reachable.
        var sticky = this.stickyPanelMode;
        // Viewport top that clears the fixed header. body padding-top already
        // equals the rendered header height + a 4px buffer (set in
        // applyStickyClasses), i.e. exactly where in-flow content — and the
        // relative build card's red top line — begins. Pin the sticky stack to
        // that SAME line (was bodyPad − 8, which floated the panel ~8px ABOVE
        // the card; the /documents reference has them flush) so the panel's red
        // line aligns with the card's instead of sitting a touch high. Fallback
        // 130. The Math.max(96, …) floor below still guarantees it clears the
        // fixed header at narrow widths.
        var headerClear = 130;
        try {
          var bodyPad = parseFloat(
            window.getComputedStyle(document.body).paddingTop
          );
          if (isFinite(bodyPad) && bodyPad > 0) {
            headerClear = Math.round(bodyPad);
          }
        } catch (e) {
          /* fall back to 130 */
        }
        var topVal = null;
        if (sticky) {
          // Fixed → viewport space, scroll-independent. off drops it below a
          // locked zoom image when one is docked above the cart.
          stack.style.top = Math.max(96, headerClear) + off + "px";
          // topVal (document space) is still computed below for the build
          // card's own sticky/fixed top alignment, which is unchanged.
          topVal =
            docTop != null && isFinite(docTop)
              ? Math.max(120, Math.round(docTop)) + off
              : null;
        } else if (docTop != null && isFinite(docTop)) {
          // Don't let it ride up under the sticky header / breadcrumb.
          // + off drops the stack below a locked zoom image when present.
          topVal = Math.max(120, Math.round(docTop)) + off;
          // Offset-parent-relative top for the absolute stack.
          stack.style.top = Math.round(topVal - appDocTop) + "px";
        } else if (off) {
          // No red-line card found but an image is locked → offset from the
          // CSS default top (250px) so the cart still clears the image.
          topVal = 250 + off;
          stack.style.top = Math.round(topVal - appDocTop) + "px";
        } else {
          // No red-line card on this page → revert to the CSS default top.
          stack.style.top = "";
        }
        // Capture this run's computed values for the global debug overlay
        // (admin-toggleable; see buildDebugText + the Account Setup -> Event
        // Logs & History checkbox). Cheap; only repaints the overlay when on.
        this._lastAlignVals = {
          docTop: docTop == null ? null : Math.round(docTop),
          appDocTop: Math.round(appDocTop),
          topVal: topVal == null ? null : Math.round(topVal),
          styleTop: stack.style.top || "(css)",
          sticky: sticky,
          // Scroll position when this align ran. The relative build-card pull is
          // computed in document space so it's scroll-independent; this is here
          // so a capture can still reveal a realign that fired mid-scroll (the
          // old "card dropped after the add-to-cart modal" bug).
          scrollY: Math.round(window.scrollY || window.pageYOffset || 0),
        };
        if (this.debugOverlay) this.refreshDebugOverlay();
        // Pin the content card (the sticky "Search for Part" / order-build
        // card) at the SAME top as the stack so its red top-line stays flush
        // with the cart's and never drifts up while scrolling. The card is
        // position:sticky; this inline top overrides its CSS default so the
        // two red lines always line up. Clear it when the stack reverts to the
        // CSS default so the card's own CSS top applies.
        var routeViewEl = document.getElementById("routeView");
        // appEl already resolved above (for the stack's offset-parent top).
        var card = document.querySelector(
          "#orderForm.create-order-form, .create-order-form, .create-rfq-form"
        );
        var cardFixed =
          card && window.getComputedStyle(card).position === "fixed";
        // The page-fit override (below) only applies to the fixed Search panel;
        // clear it on every other route so they keep their global heights.
        if (!cardFixed) {
          if (routeViewEl && routeViewEl.style.minHeight)
            routeViewEl.style.minHeight = "";
          if (appEl && appEl.style.minHeight) appEl.style.minHeight = "";
        }
        if (card && card.style) {
          // Only sticky/fixed build cards (e.g. Orders/RFQ) align their top to
          // the cart's red line. The Search card is now normal flow
          // (position:relative) so it must carry NO inline top, or it'd be
          // pushed down the page.
          var cardPos = window.getComputedStyle(card).position;
          card.style.top =
            (cardPos === "sticky" || cardPos === "fixed") && topVal != null
              ? topVal + "px"
              : "";
          // (When the Search card is fixed — not the case now — it's out of
          // flow and is sized/placed from JS in the branch below.)
          var col = card.parentElement;
          if (cardFixed && col) {
            var ccs = window.getComputedStyle(col);
            var padL = parseFloat(ccs.paddingLeft) || 0;
            var padR = parseFloat(ccs.paddingRight) || 0;
            var cr = col.getBoundingClientRect();
            var cardLeft = cr.left + padL;
            var w = (col.clientWidth || cr.width) - padL - padR;
            // Don't let the panel run UNDER the floating stack (cart / Top-20 /
            // docked image). When the stack is showing, cap the panel's right
            // edge to the stack's left edge — this is what makes the docked
            // image push the panel narrower (like an expanded widget does)
            // instead of overlapping it. When the stack is hidden (collapsed
            // pills only), sr.width is 0 and the panel keeps full column width.
            if (stack) {
              var sr = stack.getBoundingClientRect();
              if (sr.width > 0 && sr.left > cardLeft + 80) {
                var avail = Math.floor(sr.left - cardLeft - 16);
                if (avail > 0 && avail < w) w = avail;
              }
            }
            if (w > 0) {
              card.style.width = Math.round(w) + "px";
              card.style.left = Math.round(cardLeft) + "px";
            }
            // Make the PAGE fit the viewport. The card is fixed/out-of-flow,
            // so page height is governed by #routeView's global min-height
            // (calc(100vh - X)), which is tens of px TALLER than the viewport —
            // that residual scroll is what drags the footer up over the list.
            // Override #routeView's min-height so the footer lands right at the
            // viewport bottom: no page scroll, so wheel-scrolling scrolls the
            // parts list INSIDE the panel and the footer stays put. (footerH +
            // routeView's doc-top are stable → no feedback with the clamps.)
            col.style.minHeight = "";
            // The PAGE height is governed by #app's min-height (100vh) PLUS its
            // top offset: #app starts ~265px down (the body's padding-top that
            // clears the fixed header) yet is a full 100vh tall, so it overruns
            // the viewport by that offset — and the footer (position:absolute,
            // anchored to #app's bottom) lands below the fold. Earlier attempts
            // on #routeView/#createOrder did nothing because those sit INSIDE
            // #app and don't control its height. Shrink #app's min-height by its
            // own top offset so #app's bottom — and the footer — land at the
            // viewport bottom: the page fits, the footer stays put, and wheel-
            // scrolling scrolls the parts list INSIDE the panel.
            if (routeViewEl && routeViewEl.style.minHeight)
              routeViewEl.style.minHeight = ""; // drop the earlier (no-op) override
            var sectionEl = document.getElementById("createOrder");
            if (sectionEl && sectionEl.style.minHeight)
              sectionEl.style.minHeight = "";
            if (appEl) {
              var appTopDoc =
                appEl.getBoundingClientRect().top + (window.scrollY || 0);
              var appMin =
                Math.round(
                  Math.max(320, (window.innerHeight || 0) - appTopDoc)
                ) + "px";
              if (appEl.style.minHeight !== appMin) {
                appEl.style.minHeight = appMin;
              }
            }
            // Search results load asynchronously (no route change), so the
            // panel grows after alignment ran. Watch it and re-clamp.
            this._observeBuildCard(card);
          } else if (col) {
            // Not a fixed panel → drop any inline width/left/reservation.
            card.style.width = "";
            card.style.left = "";
            col.style.minHeight = "";
            // Relative (normal-flow) build card — e.g. the Search page. The
            // cart is pinned to this card's red-line docTop, but the card
            // RENDERS a few px lower than that computed top, so the cart floats
            // above the content. Measure the live gap and pull the card up with
            // a negative margin-top so its red top line meets the cart's. The
            // margin was reset at the top of this method, so this reads the
            // natural position and recomputes cleanly each run (no drift).
            if (window.getComputedStyle(card).position === "relative") {
              void card.offsetHeight; // settle layout before measuring
              // Pull the card up to the cart STACK's actual rendered top —
              // measured live, NOT from topVal. On pages where the red-line
              // scan finds no card, the stack sits at its CSS default and
              // topVal is null, but we still want the content to rise to meet
              // it. Skip when the stack is collapsed (empty cart → no panel).
              // Target = where the cart's red line actually sits. In STICKY
              // mode the cart is position:fixed at a KNOWN viewport top
              // (Math.max(96, headerClear) + off — exactly what line ~1699 sets
              // on the stack). Pull toward THAT fixed target instead of the live
              // stack rect: the rect can read height:0 / stale while the cart
              // panel re-renders (after the PO#/Quote-Ref modal) or during a
              // resize (devtools toggle), and the old `height > 0` guard then
              // SKIPPED the pull entirely — leaving the card stuck ~50px low
              // until a refresh. headerClear is stable, so the pull is
              // deterministic and re-applies identically on every re-run.
              // (Non-sticky pages keep the live-stack-top behavior.)
              // Measure the card's red line in DOCUMENT space (rect.top +
              // scrollY) and compare to the cart's red line in the SAME space.
              // onCartUpdatedRealign fires AFTER a user action (the add-to-cart
              // modal) and the page is often SCROLLED — the old code compared a
              // viewport rect.top to the target and OMITTED scrollY, so the gap
              // was understated by the scroll amount, the pull was skipped, and
              // the card sat ~50px low until a manual scroll/refresh (the "search
              // fell lower after adding via the modal" bug; worse on smaller
              // screens, where you scroll more to reach a part). It also avoids
              // the live stack rect, which reads height:0 mid re-render right
              // after the modal. headerClear / docTop are stable → deterministic
              // and scroll-independent.
              var cardDocTop =
                card.getBoundingClientRect().top +
                (window.scrollY || window.pageYOffset || 0);
              var cartTopDoc = null;
              if (sticky) {
                // Cart is position:fixed; at rest its viewport top == its doc top.
                cartTopDoc = Math.max(96, headerClear) + off;
              } else if (docTop != null && isFinite(docTop)) {
                // Cart is absolute, pinned to docTop (the content red line).
                cartTopDoc = Math.max(120, Math.round(docTop)) + off;
              }
              if (cartTopDoc != null) {
                var cardGap = Math.round(cardDocTop - cartTopDoc);
                if (cardGap > 4) {
                  card.style.marginTop = -cardGap + "px";
                }
              }
              // Search results render ASYNCHRONOUSLY (machine picked → assembly
              // list fills in) with NO route change, so the card grows AFTER
              // this alignment ran and its natural top shifts — leaving the
              // negative-margin pull computed above stale and the card sitting
              // ~50px low. The fixed-card branch already watches for that async
              // growth (_observeBuildCard, above); mirror it here so the relative
              // card re-runs alignWidgetStack() — which resets + recomputes this
              // margin-pull against the settled layout — keeping the red line
              // flush both BEFORE and AFTER a search. Idempotent (the margin is
              // reset at the top of each run) and the observer ignores our own
              // margin write (margin doesn't change the observed border-box, and
              // the MutationObserver watches childList/subtree, not attributes).
              this._observeBuildCard(card);
            }
          }
        }
        // Re-clamp height against the footer now that the top moved.
        this.constrainWidgetStack();
      });
    },
    // Push the floating stack below a locked zoom image (Search/Orders/RFQ)
    // so the image sits ABOVE the cart with a 12px gap — or flush at the
    // content top when nothing is locked. Driven by the build pages'
    // "locked-image-change" bus event (payload: { active, height }).
    onLockedImageChange(payload) {
      // The locked image docks INSIDE the stack now (no push-down offset);
      // this flag is a fallback that keeps the stack visible while docked —
      // the primary driver is the :has() CSS rule on .floating-widget-stack.
      this.lockedImagePanelActive = !!(payload && payload.active);
      this.lockedImageOffset = 0;
      this.alignWidgetStack();
    },
    // Debounced re-align after a cart change (see the $on in mounted). Coalesces
    // rapid cart-updated emits, then re-runs alignWidgetStack — which resets +
    // recomputes the relative build card's margin-pull against the settled
    // layout, so the card's red line stays flush with the cart without a manual
    // page refresh.
    onCartUpdatedRealign() {
      clearTimeout(this._cartAlignTimer);
      this._cartAlignTimer = setTimeout(() => {
        this.alignWidgetStack();
      }, 120);
    },
    // Document-space top of the first red-top-bordered card inside the route
    // content (#routeView). Brand red is ~rgb(192,57,43); match any element
    // whose computed top border is >=2.5px and reddish. The cart/Top 5/Info
    // panels also have red top borders but live OUTSIDE #routeView (in the
    // floating stack / pill rail), so they're never matched here. Adding
    // scrollY makes the result scroll-independent.
    _findContentRedLineTop() {
      try {
        var rv = document.getElementById("routeView");
        if (!rv) return null;
        var els = rv.querySelectorAll("div, section, form, ul, table");
        var isReddish = function (str) {
          var mm = (str || "").match(/(\d+),\s*(\d+),\s*(\d+)/);
          return mm && +mm[1] > 140 && +mm[2] < 100 && +mm[3] < 100;
        };
        // Natural (layout) document-top via offsetTop accumulation. This is
        // STABLE regardless of scroll OR position:sticky — getBoundingClientRect
        // returns the element's *stuck* viewport position once the card pins,
        // which then fed back into the stack top and made it jump. offsetTop
        // always reflects the element's normal-flow position.
        var docTopOf = function (el) {
          var y = 0;
          var n = el;
          while (n) {
            y += n.offsetTop || 0;
            n = n.offsetParent;
          }
          return y;
        };
        for (var i = 0; i < els.length; i++) {
          var el = els[i];
          if (!el.getClientRects().length) continue; // hidden / zero-box
          var cs = window.getComputedStyle(el);
          // (a) real red top BORDER (e.g. .insights-panel, most cards).
          if ((parseFloat(cs.borderTopWidth) || 0) >= 2.5 &&
              isReddish(cs.borderTopColor)) {
            return docTopOf(el);
          }
          // (b) red ::before. TWO shapes both live in the shared CSS:
          //   (b1) a thin reddish ACCENT BAR — e.g. the Create New Order form
          //        (.create-order-form) draws its stripe with a short ::before
          //        pinned to the top whose BACKGROUND is red. Matched by a thin
          //        (<=8px) reddish background pseudo-element.
          //   (b2) the UNIFORM PAGE-TOP BRAND STRIPE card — Cart (#cart
          //        #cartTop > .container), Order/Quote Details (#orderDetails
          //        #odTop > .container), Order Success / Finalize. There the
          //        whole card visual (white bg + 1px border + 3px red top) is
          //        drawn on a FULL-HEIGHT ::before (top:0;bottom:0) so it can't
          //        bleed across the widget reserve. Its red lives on the
          //        ::before's BORDER-TOP-COLOR, not its background — so (b1)
          //        (background test) and (a) (the HOST element's own border,
          //        which is border:0 here) both miss it, and these three pages
          //        fell back to the low CSS-default top (the reported bug).
          //        Detect a reddish ::before border-top (>=2.5px) and pin to
          //        the host's top — matching how branch (a) reads a real
          //        red border on the working /documents card (#ao-table).
          var bef = window.getComputedStyle(el, "::before");
          if (bef && bef.content && bef.content !== "none") {
            var bh = parseFloat(bef.height) || 0;
            if (bh > 0 && bh <= 8 && isReddish(bef.backgroundColor)) {
              return docTopOf(el); // (b1) thin red accent bar
            }
            // NOTE: do NOT re-add a generic reddish ::before-border-top branch
            // here. The shared brand-stripe card (Cart / Order & Quote Details /
            // Success / Finalize) draws its red on a full-height ::before
            // border-top, but detecting that generically matched the WRONG
            // element on /documents and regressed it. Those pages are aligned
            // EXPLICITLY in alignWidgetStack (the #cartTop / #odTop override).
          }
        }
      } catch (e) {
        /* defensive — any measurement failure falls back to CSS default */
      }
      return null;
    },
    // Height behavior of the floating widget stack depends on the mode:
    //
    //  • NON-STICKY (dashboard): the stack is position:ABSOLUTE and SCROLLS
    //    WITH THE PAGE. It scrolls away from the fixed footer naturally, so no
    //    footer-overlap clamp is wanted — and a viewport-based max-height would
    //    WRONGLY clip the now-inline panels (the user scrolls the PAGE to see
    //    the full cart). So we force max-height:none / overflow:visible.
    //
    //  • STICKY (build pages): the stack is position:FIXED (viewport-pinned).
    //    Here we BOUND its height to the viewport — max-height = viewportH −
    //    stackTop − bottom gutter — and turn on overflow-y:auto so the WHOLE
    //    column (locked image + cart + Top Parts) scrolls as ONE internally-
    //    scrollable area and Top Parts is always reachable, regardless of how
    //    tall/short the left results list is. Recomputed on scroll/resize so it
    //    tracks the live viewport (the per-widget inner caps are disabled via
    //    the .stack-sticky CSS so there's a single scroll region, not nested).
    //
    // Also (always) re-clamps the SEPARATE fixed Search-panel results list if
    // that card is ever position:fixed (currently it's normal-flow, so this is
    // dormant) — that card is unrelated to the stack.
    constrainWidgetStack() {
      this.$nextTick(() => {
        var stack = this.$el && this.$el.querySelector
          ? this.$el.querySelector(".floating-widget-stack")
          : null;
        if (!stack) return;

        var viewportH = window.innerHeight || 0;
        var sticky = this.stickyPanelMode;

        // ===== STACK HEIGHT CLAMP =====
        if (sticky) {
          // Viewport-bounded, internally scrollable. The stack is fixed, so
          // its current viewport top is read straight off getBoundingClientRect
          // (= the `top` alignWidgetStack set). Leave ~18px below the stack so
          // it never runs flush to the window's bottom edge / under the footer.
          var bottomGutter = 18;
          var stackTop = stack.getBoundingClientRect().top;
          var avail = Math.max(160, Math.round(viewportH - stackTop - bottomGutter));
          var maxH = avail + "px";
          if (stack.style.maxHeight !== maxH) stack.style.maxHeight = maxH;
          if (stack.style.overflowY !== "auto") stack.style.overflowY = "auto";
        } else {
          // Scroll-with-page: full natural height, page scrolls.
          if (stack.style.maxHeight !== "none") stack.style.maxHeight = "none";
          if (stack.style.overflowY && stack.style.overflowY !== "visible") {
            stack.style.overflowY = "visible";
          }
        }

        var gap = 16;
        // bottomBoundary is still used by the fixed Search-panel list clamp
        // below (the list scrolls inside that fixed card, against the footer /
        // viewport bottom). It is NO LONGER applied to the floating stack.
        var bottomBoundary = viewportH - gap;

        // Search's content card is a FIXED panel — cap its results list so the
        // panel's bottom never crosses the footer; the list scrolls inside.
        // bottomBoundary (footer top or viewport bottom, minus gap) is the same
        // limit the widget stack uses above, so the panel and the cart stop at
        // the same line. listTop is stable (the card is fixed), so this only
        // shrinks the list as the footer scrolls up into view.
        var fixedCard = document.querySelector("#orderForm.create-order-form");
        if (fixedCard && window.getComputedStyle(fixedCard).position === "fixed") {
          // The header (title / Completed Steps / dropdowns) stays static — the
          // panel itself does NOT scroll — and ONLY the results list scrolls.
          // Clamp the list to fill from its top down to just above the footer.
          // The list clamp also bounds the panel's bottom (the list is the last
          // element), so no max-height is needed on the panel itself (which
          // would clip the dropdown menus). The MutationObserver added in
          // _observeBuildCard re-runs this when the list renders.
          if (fixedCard.style.maxHeight) fixedCard.style.maxHeight = "";
          var wlist = fixedCard.querySelector(".inline-wear-list");
          if (wlist) {
            var wlistTop = wlist.getBoundingClientRect().top;
            var wlistH = Math.max(120, bottomBoundary - wlistTop);
            var wmax = Math.round(wlistH) + "px";
            if (wlist.style.maxHeight !== wmax) wlist.style.maxHeight = wmax;
          }
        }
      });
    },
    // Watch a build card (Search / Orders / RFQ "Find Your Part" card) for
    // size / content changes — results loading after a machine is picked, a
    // sub-assembly accordion expanding, the diagram image decoding — and
    // re-run the right realignment. These all happen ASYNCHRONOUSLY with NO
    // route change, so nothing else re-runs alignment after the card grows.
    //
    // The reaction depends on the card's CURRENT position (read at fire-time,
    // not capture-time):
    //   • FIXED card (the dormant fixed Search-panel variant): only the inner
    //     results list needs re-clamping against the footer → onWindowScroll()
    //     (rAF-throttled constrainWidgetStack). Top/width are viewport-stable.
    //   • RELATIVE card (the live Search / Orders / RFQ layout): its natural
    //     top SHIFTS as it grows, so the negative-margin pull computed in
    //     alignWidgetStack() goes stale → re-run alignWidgetStack(), which
    //     resets + recomputes that pull against the settled layout. Throttled
    //     to one run per animation frame via _alignRaf so a burst of result
    //     mutations collapses into a single realign and can't feedback-loop.
    //
    // Feedback safety: the RELATIVE re-run writes card.style.marginTop, but
    // (a) margin lives OUTSIDE the border-box the ResizeObserver watches, so a
    // margin write doesn't re-fire it, and (b) the MutationObserver watches
    // childList + subtree only (NOT attributes), so our own style writes —
    // marginTop here, max-height in the fixed clamp — never re-trigger it.
    _observeBuildCard(card) {
      if (this._buildCardObserved === card) return;
      this._buildCardObserved = card;
      var react = function () {
        // Re-read position each time: a card stays one mode per page, but this
        // keeps the dispatch correct without recapturing.
        var pos = "";
        try {
          pos = window.getComputedStyle(card).position;
        } catch (e) {
          /* card detached — observers are torn down on route change/destroy */
        }
        if (pos === "relative") {
          // Fire BOTH a per-frame realign AND a debounced one:
          //  • _scheduleAlign (rAF) keeps the just-fixed after-search alignment
          //    snappy — the card rises to meet the cart on the next frame as
          //    results render.
          //  • _scheduleAlignDebounced re-runs alignWidgetStack ~150ms AFTER the
          //    LAST mutation settles. Opening the "Choose an Assembly" dropdown
          //    fires a BURST of mutations/resizes over several frames (the tall
          //    assembly list renders, the part-diagram image decodes); the rAF
          //    pass therefore lands MID-TRANSITION and measures cardGap against a
          //    half-settled layout, computing a too-small negative-margin pull —
          //    so the left card settles ~80px LOW and, with no further mutations,
          //    is never re-corrected. The trailing debounced pass measures the
          //    SETTLED layout once the burst ends and recomputes the pull so the
          //    card's red line lands level with the cart and STAYS there while the
          //    dropdown is open and after an assembly is picked. (alignWidgetStack
          //    resets the margin at the top of each run, so the extra pass is
          //    idempotent — it can only converge, never stack.)
          this._scheduleAlign();
          this._scheduleAlignDebounced();
        } else {
          this.onWindowScroll();
        }
      }.bind(this);
      // ResizeObserver catches SIZE changes (e.g. the diagram image loading,
      // the results list filling in and growing the card's height).
      if (typeof ResizeObserver !== "undefined") {
        if (this._buildCardRO) this._buildCardRO.disconnect();
        this._buildCardRO = new ResizeObserver(react);
        this._buildCardRO.observe(card);
      }
      // MutationObserver catches DOM changes the ResizeObserver misses — most
      // importantly the RESULTS LIST rendering after a machine is picked (no
      // route change, and the box may not change enough to trip the
      // ResizeObserver). childList + subtree only (NOT attributes), so our own
      // marginTop / max-height writes don't re-trigger it.
      if (typeof MutationObserver !== "undefined") {
        if (this._buildCardMO) this._buildCardMO.disconnect();
        this._buildCardMO = new MutationObserver(react);
        this._buildCardMO.observe(card, { childList: true, subtree: true });
      }
    },
    // rAF-throttle for the relative build card's realign (mirrors
    // onWindowScroll's guard). A burst of result-render mutations collapses
    // into a single alignWidgetStack() run on the next frame, so the
    // margin-pull is recomputed once against the settled layout — no loop.
    _scheduleAlign() {
      if (this._alignRaf) return;
      this._alignRaf = requestAnimationFrame(() => {
        this._alignRaf = null;
        this.alignWidgetStack();
      });
    },
    // DEBOUNCED realign — runs alignWidgetStack ONCE, ~150ms after the LAST
    // observer mutation, so it measures the SETTLED layout rather than a
    // mid-transition frame. Each new mutation resets the timer, so a burst
    // (e.g. opening the assembly dropdown: list renders + diagram image
    // decodes over several frames) collapses into a single trailing realign
    // after the dust settles. This is the pass that lands the relative Search
    // card's red line level with the cart and KEEPS it there once the dropdown
    // is open / an assembly is selected — the per-frame _scheduleAlign above
    // can measure cardGap too early and under-pull. 150ms is long enough to
    // clear a typical dropdown-open reflow + image decode, short enough to be
    // imperceptible. Cleared in _disconnectBuildCard (route change / destroy).
    _scheduleAlignDebounced() {
      if (this._alignDebounce) {
        clearTimeout(this._alignDebounce);
      }
      this._alignDebounce = setTimeout(() => {
        this._alignDebounce = null;
        this.alignWidgetStack();
      }, 150);
    },
    // Tear down the build-card observers (route change / destroy) so a removed
    // card's observers don't linger. The :key on <router-view> swaps the card
    // element on navigation; clearing the cached ref also lets the next page's
    // card re-attach cleanly.
    _disconnectBuildCard() {
      if (this._buildCardRO) {
        this._buildCardRO.disconnect();
        this._buildCardRO = null;
      }
      if (this._buildCardMO) {
        this._buildCardMO.disconnect();
        this._buildCardMO = null;
      }
      this._buildCardObserved = null;
      if (this._alignRaf) {
        cancelAnimationFrame(this._alignRaf);
        this._alignRaf = null;
      }
      // Cancel any pending trailing debounced realign so it doesn't fire after
      // the card's observers are gone (route change / destroy) — it would
      // measure a detached / replaced card.
      if (this._alignDebounce) {
        clearTimeout(this._alignDebounce);
        this._alignDebounce = null;
      }
    },
    close(name) {
      this.$refs[name].$emit("close");
      setTimeout(() => {
        this.error = "";
      }, 500);
    },
  },
};
</script>

<style>
/* ===== GLOBAL DEBUG OVERLAY (admin-toggleable; contents from buildDebugText) ===== */
.jcs-debug-overlay {
  position: fixed;
  top: 54px;
  left: 8px;
  z-index: 2147483646;
  width: 460px;
  max-width: 62vw;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  background: #000;
  font: 11px/1.4 "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  border: 1px solid #19ff19;
  border-radius: 5px;
  pointer-events: auto;
}
/* Uniform bright green for ALL overlay text — no dim, gray, or black anywhere.
   State (selected baseline, hover) is shown by BACKGROUND + the inline ✓ / ·
   marks, never by changing the text color. Opaque #000 backgrounds keep the
   page from bleeding through and washing the text out. */
.jcs-debug-overlay,
.jcs-debug-overlay * {
  color: #19ff19 !important;
}
.jcs-dbg-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border-bottom: 1px solid #19ff19;
  background: #000;
  cursor: move;
  user-select: none;
  flex: 0 0 auto; /* title bar stays pinned; the body below scrolls */
}
/* The collapsible body is the scroll region: the overlay is a flex column
   capped at max-height (86vh), the bar is fixed-height, so this flexes to fill
   and scrolls when the content (esp. the CSS color editor) runs tall. min-height:0
   is required for a flex child to shrink below its content and actually scroll. */
.jcs-dbg-collapsible {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}
.jcs-dbg-title {
  flex: 1 1 auto;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.jcs-dbg-btn {
  flex: 0 0 auto;
  background: #021;
  border: 1px solid #19ff19;
  border-radius: 3px;
  font: inherit;
  cursor: pointer;
  padding: 2px 6px;
}
.jcs-dbg-btn:hover {
  background: #053;
}
.jcs-dbg-theme {
  padding: 3px 6px;
  border-bottom: 1px solid #19ff19;
  background: #000;
  white-space: pre-wrap;
  word-break: break-all;
}
.jcs-dbg-body {
  margin: 0;
  padding: 6px;
  white-space: pre-wrap;
  overflow: auto;
  max-height: 32vh;
  background: #000;
}
.jcs-dbg-audio {
  border: 1px solid #2e7d32;
  color: #9be29b;
  margin-bottom: 6px;
  max-height: 24vh;
}
.jcs-dbg-compare {
  border-top: 1px solid #19ff19;
  padding: 5px 6px;
  background: #000;
}
.jcs-dbg-compare-label {
  margin-bottom: 3px;
}
.jcs-dbg-routes {
  max-height: 16vh;
  overflow: auto;
  border: 1px solid #0a5;
  border-radius: 3px;
  background: #000;
}
.jcs-dbg-route {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 1px 4px;
  cursor: pointer;
  background: #000;
}
.jcs-dbg-route:hover {
  background: #042;
}
.jcs-dbg-route.is-baseline {
  background: #064;
  font-weight: 700;
}
.jcs-dbg-route input {
  margin: 0;
}
.jcs-dbg-route-mark {
  flex: 0 0 auto;
  width: 10px;
  text-align: center;
}
.jcs-dbg-route-path {
  white-space: nowrap;
}
.jcs-dbg-route-box {
  flex: 0 0 auto;
  width: 12px;
  height: 12px;
  border: 1px solid #19ff19;
  border-radius: 2px;
  text-align: center;
  line-height: 11px;
  font-size: 11px;
}
.jcs-dbg-topic {
  border-top: 1px solid #19ff19;
  padding: 5px 6px;
  background: #000;
}
.jcs-dbg-select {
  width: 100%;
  background: #021;
  border: 1px solid #19ff19;
  border-radius: 3px;
  font: inherit;
  padding: 2px 4px;
}
.jcs-dbg-mt {
  margin-top: 5px;
}
.jcs-dbg-search {
  width: 100%;
  margin-bottom: 4px;
  background: #021;
  border: 1px solid #19ff19;
  border-radius: 3px;
  font: inherit;
  padding: 2px 6px;
}
.jcs-dbg-search::placeholder {
  color: #0a7;
}
.jcs-dbg-fixbtn {
  width: 100%;
  margin-top: 6px;
  background: #063;
  border: 1px solid #19ff19;
  border-radius: 3px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  padding: 4px 6px;
}
.jcs-dbg-fixbtn:hover {
  background: #085;
}
.jcs-dbg-previewbtn {
  width: 100%;
  margin-top: 6px;
  background: #024;
  border: 1px solid #19ff19;
  border-radius: 3px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  padding: 4px 6px;
}
.jcs-dbg-previewbtn:hover {
  background: #046;
}
.jcs-dbg-cssedit {
  border-top: 1px dashed #19ff19;
  margin-top: 6px;
  padding-top: 6px;
}
.jcs-dbg-rescan {
  margin-top: 4px;
}
.jcs-dbg-cssrow {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 5px;
}
.jcs-dbg-cssrow label {
  width: 32px;
  margin: 0;
  font-weight: 700;
}
.jcs-dbg-cssrow input[type="color"] {
  width: 34px;
  height: 22px;
  padding: 0;
  border: 1px solid #19ff19;
  background: #000;
  cursor: pointer;
  flex: 0 0 auto;
}
.jcs-dbg-hex {
  flex: 1 1 auto;
  min-width: 0;
  background: #001100;
  color: #19ff19;
  border: 1px solid #19ff19;
  border-radius: 3px;
  font: inherit;
  padding: 2px 4px;
}
.jcs-dbg-csslist {
  max-height: 170px;
  overflow-y: auto;
  border: 1px solid #0a5d0a;
  border-radius: 3px;
  margin-top: 4px;
  background: #000;
}
.jcs-dbg-cssitem {
  padding: 2px 5px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-bottom: 1px solid #061a06;
}
.jcs-dbg-cssitem:hover {
  background: #04340a;
}
.jcs-dbg-cssitem.is-sel {
  background: #06520f;
  font-weight: 700;
}
.jcs-dbg-cssmore {
  padding: 3px 5px;
  opacity: 0.7;
  font-style: italic;
}
.jcs-dbg-neighbors {
  border-top: 1px dashed #0a5d0a;
  margin-top: 6px;
  padding-top: 4px;
}
.jcs-dbg-neighbor {
  border: 1px solid #063;
  border-radius: 3px;
  margin-top: 3px;
  opacity: 0.95;
}
.jcs-dbg-palette-label {
  margin-top: 6px;
  opacity: 0.85;
}
.jcs-dbg-tgt {
  background: #012;
  color: #19ff19;
  border: 1px solid #0a5d0a;
  border-radius: 3px;
  font: inherit;
  cursor: pointer;
  padding: 0 6px;
  margin-left: 4px;
}
.jcs-dbg-tgt.on {
  background: #06520f;
  border-color: #19ff19;
  font-weight: 700;
}
.jcs-dbg-coverwarn {
  margin-top: 6px;
  padding: 4px 6px;
  border: 1px solid #e2231a;
  border-radius: 3px;
  background: #1a0a00;
  line-height: 1.6;
}
/* ===== Mobile preview (3 phone frames, right of the debug console) ===== */
.jcs-mobile-preview {
  position: fixed;
  z-index: 2147483645;
  background: #000;
  border: 1px solid #19ff19;
  border-radius: 5px;
  padding: 6px;
  max-width: calc(100vw - 16px);
  font: 11px/1.4 "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  color: #19ff19;
  pointer-events: auto;
}
.jcs-mp-bar {
  display: flex;
  align-items: center;
  gap: 6px;
}
.jcs-mp-title {
  font-weight: 700;
}
.jcs-mp-device {
  flex: 0 1 auto;
}
.jcs-mp-note {
  opacity: 0.8;
  margin: 4px 0 6px;
  max-width: 760px;
}
.jcs-mp-frames {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  overflow-x: auto;
  padding-bottom: 6px;
}
.jcs-mp-phone {
  flex: 0 0 auto;
  border: 2px solid #2a2a2a;
  border-radius: 14px;
  overflow: hidden;
  background: #111;
}
.jcs-mp-label {
  text-align: center;
  font-weight: 700;
  padding: 3px 0;
  background: #06210a;
}
.jcs-mp-iframe {
  display: block;
  border: 0;
  background: #fff;
}
/* ===== Layout composer ===== */
.jcs-composer {
  position: fixed;
  z-index: 2147483645;
  background: #000;
  border: 1px solid #19ff19;
  border-radius: 5px;
  padding: 6px;
  width: 720px;
  max-width: calc(100vw - 16px);
  max-height: 86vh;
  overflow-y: auto;
  font: 11px/1.4 "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  color: #19ff19;
  pointer-events: auto;
}
/* ===== Layout composer — live mobile phone (real site iframe) ===== */
.jcs-composer-phone {
  position: fixed;
  z-index: 2147483645;
  background: #000;
  border: 1px solid #19ff19;
  border-radius: 5px;
  padding: 6px;
  max-width: calc(100vw - 16px);
  max-height: 92vh;
  overflow: auto;
  font: 11px/1.4 "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  color: #19ff19;
  pointer-events: auto;
}
.jcs-cv-phone {
  margin-top: 6px;
  padding: 10px;
  border-radius: 22px;
  background: #1c1f24;
  display: inline-block;
}
.jcs-cv-phone-page {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  overflow-y: auto;
  overflow-x: hidden;
  border: 0;
  border-radius: 12px;
  background: #fff;
}
.jcs-cv-phone-blank {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 16px;
  color: #c2c7cc;
  font-weight: 700;
  text-align: center;
}
.jcs-cv-browser {
  flex: 1 1 auto;
  min-width: 0;
  border: 1px solid #2a2a2a;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}
.jcs-cv-chrome {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 8px;
  background: #e6e8eb;
  border-bottom: 1px solid #cfd3d7;
}
.jcs-cv-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #c5c9ce;
  display: inline-block;
}
.jcs-cv-url {
  flex: 1 1 auto;
  margin-left: 6px;
  background: #fff;
  border: 1px solid #cfd3d7;
  border-radius: 10px;
  padding: 2px 9px;
  color: #444;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.jcs-cv-page {
  position: relative;
  min-height: 360px;
  padding: 10px;
  background: #fff;
}
.jcs-cv-stack {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
}
.jcs-cv-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}
.jcs-cv-col {
  flex: 1 1 0;
  min-width: 0;
  min-height: 320px;
  border: 1px dashed #cfd3d7;
  border-radius: 4px;
  padding: 6px;
}
.jcs-cv-col-title {
  text-transform: uppercase;
  font-weight: 700;
  text-align: center;
  color: #9aa0a6;
  font-size: 10px;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
}
.jcs-cv-viewport {
  overflow: auto;
  max-height: 46vh;
  background: #fff;
}
.jcs-cv-mock {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 8px;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: grab;
}
.jcs-cv-mock:active {
  cursor: grabbing;
}
.jcs-cv-mock:hover {
  border-color: #b8c4d2;
}
.jcs-cv-sel {
  outline: 2px solid #2b86ff;
  outline-offset: 1px;
}
.jcs-cv-mock-inner {
  pointer-events: none;
}
.jcs-cv-resize {
  position: absolute;
  right: 1px;
  bottom: 1px;
  z-index: 3;
  width: 14px;
  height: 14px;
  cursor: nwse-resize;
  background: linear-gradient(135deg, transparent 45%, #2b86ff 45%);
  border-bottom-right-radius: 6px;
  display: none;
}
.jcs-cv-mock:hover .jcs-cv-resize,
.jcs-cv-sel .jcs-cv-resize {
  display: block;
}
.jcs-cv-mock-tools {
  position: absolute;
  top: -8px;
  right: -6px;
  z-index: 2;
  display: none;
  gap: 2px;
}
.jcs-cv-mock:hover .jcs-cv-mock-tools,
.jcs-cv-sel .jcs-cv-mock-tools {
  display: flex;
}
.jcs-cv-mock-tools button {
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #2a2f36;
  color: #fff;
  font-weight: 700;
  line-height: 16px;
  cursor: pointer;
}
.jcs-cv-mock-tools button:last-child {
  background: #c8302a;
}
.jcs-cv-editor {
  margin-top: 6px;
  border: 1px solid #0a5d0a;
  border-radius: 4px;
  padding: 6px;
}
.jcs-cv-editor-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 700;
  margin-bottom: 6px;
}
.jcs-cv-editor-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}
.jcs-cv-editor-grid label {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 10px;
  opacity: 0.85;
}
.jcs-cv-editor-grid input,
.jcs-cv-editor-grid select {
  background: #0a0f0a;
  color: #19ff19;
  border: 1px solid #0a5d0a;
  border-radius: 3px;
  padding: 2px 4px;
  font: inherit;
}
.jcs-cv-editor-grid input[type="color"] {
  padding: 0;
  height: 22px;
}
.jcs-cv-drop {
  text-align: center;
  color: #b6bbc0;
  font-style: italic;
  padding: 14px 0;
}
.jcs-cv-blank {
  position: absolute;
  inset: 44px 10px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c2c7cc;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.04em;
  pointer-events: none;
}
.jcs-comp-body {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
.jcs-comp-palette {
  flex: 0 0 156px;
  border: 1px solid #0a5d0a;
  border-radius: 3px;
  padding: 4px;
  max-height: 340px;
  overflow-y: auto;
}
.jcs-comp-palette-title {
  font-weight: 700;
  margin-bottom: 4px;
}
.jcs-comp-chip {
  border: 1px solid #19ff19;
  border-radius: 3px;
  padding: 3px 5px;
  margin-bottom: 4px;
  cursor: grab;
  background: #021;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.jcs-comp-chip:active {
  cursor: grabbing;
}
.jcs-comp-zones {
  flex: 1 1 auto;
  display: flex;
  gap: 6px;
}
.jcs-comp-zone {
  flex: 1 1 0;
  min-width: 0;
  border: 1px dashed #0a5d0a;
  border-radius: 3px;
  padding: 4px;
  min-height: 130px;
}
.jcs-comp-zone-title {
  text-transform: uppercase;
  font-weight: 700;
  text-align: center;
  opacity: 0.8;
  margin-bottom: 4px;
}
.jcs-comp-placed {
  display: flex;
  align-items: center;
  gap: 2px;
  border: 1px solid #063;
  border-radius: 3px;
  padding: 2px 3px;
  margin-bottom: 3px;
  background: #04340a;
}
.jcs-comp-placed-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.jcs-comp-placed button,
.jcs-comp-version button {
  background: #012;
  border: 1px solid #0a5d0a;
  color: #19ff19;
  cursor: pointer;
  border-radius: 2px;
  padding: 0 3px;
  font: inherit;
}
.jcs-comp-empty {
  opacity: 0.5;
  text-align: center;
  padding: 8px 0;
}
.jcs-comp-save {
  display: flex;
  gap: 6px;
  margin-top: 6px;
}
.jcs-comp-versions {
  margin-top: 6px;
  border-top: 1px dashed #0a5d0a;
  padding-top: 4px;
}
.jcs-comp-version {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 0;
}
.jcs-comp-version-name {
  flex: 1 1 auto;
  min-width: 0;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.jcs-comp-version-name:hover {
  text-decoration: underline;
}
.jcs-dbg-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.jcs-dbg-swatch {
  width: 22px;
  height: 22px;
  border: 1px solid #19ff19;
  border-radius: 3px;
  cursor: pointer;
  padding: 0;
}
.jcs-dbg-swatch:hover {
  outline: 2px solid #19ff19;
  outline-offset: 1px;
}
.jcs-dbg-hoverbox {
  border: 1px solid #19ff19;
  border-radius: 3px;
  margin-top: 4px;
  padding: 3px;
  background: #001400;
  max-height: 150px;
  overflow-y: auto;
}
.jcs-dbg-hoveritem.is-hover {
  background: #06520f;
  font-weight: 700;
}
.jcs-dbg-prompt-wrap {
  border-top: 1px solid #19ff19;
  padding: 5px 6px;
  background: #000;
}
.jcs-dbg-prompt {
  margin: 0;
  padding: 5px;
  white-space: pre-wrap;
  overflow: auto;
  max-height: 24vh;
  background: #001a00;
  border: 1px solid #0a5;
  border-radius: 3px;
}
/* The composer's prompt — brighter + always a visible box so it's clearly live. */
.jcs-cv-prompt {
  color: #c8ffc8;
  background: #00220a;
  min-height: 54px;
}
@media print {
  .jcs-debug-overlay { display: none !important; }
}

/* App.vue — minimal global layout styles only.

   IMPORTANT: All breadcrumb sizing/positioning, body top-padding,
   and side rails now live in `steele-consistency-fix.css`. Do not
   re-add `#globalBreadcrumbs` rules or `.dashboard { padding-top }`
   rules here — they will fight the global stylesheet. The relevant
   sections in steele-consistency-fix.css are:
     - section 2  body padding-top (clears sticky header + breadcrumb)
     - section 8  #globalBreadcrumbs (position:fixed, etc.)
     - section 16 side rails
     - section 9  font-size toggle styling

   The JS below in applyStickyClasses() still sets body.style.paddingTop
   and #header-search top inline as a safety net for cases where the
   global CSS hasn't loaded yet, but those values are normally beaten
   by the `!important` rules in steele-consistency-fix.css. */

.dashboard #app {
  overflow: initial;
}

.stickyHeader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background: #fff;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* PORTAL (non-admin) header width.
   The sticky header band used to span the full viewport edge-to-edge —
   a holdover from the admin console layout (#app.login keeps that). On
   the customer portal (#app.fe) we constrain it to the SAME centered
   width as the page content's Bootstrap .container, so the brushed-metal
   page edges show beside the header too instead of a full-bleed bar.
   Centered via fixed + left/right:0 + margin auto + a max-width that
   mirrors Bootstrap's container breakpoints. Applies in light AND dark
   mode. Admin is intentionally untouched. */
#app.fe .stickyHeader {
  right: 0;
  margin-left: auto;
  margin-right: auto;
  /* Match the white "sheet" stripe exactly. The body's center-stripe
     gradient (steele-consistency-fix.css) is 2 x --steele-rail-half-width
     wide and that variable is updated per breakpoint, so reusing it here
     keeps the header band lined up with the content sheet AND the metal
     rails at every width. On mobile the var is 100vw → effectively
     full-width (capped by width:100%). */
  max-width: calc(var(--steele-rail-half-width, 274px) * 2);
}

/* Hide legacy in-template page-level breadcrumbs. The global
   #globalBreadcrumbs (rendered by Breadcrumbs.vue inside #routeView)
   is the single source of truth now. */
#routeView #breadcrumb {
  display: none !important;
}

/* Route content area. Min-height keeps short pages from collapsing
   above the footer. The body padding-top set globally already
   accounts for the sticky header + fixed breadcrumb. */
#routeView {
  position: relative;
  min-height: 60vh;
}

.sectionView {
  padding-top: 0 !important;
}

/* Sales Insights (/reports) hides the fixed breadcrumb bar (see
   showGlobalBreadcrumbs), so the ~90px band it reserved inside the body's
   padding-top (245px, set in steele-consistency-fix.css: breadcrumb top 155px +
   90px min-height = 245px) would otherwise be an empty gap between the nav and
   the first card. Pull content up to just under the nav bottom (the breadcrumb's
   old `top: 155px` anchor) so the report cards sit directly under the nav. We
   use 165px, not 155px: the portal card is flushed to the content top with NO
   margin buffer (steele-consistency-fix.css §"Flush the card"), so a ~10px cushion
   keeps the card's red top stripe cleanly below the nav instead of tucked under
   it. Higher specificity than the plain `body` rule + !important beats it (and
   the inline padding-top App.vue writes). Desktop only: on mobile the breadcrumb
   is hidden globally and the body keeps its own tuned padding (215/185). */
@media screen and (min-width: 992px) {
  body.report-route {
    padding-top: 165px !important;
  }
}
/* Same pull-up on MOBILE: the breadcrumb band is hidden there too, but the body's tuned
   215px/185px paddings still reserve it → an empty gap between the nav and the hero Ask.
   Mirror the desktop math (reserved band ≈ 80px + keep the ~10px cushion under the nav). */
@media screen and (max-width: 991.98px) {
  body.report-route {
    padding-top: 135px !important;
  }
}
@media screen and (max-width: 575.98px) {
  body.report-route {
    padding-top: 105px !important;
  }
}

.removeTopSpace {
  top: auto !important;
}

#shoppingPO {
  position: relative;
  top: auto;
  left: auto;
  width: 100%;
  z-index: 1;
  background: transparent;
}

#header-account .dropdown-menu {
  /* Above the floating widget stack (8500) and pill rail (9000) so the
     My Account menu is never covered by the cart / Top-20 panels. Still
     below the info modal (10000). */
  z-index: 9600;
}

/* ===== GLOBAL FLOATING WIDGET STACK =====
   Hosts the EXPANDED Cart + Top 5 panels, stacked vertically and
   anchored to the RIGHT EDGE OF THE CONTENT AREA (not the viewport
   edge / steel rail) so they sit where the old inline cart did. The
   collapsed pills float independently (fixed / cart-pill portal).
   pointer-events:none keeps the container from blocking the page in
   the gaps; each panel re-enables them. z-index sits just under the
   pills (9000) and above page content. */
.floating-widget-stack {
  /* position: ABSOLUTE (not fixed) so the stack SCROLLS WITH THE PAGE — as
     the user scrolls the dashboard, the cart panel moves up/away with the
     content instead of staying pinned in the viewport. The offset parent is
     #app (it's position: relative — see compiled-app.css — and the stack is a
     direct child of #app), so `top`/`right` resolve in #app's document space.
     alignWidgetStack() sets `top` in that SAME space (offset-parent-relative),
     keeping the cart's red top-line flush with the first content card's. */
  position: absolute;
  /* Content-area top: the fixed header + breadcrumb are cleared by the
     body's `padding-top: 245px`, so in-flow content starts at 245. Pin the
     stack there so it's flush with the page content on every page (measured:
     SavedCarts / Documents content tops sit at exactly 245). alignWidgetStack
     overrides this only where it finds a content red-line (e.g. the dashboard
     insights card); elsewhere this default holds.
     NOTE: this top is relative to #app, whose top is already offset down the
     document by the body's padding-top; alignWidgetStack normally replaces it
     with an offset-parent-relative measurement anyway, so the default is only
     a pre-measurement fallback. */
  top: 245px;
  right: 0;
  width: 360px;
  max-width: 92vw;
  /* No max-height: the stack is absolute and scrolls with the page, so it can
     be as tall as its content — the user scrolls the PAGE to see all of the
     cart. (When it was position:fixed this capped the panel to the viewport;
     that's no longer wanted. clampStackHeight() is also neutralized below.) */
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  /* No right padding: the panels must reach the stack's right edge so they
     line up flush with the content container's right edge (set below). */
  padding: 0 0 12px 0;
  z-index: 8500;
  pointer-events: none;
  /* overflow visible (was overflow-y:auto): with no height cap the stack
     never needs its own scrollbar — it grows with content and the page
     scrolls. */
  overflow: visible;
}
.floating-widget-stack > * {
  pointer-events: auto;
  width: 100%;
}

/* When neither the cart nor Top 5 is expanded, the stack has no panel to
   show — only the side pills, which are portaled out to the pill rail. The
   empty stack box is still a 360×~12px shell pinned at the content's top
   offset, so it hovers over the first card's red top line and clips it.
   Collapse it away entirely; the portaled pills are unaffected. */
.floating-widget-stack.stack-collapsed {
  display: none;
}

/* ===== STICKY (VIEWPORT-PINNED) MODE — build / shopping pages =====
   On the Search / Create-Order / Create-Quote build pages the right stack
   must behave as a sticky sidebar: it STAYS on-screen while the left results
   list scrolls, is bounded by the VIEWPORT (not the results column or the
   footer), and scrolls INTERNALLY so the whole column — especially Top Parts —
   is always reachable even after the left list ends.

   position:fixed (not CSS `position:sticky`) is deliberate: sticky inside the
   results row would be clamped by that row's height and re-introduce the exact
   cut-off we're fixing. Fixed pins to the viewport, independent of the results
   container's height. App.vue's alignWidgetStack() sets the viewport `top`
   (just below the fixed header) and constrainWidgetStack() sets the inline
   max-height (= viewport − top − gutter) + overflow-y:auto, recomputed on
   scroll/resize. The right-edge anchoring (the `right:` breakpoints below)
   is shared with the absolute mode, so the panel still lines up with the
   content container's right edge.

   pointer-events:auto (the base rule sets none on the container) so the whole
   pinned column — including the gaps between panels — is one wheel-scrollable
   region rather than letting wheel events fall through to the page. */
.floating-widget-stack.stack-sticky {
  position: fixed;
  pointer-events: auto;
}

/* In sticky mode the stack is ONE internally-scrolling column, so disable the
   per-widget vertical caps (which would otherwise create nested scrollbars):
   the Top Parts card's own max-height/overflow is removed here so it flows at
   natural height inside the stack's single scroll area. (Outside sticky mode
   these inner caps stay as-is — see TopPartsWidget's scoped CSS.) The cart
   panel already carries no inner cap. These are unscoped on purpose: the
   widgets render as children of this stack, and an ancestor-scoped global rule
   reaches them without editing their scoped component CSS. */
.floating-widget-stack.stack-sticky .top-parts-floating .top-parts-card {
  max-height: none;
  /* Reset BOTH axes to visible. The scoped rule in TopPartsWidget sets
     overflow-x:hidden (so its rounded top corners clip in the non-sticky
     scroll-container case); if only overflow-y were reset to visible here,
     the spec's "visible computes to auto when the other axis isn't visible"
     rule would silently turn overflow-y back into auto and re-introduce the
     nested inner scrollbar this sticky single-scroll column exists to avoid.
     Setting the shorthand to visible keeps the whole column as ONE scroll
     region. The card's top corners stay rounded via the border-top-radius
     declared in the scoped rule (radius doesn't require clipping to render the
     background corners). */
  overflow: visible;
}

/* Slim, unobtrusive scrollbar for the pinned column's internal scroll. */
.floating-widget-stack.stack-sticky::-webkit-scrollbar {
  width: 8px;
}
.floating-widget-stack.stack-sticky::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.22);
  border-radius: 4px;
}
.floating-widget-stack.stack-sticky::-webkit-scrollbar-track {
  background: transparent;
}

/* Align the EXPANDED panel stack's RIGHT edge with the content container's
   right edge (= the header search box's right edge) at each breakpoint.
   #routeView .container is sized by steele to Bootstrap width − 96px
   (624 / 864 / 1044), so we use THOSE widths (not the standard 960/1140,
   and without the old "+ 12px" that pushed the panels past the content).
   The collapsed pill rail is intentionally NOT included here — it stays
   tucked at the viewport's right edge (its base `right: 12px` rule below)
   as a set of edge tabs; only the open panels reflow in to line up with
   the page content + search field. */
@media (min-width: 768px) {
  #app .floating-widget-stack { right: calc((100% - 624px) / 2); }
}
@media (min-width: 992px) {
  #app .floating-widget-stack { right: calc((100% - 864px) / 2); }
}
@media (min-width: 1200px) {
  #app .floating-widget-stack { right: calc((100% - 1044px) / 2); }
}
@media (max-width: 991.98px) {
  .floating-widget-stack {
    /* Smaller header at narrow widths → lower default top fallback. No
       max-height: the stack is absolute and scrolls with the page (see the
       base rule above). */
    top: 220px;
  }
}

/* ===== MOBILE: Cart + Top Parts folded into the page flow =====
   On phones (<992px) the floating Cart + Top Parts are mounted INLINE (no
   `floating` prop) at the top of the content (Cart) and above the footer
   (Top Parts), per the composed mobile layout — the fixed right-edge stack is
   gated off (v-if !isMobile). These wrappers only exist below 992px, so no
   media guard is needed. */
.jcs-mobile-widget {
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin: 0 0 12px 0;
  pointer-events: auto;
}
.jcs-mobile-cart {
  /* Expanded cart DROPS from the cart bar as an OVERLAY (user request): fixed
     just below the bar (~44px tall), covering the header / nav / content
     beneath rather than pushing them down. The cart bar itself (top 0–44px,
     inside the sticky header) stays visible above this panel. */
  position: fixed;
  top: var(--jcs-header-bottom, 90px);   /* right below the logo/icons row */
  left: 0;
  right: 0;
  margin: 0 !important;
  /* Above the sticky header (z 9200, raised in steele-consistency-fix.css) so
     the cart drops OVER the nav — matching the dropdowns/modal. 1100 was below
     it, so the nav painted on top. Stays under the modal (9600). */
  z-index: 9300;
  max-height: calc(100vh - var(--jcs-header-bottom, 90px));
  overflow-y: auto;
  background: #fff;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28);
}
/* The panel scrolls as ONE unit — drop the inner tray's own max-height so it
   doesn't create a nested scrollbar inside the overlay. */
.jcs-mobile-cart .staging-tray-wrapper,
.jcs-mobile-cart .top-parts-card,
.jcs-mobile-cart .dashboard-card {
  max-height: none !important;
}
/* Compact cart bar: tap to expand the inline cart below it. */
.jcs-mobile-cart-wrap {
  margin: 8px 0 12px;
}
.jcs-mobile-cart-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  background: #c8302a;
  color: #fff;
  border: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 0;
  padding: 10px 14px;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
}
.jcs-mcb-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.jcs-mcb-cart-icon {
  width: 19px;
  height: 19px;
  flex: 0 0 auto;
}
.jcs-mcb-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}
/* Orders: white circle, black number. */
.jcs-mcb-orders {
  background: #fff;
  color: #000;
  border: 1px solid rgba(0, 0, 0, 0.2);
}
/* Quotes: black circle, white number. */
.jcs-mcb-quotes {
  background: #000;
  color: #fff;
}
.jcs-mobile-cart-chev {
  font-weight: 600;
  font-size: 13px;
  opacity: 0.95;
}
.jcs-mobile-cart-wrap .jcs-mobile-cart {
  margin-top: 6px;
}
/* TEMP build tag — tiny fixed corner marker (see template). */
.jcs-build-tag {
  position: fixed;
  left: 4px;
  bottom: 4px;
  z-index: 2147483646;
  font-family: monospace;
  font-size: 11px;
  line-height: 1;
  color: rgba(0, 0, 0, 0.62);
  background: rgba(255, 255, 255, 0.82);
  padding: 6px 9px;
  border-radius: 4px;
  /* Tappable while the mobile-header debug is active — copies the live header
     diagnostic to the clipboard (see copyHeaderDiag). */
  pointer-events: auto;
  cursor: pointer;
  -webkit-user-select: none;
  user-select: none;
}
.jcs-build-tag:active {
  background: rgba(190, 230, 200, 0.95);
}
/* TEMP: copyable header-diagnostic overlay (mobile-header debug). */
.jcs-diag-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2147483647;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
}
.jcs-diag-box {
  width: 100%;
  max-width: 640px;
  max-height: 86vh;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.jcs-diag-title {
  padding: 10px 14px;
  font-weight: 700;
  font-size: 14px;
  background: #13294b;
  color: #fff;
}
.jcs-diag-text {
  flex: 1 1 auto;
  min-height: 220px;
  margin: 0;
  border: 0;
  padding: 10px 12px;
  font-family: monospace;
  font-size: 12px;
  line-height: 1.4;
  color: #1f2733;
  background: #f6f8fa;
  resize: none;
  white-space: pre;
  overflow: auto;
}
.jcs-diag-btns {
  display: flex;
  gap: 10px;
  padding: 10px 14px;
  border-top: 1px solid #e2e6ea;
}
.jcs-diag-btn {
  flex: 1 1 auto;
  padding: 11px 14px;
  border: 1px solid #c0392b;
  background: #fff;
  color: #c0392b;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.jcs-diag-btn.primary {
  background: #c0392b;
  color: #fff;
}
/* The inline panels fill the content column width, but stay compact: cap the
   height and scroll internally so the folded Cart / Top Parts don't push the
   whole page down on a phone (matches the composed compact cart). */
.jcs-mobile-widget .staging-tray-wrapper,
.jcs-mobile-widget .top-parts-card,
.jcs-mobile-widget .dashboard-card {
  width: 100%;
  max-width: 100%;
  max-height: 46vh;
  overflow-y: auto;
}

/* ===== RESERVE RIGHT-SIDE ROOM WHEN A PANEL IS OPEN =====
   While either floating panel is expanded (#app.widgets-expanded), the
   portal pages' main content reflows into the left so nothing sits
   under the panels — restoring the old "content on the left, cart on
   the right" relationship without a per-page grid column. Only at lg+
   (where there's room); on smaller screens the panels overlay as a
   drawer. The padding-right is !important because the consistency
   stylesheet pads several of these containers with !important. The
   selector list covers the recognized portal content containers; add
   to it if a page's content still slips under the panel.

   Scoped to `screen` so it never reflows printed pages — the panels are
   hidden in print (see the @media print rule below), so reserving a
   right gutter there would only push the printed content off-center. */
@media screen and (min-width: 992px) {
  /* Descendant `.container` (not `> .container`): several pages nest
     their content under a sub-wrapper (#odTop, #assemblyForm, #cartTop,
     #finalizeTop, …) that a direct-child selector missed, leaving that
     content to slide under the floating panels. Matching by descendant
     reflows ALL of a section's content containers. This is safe because
     b-modals use .modal-* (not .container) and the in-template
     #breadcrumb is display:none. #cart covers Cart/Index + RFQSavedItems;
     #orderDetails covers Order + RFQ details; #savedRFQ covers Saved
     Quotes. */
  #app.widgets-expanded #acctImportantInfo .container,
  #app.widgets-expanded #acctInsights .container,
  #app.widgets-expanded #account-content .content,
  #app.widgets-expanded #createOrder .container,
  #app.widgets-expanded #chooseAssembly .container,
  #app.widgets-expanded #shopAssembly .container,
  #app.widgets-expanded #searchPart,
  #app.widgets-expanded #acctOrders .container,
  #app.widgets-expanded #acctQuotes .container,
  #app.widgets-expanded #savedCarts .container,
  #app.widgets-expanded .saved-carts .container,
  #app.widgets-expanded #orderDetails .container,
  #app.widgets-expanded #infoCenter .container,
  #app.widgets-expanded #cart .container,
  #app.widgets-expanded #savedRFQ .container,
  #app.widgets-expanded #finalizeTop .container,
  #app.widgets-expanded #bottomTotals .container,
  #app.widgets-expanded #cartFinalize .container,
  #app.widgets-expanded #cartSuccess .container {
    padding-right: 384px !important;
    transition: padding-right 0.18s ease;
  }

  /* Event-free reflow when a locked image is docked into the stack — the
     :has() on #app detects the teleported image, so the build-page content
     makes room for it even when the cart + Top 5 are empty (the
     .widgets-expanded class above only fires while a cart/Top-5 panel is
     open). */
  #app:has(.floating-widget-stack .machine-image-wrap.is-zoom-locked) #createOrder .container,
  #app:has(.floating-widget-stack .machine-image-wrap.is-zoom-locked) #searchPart,
  #app:has(.floating-widget-stack .machine-image-wrap.is-zoom-locked) #chooseAssembly .container,
  #app:has(.floating-widget-stack .machine-image-wrap.is-zoom-locked) #shopAssembly .container {
    padding-right: 384px !important;
    transition: padding-right 0.18s ease;
  }

  /* Wide tables use b-table responsive="sm", which only scroll-contains
     BELOW the sm breakpoint — at desktop widths they render at natural
     width and overflow the condensed container rightward, under the
     floating panels (e.g. the Saved Carts / history table). Force the
     responsive wrapper to scroll-contain at lg+ while a panel is open so
     the table stays inside the reserved content area. */
  #app.widgets-expanded .table-responsive-sm,
  #app.widgets-expanded .table-responsive-md,
  #app.widgets-expanded .table-responsive {
    overflow-x: auto;
  }
}

/* ===== NEVER PRINT THE FLOATING WIDGETS =====
   The cart + Top 5 panels/pills are position:fixed, so without this
   they paint over the printed order (e.g. the Print Order view on
   /cart/success showed the Top 5 panel overlapping — and squeezing —
   the order table) and would repeat on every printed page. Hide them
   for print so only the page's own printable content remains. */
/* ===== COLLAPSED-PILL RAIL =====
   Fixed flex column on the right edge that hosts the Cart / Top 5 / Info
   pills (each portaled in via <portal to="floating-pills">). flex + gap
   gives an even space between whichever pills are present, regardless of
   their individual heights — replacing the old per-pill fixed offsets
   (42%, +90px, +180px) that produced uneven gaps because the cart pill
   is taller when it shows badges. Centered as a group via translateY. */
/* Dock visibility: the floating stack is display:none when the cart + Top 5
   are both collapsed/empty. When a locked image is teleported INTO it, force
   it visible so the docked image shows even with an empty cart. (:has is the
   robust, event-free driver; lockedImagePanelActive is a JS fallback.) */
html .floating-widget-stack:has(.machine-image-wrap.is-zoom-locked) {
  display: flex !important;
}

/* ===== Locked-image side pill =====
   Collapsed state of a locked assembly/machine image on the build pages
   (Search / Orders / RFQ). Teleported via <portal to="locked-image-pill">
   into the rail ABOVE the cart pill. Mirrors the cart/Top-5 pill frame:
   tucked to the right edge with a red left accent. Click to restore. */
/* LAYOUTS stand-in pill for the non-host /reports/ pages. .ci-rail-pill lives in
   CustomerInsights.vue's SCOPED style block, so reusing that class name here produced an UNSTYLED
   label (the class applied, the CSS did not). These rules mirror .ci-rail-pill exactly — same 56px
   frame, same left red stripe, same outward shadow — so the two are indistinguishable in the rail. */
.jcs-layouts-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 56px;
  box-sizing: border-box;
  background: #fff;
  color: #9e2436;
  border: 1px solid #ececec;
  border-left: 3px solid #9e2436;
  border-radius: 0 8px 8px 0;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.15);
  padding: 10px 8px;
  cursor: pointer;
  user-select: none;
  pointer-events: auto;   /* the rail sets pointer-events:none and children opt back in */
}
.jcs-layouts-pill:hover { background: #faf3f4; }
.jcs-layouts-pill-icon { font-size: 18px; line-height: 1; }
.jcs-layouts-pill-label { font-size: 10px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; text-align: center; }

.locked-image-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  min-width: 56px;
  height: 48px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-left: 3px solid #c0392b;
  border-radius: 0 8px 8px 0;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.15);
  color: #c0392b;
  cursor: pointer;
  pointer-events: auto;
}
.locked-image-pill svg {
  width: 26px;
  height: 26px;
}
.locked-image-pill:hover {
  background: #fdecea;
}

.floating-pill-rail {
  position: fixed;
  /* Below 992px this stays the historic 12px-from-the-edge anchor; the desktop
     block further down re-anchors it to the steel rail's INNER edge via `left`
     (see the RIGHT-EDGE TOGGLE GROUPS block in
     public/steele-consistency-fix.css for the rail geometry). */
  right: var(--jcs-rail-pill-offset, 12px);
  top: 50%;
  transform: translateY(-50%);
  /* Above the steel side rails, page content, and the expanded widget panels
     (stack z 8500) so the INFO / GUIDE / SETUP pills are never covered. Kept
     just below the My Account dropdown (9600). */
  z-index: 9590;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

/* DESKTOP: bound the rail to a px BAND instead of centring it on the viewport.
   WHY (zoom-dependent overlap, fixed 2026-07-26): this rail was centred
   (top: 50% + translateY(-50%)) while the language / AA / bug / theme stack
   (#utility-toggle-stack) is anchored bottom: 20px in px. The rail's bottom
   edge therefore sat at 50vh + railHeight/2 and the util stack's top edge at
   100vh - 192px: because 100vh in CSS px = physical height / zoom, the util
   stack's top rises TWICE as fast as the rail's bottom as you zoom out, so
   zooming IN closed the gap. Overlap started at railHeight > 100vh - 384px —
   on a ~900px viewport at 100% zoom that is 516px, and a Master on Sales
   Insights renders ~8 pills = 540px, so the bottom pill (TOKENS) sat under
   the globe (util stack z 10000 > rail z 9590, so the globe won).

   Anchoring top AND bottom in px deletes the 50% term: the rail's bottom edge
   is now always --jcs-util-band away from the viewport bottom — the same edge,
   in the same units, the util stack measures from — so the clearance is
   constant at every zoom level. --jcs-util-band is declared next to the util
   stack itself in public/steele-consistency-fix.css. overflow-y turns the band
   into a hard ceiling: too many pills scroll INSIDE the band instead of
   growing down into the stack, so they cannot collide by construction.
   --jcs-sticky-h is the live sticky-header height published by
   applyStickyClasses(). Scoped to >=992px so the mobile edge-tray parking
   rules (steele-consistency-fix.css, which re-apply their own -50% transform)
   are untouched. */
@media (min-width: 992px) {
  .floating-pill-rail {
    top: calc(var(--jcs-sticky-h, 175px) + 12px);
    bottom: var(--jcs-util-band, 208px);
    transform: none;
    /* ATTACH TO THE RAIL'S INNER EDGE (2026-08-02). The white content sheet is
       2 x --steele-rail-half-width wide and centred, so its right edge — where
       the steel rail begins — is exactly 50% + railHalfWidth from the left.
       Anchoring by `left` there (instead of computing right = band - pillWidth)
       makes this independent of how wide any individual pill is: every pill's
       red stripe lands ON that boundary and the tab grows rightward into the
       steel. align-items: flex-start lines up their left edges. */
    left: calc(50% + var(--steele-rail-half-width));
    right: auto;
    align-items: flex-start;
    /* Keeps the pills visually centred (as before) while the band has room;
       `safe` stops the overflow case from pushing the first pill out of reach
       (browsers without `safe` fall back to the plain `center` above). */
    justify-content: center;
    justify-content: safe center;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: none;
    /* A scroll container clips at its padding box: pad so the pills'
       2px 2px 8px shadow isn't shaved off. The pad is on the RIGHT now that the
       pills are mirrored and cast their shadow outward into the rail. */
    padding: 8px 14px 10px 0;
    /* The band-height box would otherwise be a full-height invisible click
       target over the right edge on pages with only a few pills. */
    pointer-events: none;
  }
  .floating-pill-rail > * {
    pointer-events: auto;
  }
  .floating-pill-rail::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
}

/* While the report's Sales Insights drawer is open it occupies the right edge,
   so the report toggles a `jcs-si-open` class on <body> and we hide every
   edge toggle (the pill rail AND the language/font/debug/theme util stack, plus
   the mobile edge-tray handle) so nothing overlaps the drawer. Removed again on
   close and when leaving the report. */
body.jcs-si-open .floating-pill-rail,
body.jcs-si-open #utility-toggle-stack,
body.jcs-si-open .jcs-edge-tray-handle,
body.jcs-si-open .jcs-rail-peek {
  display: none !important;
}
/* Any open report panel (Filters/Tokens drawer, Dashboard popout, Sales Insights drawer) sets
   `jcs-report-panel-open` on <body>; hide the right-edge pill rail so no rail shows beside the open panel.
   (The report's own scoped CSS hides the left edge rail; jcs-si-open additionally hides the util stack.) */
body.jcs-report-panel-open .floating-pill-rail,
body.jcs-report-panel-open .jcs-rail-peek {
  display: none !important;
}

@media print {
  .floating-widget-stack,
  .floating-pill-rail,
  .jcs-rail-peek,
  .cart-pill,
  .top-parts-pill,
  .important-info-pill,
  .how-it-works-pill {
    display: none !important;
  }
}

/* ===== GLOBAL: portal page-top brand stripe =====
   The uniform "first content card sits flush at the top, reflows left
   under the floating widgets, and carries a 3px brand-red top stripe"
   treatment (the Documents #ao-table model) is now controlled in ONE
   place: steele-consistency-fix.css → "UNIFORM PAGE-TOP BRAND STRIPE".
   The earlier `section > .container > .row:first-of-type` rule that
   lived here never matched — every portal page nests a wrapper
   (#odTop / #cartTop / #finalizeTop / #acctOrders) between the section
   and its .container — so it was removed in favor of that single block. */
</style>